export type Language = "en" | "es" | "it" | "fr" | "pt" | "ar" | "zh" | "nl";

// Textos completos de cada idioma. El neerlandés (nl) se monta al final del
// archivo sobre el inglés y se exporta todo junto como `translations`.
const baseTranslations: Record<Exclude<Language, "nl">, {
  nav: {
    homepage: string;
    aboutUs: string;
    software: string;
    personalize: string;
    caseStudies: string;
    blog: string;
    contactUs: string;
    bookingCall: string;
  };
  personalize: {
    title: string;
    subtitle: string;
    swipeHint: string;
    dashboardPreview: string;
    whatWeBuildTitle: string;
    whatWeBuildSubtitle: string;
    bridgeSection: {
      title: string;
      subtitle: string;
    };
    softwareCard: {
      title: string;
      description: string;
    };
    impactCard: {
      title: string;
      description: string;
    };
    newCard1: {
      title: string;
      description: string;
      keyPoints: string[];
    };
    newCard2: {
      title: string;
      description: string;
      keyPoints: string[];
    };
    buttons: {
      startBuilding: string;
      viewCaseStudies: string;
    };
    mobileButtons: {
      startBuilding: string;
      viewCaseStudies: string;
    };
    workProcess: {
      sectionTitle: string;
      sectionTitleHighlight: string;
      sectionSubtitle: string;
      steps: {
        step1: { week: string; title: string; highlight: string; description: string; features: string[] };
        step2: { week: string; title: string; highlight: string; description: string; features: string[] };
        step3: { week: string; title: string; highlight: string; description: string; features: string[] };
        step4: { week: string; title: string; highlight: string; description: string; features: string[] };
      };
      cta: {
        title: string;
        titleHighlight: string;
        subtitle: string;
        buttonText: string;
      };
    };
  };
  hero: {
    badge: string;
    title: string;
    subtitle: string;
    heroSubtitle: string;
    cta: string;
    ctaSecondary: string;
  };
  whatIsUlture: {
    badge: string;
    title: string;
    titleHighlight: string;
    subtitle: string;
    painPoints: {
      time: { title: string; description: string };
      money: { title: string; description: string };
      work: { title: string; description: string };
    };
    cta: string;
  };
  metrics: {
    problemsSolved: string;
    savedPerYear: string;
    efficiency: string;
  };
  categories: {
    title: string;
    titleHighlight: string;
    subtitle: string;
    administration: { title: string; description: string };
    workers: { title: string; description: string };
    production: { title: string; description: string };
    costs: { title: string; description: string };
    clients: { title: string; description: string };
    marketing: { title: string; description: string };
  };
  footer: {
    copyright: string;
    tagline: string;
    product: string;
    company: string;
    legal: string;
    privacy: string;
    terms: string;
    cookiePolicy: string;
    dpa: string;
  };
  whyWeExist: {
    badge: string;
    title: string;
    titleHighlightBlue: string;
    subtitle: string;
    steps: {
      challenge: { number: string; title: string; text: string };
      revelation: { number: string; title: string; text: string };
      solution: { number: string; title: string; text: string };
      result: { number: string; title: string; text: string };
    };
    mission: {
      badge: string;
      title: string;
      titleHighlight: string;
      subtitle: string;
    };
    pillars: {
      automation: { label: string; description: string };
      integration: { label: string; description: string };
      intelligence: { label: string; description: string };
      efficiency: { label: string; description: string };
    };
  };
  about: {
    badge: string;
    valueLabel: string;
    hero: {
      title: string;
      subtitle: string;
    };
    story: {
      title: string;
      paragraph1: string;
      paragraph2: string;
      paragraph3: string;
      years: string;
    };
    mvv: {
      title: string;
      subtitle: string;
    };
    mission: {
      title: string;
      description: string;
    };
    vision: {
      title: string;
      description: string;
    };
    values: {
      innovation: { title: string; description: string };
      commitment: { title: string; description: string };
      collaboration: { title: string; description: string };
    };
    team: {
      title: string;
      subtitle: string;
      headline: string;
      description: string;
      ceo: string;
      cto: string;
      coo: string;
      head_product: string;
      roles: {
        ceoCofounder: string;
        ctoCofounder: string;
        headProduct: string;
        headDevelopment: string;
      };
    };
    timeline: {
      title: string;
      subtitle: string;
      welcomeText: string;
      flipWords: string[];
      milestone1: { date: string; title: string; description: string };
      milestone2: { date: string; title: string; description: string };
      milestone3: { date: string; title: string; description: string };
      milestone4: { date: string; title: string; description: string };
      milestone5: { date: string; title: string; description: string };
      milestone6: { date: string; title: string; description: string };
      milestone7: { date: string; title: string; description: string };
    };
    cta: {
      title: string;
      description: string;
      contact: string;
      demo: string;
    };
  };
  macbook: {
    title: string;
    subtitle: string;
  };
  blog: {
    badge: string;
    title: string;
    subtitle: string;
    heroTitle: string;
    searchPlaceholder: string;
    postTitle: string;
    postExcerpt: string;
    readMore: string;
    comingSoonTitle: string;
    comingSoonText: string;
  };
  contact: {
    badge: string;
    title: string;
    subtitle: string;
    emailLabel: string;
    phoneLabel: string;
    locationLabel: string;
    infoTitle: string;
    infoSubtitle: string;
    responseTime: string;
    successTitle: string;
    successMessage: string;
    thankYouTitle: string;
    thankYouMessage: string;
    sendAnother: string;
    nameLabel: string;
    namePlaceholder: string;
    emailFormLabel: string;
    emailPlaceholder: string;
    companyLabel: string;
    companyPlaceholder: string;
    messageLabel: string;
    messagePlaceholder: string;
    sending: string;
    submitButton: string;
  };
  software: {
    badge: string;
    title: string;
    subtitle: string;
    cta: string;
    ctaSecondary: string;
    screenshotAlt: string;
    featuresTitle: string;
    featuresSubtitle: string;
    features: {
      administration: { title: string; description: string };
      workers: { title: string; description: string };
      production: { title: string; description: string };
      costs: { title: string; description: string };
      analytics: { title: string; description: string };
      automation: { title: string; description: string };
    };
    benefitsTitle: string;
    benefitsSubtitle: string;
    benefits: {
      item1: string;
      item2?: string;
      item3: string;
      item4: string;
      item5: string;
      item6: string;
    };
    stats: {
      farms: string;
      savings: string;
      time: string;
      satisfaction: string;
    };
    connectivity: {
      badge: string;
      title: string;
      description: string;
      heroTitle: string;
      heroSubtitle: string;
      stats: {
        countries: string;
        farms: string;
        availability: string;
        uptime: string;
      };
    };
    ctaTitle: string;
    ctaSubtitle: string;
    ctaButton: string;
    ctaButtonSecondary: string;
  };
  aboutStory: {
    sectionTitle: string;
    sectionSubtitle: string;
    intro: {
      title: string;
      subtitle: string;
    };
    challenge: {
      title: string;
      text: string;
    };
    realization: {
      title: string;
      text: string;
    };
    solution: {
      title: string;
      text: string;
    };
    transformation: {
      title: string;
      text: string;
    };
    mission: {
      title: string;
      subtitle: string;
      pillars: {
        automation: { title: string; text: string };
        integration: { title: string; text: string };
        dataIntelligence: { title: string; text: string };
      };
    };
  };
  heroInfrastructure: {
    title: string;
    subtitle: string;
    tag1: string;
    tag2: string;
    tag3: string;
    tag4: string;
  };
  operations: {
    title: string;
    subtitle: string;
    footer: string;
    learnMore: string;
    intro: {
      title: string;
      text: string;
      bulletsTitle: string;
      bullets: {
        visibility: { title: string; desc: string };
        decisions: { title: string; desc: string };
        savings: { title: string; desc: string };
        duplication: { title: string; desc: string };
        scalability: { title: string; desc: string };
      };
    };
    tabs: {
      mission: { title: string; heading: string; description: string };
      vision: { title: string; heading: string; description: string };
      innovation: { title: string; heading: string; description: string };
      commitment: { title: string; heading: string; description: string };
      collaboration: { title: string; heading: string; description: string };
    };
  };
  blogPosts: {
    post1: { title: string; subtitle: string; category: string; excerpt: string; readTime: string };
    post2: { title: string; subtitle: string; category: string; excerpt: string; readTime: string };
    post3: { title: string; subtitle: string; category: string; excerpt: string; readTime: string };
    post4: { title: string; subtitle: string; category: string; excerpt: string; readTime: string };
    post5: { title: string; subtitle: string; category: string; excerpt: string; readTime: string };
    post6: { title: string; subtitle: string; category: string; excerpt: string; readTime: string };
  };
  blogPage: {
    notFound: string;
    backToBlog: string;
    quickInsight: string;
    readNext: string;
    readArticle: string;
    allRights: string;
  };
  wobbleCards: {
    card1: { title: string; description: string; imageAlt: string };
    card2: { title: string; description: string };
    card3: { title: string; description: string; imageAlt: string };
  };
  ourStory: {
    mainTitle: string;
    challenge: { badge: string; title: string; description: string };
    realization: { badge: string; title: string; description: string };
    solution: { badge: string; title: string; description: string };
    stats: {
      betterFruits: { title: string; subtitle: string };
      betterEfficiency: { title: string; subtitle: string };
      betterLives: { title: string; subtitle: string };
    };
  };
  buttons: {
    startNow: string;
    learnMore: string;
    getStarted: string;
    bookDemo: string;
    contactUs: string;
    viewMore: string;
  };
  techSection: {
    eyebrow: string;
    headlinePart1: string;
    headlineHighlight: string;
    subheadlineHighlight: string;
    subheadline: string;
    block1: {
      title: string;
      description: string;
      bullets: string[];
    };
    block2: {
      title: string;
      description: string;
      bullets: string[];
    };
    block3: {
      title: string;
      description: string;
      bullets: string[];
    };
    supportingSentence: string;
    cta: string;
  };
  magicBento: {
    title: string;
    titleHighlight: string;
    subtitle: string;
    cta: string;
    documents: { 
      title: string; 
      description: string;
      reportFiles?: Array<{ name: string; date: string; type: string }>;
    };
    notifications: { 
      title: string; 
      description: string;
      notificationItems?: Array<{ text: string; icon: string; color: string }>;
    };
    integrations: { title: string; description: string };
    calendar: { title: string; description: string };
  };
  aiWorkflowBento: {
    title: string;
    titleHighlight: string;
    titlePart1: string;
    titlePart2: string;
    subtitle: string;
    cta: string;
    collaboration: {
      title: string;
      description: string;
      userMessage?: string;
      aiResponse?: string;
      menuItems?: {
        planTask: string;
        assignTask: string;
        manageTeam: string;
        analytics: string;
      };
    };
    integrations: {
      title: string;
      description: string;
    };
    insights: {
      title: string;
      description: string;
      metric: string;
    };
    automation: {
      title: string;
      description: string;
      notifications?: {
        meeting: string;
        treatment: string;
        lowStock: string;
      };
    };
  };
  scrollVelocity: {
    line1: string;
    line2: string;
    line3: string;
    line4: string;
    line5: string;
    line6: string;
    line7: string;
    line8: string;
  };
  featuresPreview: {
    titleLine1: string;
    titleLine2: string;
    subtitle: string;
    exploreSolutions: string;
    viewAll: string;
    highlights: {
      monitoring: string;
      alerts: string;
      analytics: string;
    };
    seeItTitle: string;
    seeItHighlight: string;
    dashboardDescription: string;
    stats: {
      pesticide: string;
      reporting: string;
      monitoring: string;
    };
    joinText: string;
    features: {
      control: { title: string; description: string };
      costs: { title: string; description: string };
      oversight: { title: string; description: string };
    };
    cards: {
      card1: { title: string; description: string };
      card2: { title: string; description: string };
      card3: { title: string; description: string };
      card4: { title: string; description: string };
      card5: { title: string; description: string };
    };
  };
  featuresPreviewReversed: {
    title: string;
    card1: {
      title: string;
      description: string;
      cta: string;
    };
    card2: {
      title: string;
      description: string;
      cta: string;
    };
    card3: {
      title: string;
      description: string;
    };
  };
  softwareShowcase: {
    ctaButton: string;
    previewButton: string;
    closePreview: string;
  };
  caseStudies: {
    title: string;
    subtitle: string;
    sectionTitle: string;
    sectionSubtitle: string;
    softwareCard: {
      title: string;
      description: string;
    };
    impactCard: {
      title: string;
      description: string;
    };
    results: {
      eyebrow: string;
      stats: { value: string; label: string }[];
    };
    bridgeTitle: string;
    bridgeSubtitle: string;
    ctaButton: string;
  };
  ulturePest: {
    hero: {
      titleLine1: string;
      titleLine2: string;
      subtitle: string;
      bookCall: string;
      startFree: string;
      stat1Label: string;
      stat2Label: string;
      stat3Label: string;
    };
    features: {
      titleLine1: string;
      titleLine2: string;
      subtitle: string;
      viewAll: string;
      bullets: {
        bullet1: string;
        bullet2: string;
        bullet3: string;
      };
      assistant: { title: string; description: string };
      farm: { title: string; description: string };
      prevention: { title: string; description: string };
      spraylogs: { title: string; description: string };
      treatments: { title: string; description: string };
      stock: { title: string; description: string };
      tasks: { title: string; description: string };
      cycles: { title: string; description: string };
    };
    video: {
      title: string;
      titleHighlight: string;
      subtitle: string;
      stat1Label: string;
      stat2Label: string;
      stat3Label: string;
    };
  };
}> = {
  en: {
    nav: {
      homepage: "Home",
      aboutUs: "About Us",
      software: "Our Apps",
      personalize: "Custom Solutions",
      caseStudies: "Case Studies",
      blog: "Blog",
      contactUs: "Contact",
      bookingCall: "Book a Demo"
    },
    personalize: {
      title: "Build your custom",
      subtitle: "agricultural solution",
      swipeHint: "Swipe to explore →",
      dashboardPreview: "Dashboard Preview",
      whatWeBuildTitle: "What we build",
      whatWeBuildSubtitle: "Custom software designed, built, and already running in real operations.",
      bridgeSection: {
        title: "Not a template. Not a generic platform.",
        subtitle: "Built around your workflows. Designed for daily use, not demos."
      },
      softwareCard: {
        title: "Software",
        description: "We built a custom maintenance control system to manage machines, locations, breakdowns, and scheduled servicing in one place. Every asset had its own history, tasks were assigned automatically when issues occurred, and teams could see priorities and status in real time."
      },
      impactCard: {
        title: "Impact",
        description: "The company reduced downtime, reacted faster to failures, and gained clear visibility over maintenance costs and recurring issues. What was once reactive and manual became structured, predictable, and easier to manage every day."
      },
      newCard1: {
        title: "Your Vision",
        description: "You understand your business better than anyone.\nYou know what needs to improve, what slows things down, and where existing tools fall short.\n\nYour vision isn't about adding software — it's about making daily work clearer, simpler, and easier to manage.\n\nThat's where we start.",
        keyPoints: ["Your workflows", "Your constraints", "Your priorities"]
      },
      newCard2: {
        title: "Our Solution",
        description: "We turn that vision into software built around how your business actually works.\n\nNo templates. No generic platforms.\nJust practical tools designed for daily use, shaped through collaboration and real feedback.",
        keyPoints: ["Built around real operations", "Designed for everyday use", "Grows with your business"]
      },
      buttons: {
        startBuilding: "Start Building Your Solution",
        viewCaseStudies: "View Case Studies"
      },
      mobileButtons: {
        startBuilding: "Start Building",
        viewCaseStudies: "Our Work"
      },
      workProcess: {
        sectionTitle: "How We Work",
        sectionTitleHighlight: "With You",
        sectionSubtitle: "From idea to production-ready software — with a clear, transparent process. We work side by side with you, keeping things simple, structured, and focused on real results.",
        steps: {
          step1: {
            week: "Week 1",
            title: "Understanding your",
            highlight: "operation",
            description: "We start by getting a clear picture of how your business actually works. This first phase is about listening, mapping processes, and defining what the software needs to do — and what it doesn't.",
            features: ["Discovery sessions with your team", "Review of current workflows and tools", "Definition of goals, scope, and priorities"]
          },
          step2: {
            week: "Weeks 2-4",
            title: "Building and refining the",
            highlight: "software",
            description: "Using our internal platform, we start building quickly and iterating based on real feedback. You see progress every week and can adjust direction as needed.",
            features: ["Rapid development using our proprietary tools", "Weekly check-ins and live demos", "Continuous testing and refinement"]
          },
          step3: {
            week: "Week 5",
            title: "Launch and",
            highlight: "handover",
            description: "Once the software is ready, we prepare everything for real-world use. Your team is trained, data is set up, and the system goes live with our support.",
            features: ["Team onboarding and training", "Data setup or migration", "Production launch with close monitoring"]
          },
          step4: {
            week: "Ongoing",
            title: "Support and continuous",
            highlight: "improvement",
            description: "The launch is not the end. We stay involved to ensure the software keeps working as your business evolves.",
            features: ["Ongoing improvements and adjustments", "New features as needs change", "Reliable technical support when needed"]
          }
        },
        cta: {
          title: "Ready to",
          titleHighlight: "Get Started",
          subtitle: "Schedule your free call and start transforming your business.",
          buttonText: "Book a Free Call"
        }
      }
    },
    hero: {
      badge: "Business Operations Platform",
      title: "Take care of the Fields.\nWe'll take care of the Rest.",
      subtitle: "From workflows and analytics to team collaboration and customer success, we help you organize and automate your business.",
      heroSubtitle: "Software, custom solutions, and consulting to bring clarity and control to agricultural operations.",
      cta: "Explore Our Solutions",
      ctaSecondary: "Book a Free Demo"
    },
    whatIsUlture: {
      badge: "What is Ulture?",
      title: "Tired of not having control",
      titleHighlight: "over everything happening",
      subtitle: "Losing time, money, and work because nothing is in one place. That's what we solve.",
      painPoints: {
        time: { title: "Time lost", description: "Jumping between spreadsheets, papers, and apps that don't talk to each other." },
        money: { title: "Money slipping away", description: "No real visibility into costs, margins, or where profitability leaks." },
        work: { title: "Duplicated work", description: "Entering the same data over and over because nothing is connected." }
      },
      cta: "Discover how we solve it"
    },
    metrics: {
      problemsSolved: "Problems Solved",
      savedPerYear: "Saved per Year",
      efficiency: "Operational Efficiency"
    },
    categories: {
      title: "Everything You Need to",
      titleHighlight: "Grow Your Business",
      subtitle: "Six key areas where Ulture helps you save time, reduce costs, and increase efficiency.",
      administration: { title: "Workflow Automation", description: "Automate repetitive tasks and streamline your business processes with smart workflows." },
      workers: { title: "Team Collaboration", description: "Manage teams, assign tasks, and track productivity in real-time." },
      production: { title: "Project Management", description: "Monitor project progress, track milestones, and manage resources efficiently." },
      costs: { title: "Analytics & Insights", description: "Gain insights into performance, trends, and profitability analytics." },
      clients: { title: "Customer Success", description: "Streamline customer management and maintain strong client relationships." },
      marketing: { title: "Integrations", description: "Connect with your favorite tools and build powerful workflows." }
    },
    footer: {
      copyright: "© 2026 Ulture",
      tagline: "Built with care for the people who feed the world.",
      product: "Product",
      company: "Company",
      legal: "Legal",
      privacy: "Privacy Policy",
      terms: "Terms of Service",
      cookiePolicy: "Cookie Policy",
      dpa: "DPA"
    },
    whyWeExist: {
      badge: "Our Story",
      title: "Created by farmers",
      titleHighlightBlue: "for farmers",
      subtitle: "A path of discovery towards digital agricultural transformation",
      steps: {
        challenge: { number: "01", title: "The Challenge", text: "Growers are experts like no one else in producing fruits and vegetables — but not in accounting, data management or marketing.\nWe realized that while their fields were thriving, their management systems were outdated." },
        revelation: { number: "02", title: "The Revelation", text: "Most software companies were building tools from offices, not from the fields. None of them truly understood what growers needed.\nAfter years working side by side with farmers, we decided to design solutions made from real experience, not theory." },
        solution: { number: "03", title: "The Solution", text: "We created a unified ecosystem where every tool communicates perfectly, eliminating friction and automating processes." },
        result: { number: "04", title: "The Result", text: "Farmers who reclaim hours of their day, reduce errors and make decisions based on real data in real time." }
      },
      mission: {
        badge: "Our Mission",
        title: "Accelerate Digital Transformation",
        titleHighlight: "of Agriculture",
        subtitle: "Building technology that understands the real needs of the field"
      },
      pillars: {
        automation: { label: "Automation", description: "Frictionless processes" },
        integration: { label: "Integration", description: "Everything connected" },
        intelligence: { label: "Intelligence", description: "Adaptive AI" },
        efficiency: { label: "Efficiency", description: "Real results" }
      }
    },
    macbook: {
      title: "Powerful Platform. Simple Interface.",
      subtitle: "Manage your entire operation from one dashboard"
    },
    about: {
      badge: "About Ulture",
      valueLabel: "Value",
      hero: {
        title: "Reshaping How Agriculture Operates",
        subtitle: "We are a team passionate about technology and efficiency, working to help businesses worldwide operate smarter."
      },
      story: {
        title: "Our Story",
        paragraph1: "Ulture was born from a deep understanding of the challenges modern businesses face every day. Our founders, with roots in both startups and enterprise, saw an opportunity to bridge the gap between complexity and simplicity.",
        paragraph2: "Since 2024, we have been developing tools that simplify the complexity of business operations. From small startups to large enterprises, our platform adapts to the unique needs of each organization.",
        paragraph3: "Today, we help thousands of teams across 50+ countries manage their operations more efficiently, reduce costs, and accelerate growth.",
        years: "Years of Experience"
      },
      mvv: {
        title: "Mission, Vision & Values",
        subtitle: "The principles that guide everything we do"
      },
      mission: {
        title: "Our Mission",
        description: "To empower businesses with intuitive digital tools that simplify operations, increase efficiency, and drive sustainable growth."
      },
      vision: {
        title: "Our Vision",
        description: "To become the go-to platform for business operations, where every team has access to enterprise-grade tools."
      },
      values: {
        innovation: { title: "Innovation", description: "We constantly seek new ways to solve complex problems, leveraging cutting-edge technology to create practical solutions." },
        commitment: { title: "Commitment", description: "We are deeply committed to our customers' success. Their growth is our growth, and we stand by them at every step." },
        collaboration: { title: "Collaboration", description: "We believe in the power of working together. We partner with teams, developers, and industry experts to build better solutions." }
      },
      team: {
        title: "Meet our founding team",
        subtitle: "The people behind Ulture — rooted in the field, driven by technology.",
        headline: "The Team",
        description: "We are a team passionate about technology and efficiency, working to help businesses worldwide operate smarter.",
        ceo: "Growth & Strategy",
        cto: "CTO & Co-Founder",
        coo: "Chief Operations Officer",
        head_product: "Head of Product",
        roles: {
          ceoCofounder: "Growth & Strategy",
          ctoCofounder: "CTO & Co-Founder",
          headProduct: "Head of Product",
          headDevelopment: "Head of Development"
        }
      },
      timeline: {
        title: "From the field, for the field",
        subtitle: "Key milestones in our history",
        welcomeText: "This is ",
        flipWords: ["Ulture", "Our history"],
        milestone1: { date: "1974", title: "The roots of the family business", description: "Our story begins with agriculture. In 1974, our grandparents built one of the first greenhouses in Almeria, Spain. It was hands-on work, shaped by experience, effort, and a strong connection to the land." },
        milestone2: { date: "2002", title: "Growth brings new challenges", description: "As the family business grew, modern greenhouses and new technologies improved production. At the same time, managing operations, administration, and costs became increasingly complex." },
        milestone3: { date: "2024", title: "The problem becomes clear", description: "Working directly inside the family business made one thing clear: while greenhouse technology had evolved, the way daily operations were managed had not. Information was scattered, processes were manual, and control was difficult to maintain." },
        milestone4: { date: "Mid 2025", title: "Built for real use", description: "The first version of the software was developed and implemented directly in the family greenhouses. It was built to solve real, everyday problems — as a working tool, not a product." },
        milestone5: { date: "February 2026", title: "Ulture launches", description: "After months of real-world use and refinement, Ulture was officially launched, staying focused on clarity, simplicity, and real operational needs." },
        milestone6: { date: "April 2026", title: "Ulture Pest", description: "Ulture Pest was introduced as the first solution, supporting pest monitoring, treatments, and spray records for agricultural operations." },
        milestone7: { date: "2026...", title: "What's next", description: "Ulture continues to evolve. New tools and solutions are in development, guided by real needs from the field." },
      },
      cta: {
        title: "Ready to Transform Your Operations?",
        description: "Join thousands of teams who are already saving time and driving growth with Ulture.",
        contact: "Contact Us",
        demo: "Request a Demo"
      }
    },
    blog: {
      badge: "Latest Insights",
    title: "Blog & Resources",
    subtitle: "Discover the latest insights, tips, and stories about agriculture and farm management.",
    heroTitle: "The latest in agriculture",
    searchPlaceholder: "Search articles...",
      postTitle: "How Technology is Transforming Business Operations",
      postExcerpt: "Explore how modern digital tools are helping teams increase efficiency and reduce costs.",
      readMore: "Read more",
      comingSoonTitle: "More content coming soon!",
      comingSoonText: "We're working on bringing you more valuable insights and resources."
    },
    contact: {
      badge: "Get in Touch",
      title: "Contact Us",
      subtitle: "Have questions about our platform? We'd love to hear from you.",
      emailLabel: "Email",
      phoneLabel: "Phone",
      locationLabel: "Location",
      infoTitle: "Let's talk",
      infoSubtitle: "Whether you have a question about features, pricing, or anything else, our team is ready to answer.",
      responseTime: "We usually respond within 24 hours",
      successTitle: "Message sent!",
      successMessage: "We'll get back to you soon.",
      thankYouTitle: "Thank you!",
      thankYouMessage: "Your message has been sent successfully. We'll get back to you soon.",
      sendAnother: "Send another message",
      nameLabel: "Name",
      namePlaceholder: "Your name",
      emailFormLabel: "Email",
      emailPlaceholder: "your@email.com",
      companyLabel: "Company (optional)",
      companyPlaceholder: "Your company",
      messageLabel: "Message",
      messagePlaceholder: "Tell us how we can help...",
      sending: "Sending...",
      submitButton: "Send Message"
    },
    software: {
      badge: "Our Platform",
      title: "Powerful Platform for Modern Teams",
      subtitle: "One platform to manage your entire operation — from workflows to analytics.",
      cta: "Get Started Free",
      ctaSecondary: "Request a Demo",
      screenshotAlt: "Ulture platform dashboard",
      featuresTitle: "Everything you need to scale your business",
      featuresSubtitle: "Our comprehensive suite of tools covers every aspect of business operations.",
      features: {
        administration: { title: "Workflow Automation", description: "Automate repetitive tasks and streamline your processes with smart workflows." },
        workers: { title: "Team Collaboration", description: "Manage teams, assign tasks, and track productivity in real-time." },
        production: { title: "Project Management", description: "Monitor project progress, milestones, and resource allocation." },
        costs: { title: "Analytics & Insights", description: "Gain insights into performance, trends, and profitability analytics." },
        analytics: { title: "Custom Dashboards", description: "Real-time insights and custom reports to make data-driven decisions." },
        automation: { title: "AI-Powered Tools", description: "AI-powered workflows that save time and reduce manual errors." }
      },
      benefitsTitle: "Why choose Ulture?",
      benefitsSubtitle: "",
      benefits: {
        item1: "Save 10+ hours per week on repetitive tasks",
        item3: "Real-time visibility across all operations",
        item4: "Seamless integration with 100+ tools",
        item5: "Constant support and assistance from our team",
        item6: "Regular updates with new features"
      },
      stats: {
        farms: "Teams using Ulture",
        savings: "Average cost savings",
        time: "Weekly time saved",
        satisfaction: "Customer satisfaction"
      },
      connectivity: {
        badge: "Global Network",
        title: "Global connectivity",
        description: "Manage your operations from anywhere in the world.",
        heroTitle: "Connect with your team from anywhere in the world",
        heroSubtitle: "A global platform to manage, analyze and make decisions without borders.",
        stats: {
          countries: "Countries",
          farms: "Teams",
          availability: "Availability",
          uptime: "Uptime"
        }
      },
      ctaTitle: "Ready to transform your operations?",
      ctaSubtitle: "Start your free trial today and see how Ulture can help you save time and scale faster.",
      ctaButton: "Start Free Trial",
      ctaButtonSecondary: "Talk to Sales"
    },
    aboutStory: {
      sectionTitle: "Our Story",
      sectionSubtitle: "Our story, built from the field",
      intro: {
        title: "Built from real agriculture",
        subtitle: "We didn't start with slides or theory — we started in the field, facing manual processes, inefficiencies and lack of control."
      },
      challenge: {
        title: "The Challenge",
        text: "Growers are experts like no one else in producing fruits and vegetables — but not in accounting, logistics, or data management. While their fields were thriving, their management systems were outdated."
      },
      realization: {
        title: "From problems to software",
        text: "Every tool we build comes from a real operational problem: pesticide management, planning, orders, traceability and automation — all designed to work in real agricultural environments."
      },
      solution: {
        title: "Built fast, tested in the field",
        text: "In the first days, we built our initial internal tools. In the following weeks, we iterated fast — testing, adjusting and improving directly with real agricultural workflows."
      },
      transformation: {
        title: "The Transformation",
        text: "We build software the same way agriculture works: step by step, season by season, with real results."
      },
      mission: {
        title: "Our Mission",
        subtitle: "Accelerate the digital transformation of agriculture",
        pillars: {
          automation: { title: "Automation", text: "Making every process more efficient — from production to sales" },
          integration: { title: "Integration", text: "Connecting all parts of your operation in one place" },
          dataIntelligence: { title: "Data Intelligence", text: "Transforming information into strategic decisions" }
        }
      }
    },
    heroInfrastructure: {
      title: "Connect from anywhere",
      subtitle: "A living digital infrastructure that connects fields, teams and data in real time.",
      tag1: "Real-time data",
      tag2: "AI-powered decisions",
      tag3: "Global scale",
      tag4: "Automation-first"
    },
    operations: {
      title: "One platform. Full operational control.",
      subtitle: "Manage every aspect of your agricultural operation from a single, connected system",
      footer: "One platform. One source of truth. Full operational control.",
      learnMore: "Explore Operations",
      intro: {
        title: "One Platform, Full Operational Control",
        text: "Managing an agricultural operation shouldn't require dozens of different tools. Our platform centralizes everything you need in a single integrated system, eliminating complexity and multiplying your efficiency. From planning to cost control, every module works together to give you complete visibility and total control of your operation. No more fragmented data, messy spreadsheets, or information lost between systems.",
        bulletsTitle: "With our unified platform you get:",
        bullets: {
          visibility: { title: "Total visibility", desc: "All your operational data in one place, updated in real-time" },
          decisions: { title: "Smarter decisions", desc: "Forecasting and analysis tools that predict trends and optimize resources" },
          savings: { title: "Automatic savings", desc: "Integrated cost control that identifies optimization opportunities without manual effort" },
          duplication: { title: "Zero duplication", desc: "Enter information once and it syncs automatically across all modules" },
          scalability: { title: "Guaranteed scalability", desc: "Grow from 10 hectares to 1,000 without changing systems" }
        }
      },
      tabs: {
        mission: {
          title: "Our Mission",
          heading: "Our Mission",
          description: "To empower businesses with intuitive digital tools that simplify operations, increase efficiency, and drive sustainable growth."
        },
        vision: {
          title: "Our Vision",
          heading: "Our Vision",
          description: "To become the go-to platform for business operations, where every team has access to enterprise-grade tools."
        },
        innovation: {
          title: "Innovation",
          heading: "Innovation",
          description: "We constantly seek new ways to solve complex problems, leveraging cutting-edge technology to create practical solutions."
        },
        commitment: {
          title: "Commitment",
          heading: "Commitment",
          description: "We are deeply committed to our customers' success. Their growth is our growth, and we stand by them at every step."
        },
        collaboration: {
          title: "Collaboration",
          heading: "Collaboration",
          description: "We believe in the power of working together. We partner with teams, developers, and industry experts to build better solutions."
        }
      }
    },
    blogPosts: {
      post1: {
        title: "How AI is Revolutionizing Modern Agriculture",
        subtitle: "Smart technology meets sustainable farming",
        category: "AI & Technology",
        excerpt: "From real-time crop monitoring to predictive analytics, AI is transforming how we grow food and manage agricultural operations.",
        readTime: "6 min read"
      },
      post2: {
        title: "Making Data-Driven Farming Decisions",
        subtitle: "Turn information into action",
        category: "Data Management",
        excerpt: "Learn how centralized data systems help growers make better decisions, reduce costs, and improve profitability.",
        readTime: "5 min read"
      },
      post3: {
        title: "Sustainable Farming Through Technology",
        subtitle: "Better for the planet, better for business",
        category: "Sustainability",
        excerpt: "Discover how smart technology enables sustainable practices that benefit both the environment and your bottom line.",
        readTime: "7 min read"
      },
      post4: {
        title: "Automation in Agricultural Operations",
        subtitle: "Work smarter, not harder",
        category: "Automation",
        excerpt: "Explore how automation reduces manual work, eliminates errors, and allows teams to focus on strategic activities.",
        readTime: "6 min read"
      },
      post5: {
        title: "Forecasting Production with Confidence",
        subtitle: "Plan ahead with data, not guesswork",
        category: "Planning & Forecasting",
        excerpt: "Learn how predictive models help growers anticipate production, manage inventory, and meet customer commitments.",
        readTime: "7 min read"
      },
      post6: {
        title: "Cost Control and Profitability in Agriculture",
        subtitle: "Know your numbers, improve your margins",
        category: "Financial Management",
        excerpt: "Understand how proper cost allocation and analysis help identify inefficiencies and increase profitability.",
        readTime: "8 min read"
      }
    },
    blogPage: {
      notFound: "Article Not Found",
      backToBlog: "Back to Blog",
      quickInsight: "Quick Insight",
      readNext: "Read Next",
      readArticle: "Read Article",
      allRights: "All rights reserved."
    },
    buttons: {
      startNow: "Start Now",
      learnMore: "Explore Solutions",
      getStarted: "Get Started",
      bookDemo: "Book Demo",
      contactUs: "Contact Us",
      viewMore: "View More"
    },
    wobbleCards: {
      card1: {
        title: "Complete Digital Control",
        description: "Manage your entire agricultural operation from one platform. From orders and invoices to production tracking and cost analysis.",
        imageAlt: "Ulture platform dashboard"
      },
      card2: {
        title: "Intelligent Automation",
        description: "Reduce manual work and eliminate errors. Our smart workflows adapt to your operation."
      },
      card3: {
        title: "Global Connectivity",
        description: "Access your data from anywhere. Real-time synchronization across all your devices and teams.",
        imageAlt: "Ulture platform dashboard"
      }
    },
    ourStory: {
      mainTitle: "We were tired of seeing inefficiencies in agriculture, so one day we decided to change things.",
      challenge: {
        badge: "The Challenge",
        title: "The Challenge",
        description: "Growers are experts like no one else in producing fruits and vegetables — but not in accounting, logistics, or data management. We realized that while their fields were thriving, their management systems were outdated."
      },
      realization: {
        badge: "The Realization",
        title: "The Realization",
        description: "Most software companies were building tools from offices, not from the fields. None of them truly understood what growers needed. After years working side by side with farmers, we decided to design solutions made from real experience, not theory."
      },
      solution: {
        badge: "The Solution",
        title: "The Solution",
        description: "We built a platform that automates operations, centralizes information, and transforms agricultural management into a connected, digital, and human-centered system."
      },
      stats: {
        betterFruits: { title: "Better Fruits", subtitle: "Quality you can measure" },
        betterEfficiency: { title: "Better Efficiency", subtitle: "Time and cost savings" },
        betterLives: { title: "Better Lives", subtitle: "For those who feed the world" }
      }
    },
    techSection: {
      eyebrow: "FOR REAL-WORLD AGRICULTURE & HARD-TO-DIGITALIZE OPERATIONS",
      headlinePart1: "We build our own technology to ",
      headlineHighlight: "build your own software.",
      subheadlineHighlight: "We are not a generic SaaS.",
      subheadline: "We design and develop custom-built software solutions adapted to the real needs of each agricultural operation — from small producers to complex, multi-channel businesses.",
      block1: {
        title: "We build our own technology",
        description: "We have developed our own internal platform and tools to design, deploy, and evolve software efficiently.",
        bullets: [
          "Move fast without sacrificing reliability",
          "Adapt solutions to very specific use cases",
          "Build software that fits how people actually work in the field"
        ]
      },
      block2: {
        title: "Software adapted to each operation",
        description: "Every farm, company, and operation is different. That's why we don't sell one-size-fits-all software.",
        bullets: [
          "The size of the operation",
          "The type of crops or products",
          "Existing workflows and tools",
          "The level of digital maturity of the team"
        ]
      },
      block3: {
        title: "Not limited to one industry",
        description: "Although our roots are in agriculture, our technology allows us to build custom software for other industries with complex operations.",
        bullets: [
          "Operational complexity",
          "Manual processes",
          "Disconnected data",
          "Need for control and automation"
        ]
      },
      supportingSentence: "We don't ask operations to adapt to software. We build software that adapts to operations.",
      cta: "Talk to us about your operation"
    },
    magicBento: {
      title: "Key",
      titleHighlight: "Features",
      subtitle: "Everything you need to manage your business efficiently.",
      cta: "Learn more",
      documents: { 
        title: "Records & Reports", 
        description: "Keep track of all activities and generate reports in seconds. Filter by field, crop, or date to find what you need.",
        reportFiles: [
          { name: "Treatment Usage Block 1", date: "May 2026", type: "pdf" },
          { name: "Inventory Costs Report", date: "April 2026", type: "xlsx" },
          { name: "Harvest Summary Q1", date: "2026", type: "pdf" },
          { name: "Field Activity Log", date: "June 2026", type: "docx" },
          { name: "Pesticide Application Report", date: "May 2026", type: "pdf" }
        ]
      },
      notifications: { 
        title: "Clear oversight", 
        description: "See what's happening across fields and activities to support better decisions.",
        notificationItems: [
          { text: "Treatment Block 1 executed ✓", icon: "✓", color: "#00C9A7" },
          { text: "+50kg purchased: Product A", icon: "📦", color: "#1E86FF" },
          { text: "Stock level: Good", icon: "📊", color: "#00C9A7" },
          { text: "Automatic monthly report generated", icon: "📄", color: "#FFB800" }
        ]
      },
      integrations: { title: "Integrations", description: "Compatible with 100+ integrations." },
      calendar: { title: "Calendar", description: "Use the calendar to organize your tasks." }
    },
    aiWorkflowBento: {
      title: "built around your operations",
      titleHighlight: "Technology",
      titlePart1: "Built Around",
      titlePart2: "Your Operations",
      subtitle: "",
      cta: "Learn more",
      collaboration: {
        title: "Operational Digitalization",
        description: "We structure and digitalize daily agricultural operations for better control and efficiency.",
        menuItems: {
          planTask: "Plan Task",
          assignTask: "Assign Task",
          manageTeam: "Manage Team",
          analytics: "Analytics"
        }
      },
      integrations: {
        title: "Connected Operations",
        description: "We connect tools, data, and teams so information flows clearly and nothing gets lost between systems."
      },
      insights: {
        title: "Clarity & Control",
        description: "Get a clear, real-time view of operations, resources, and performance, so you can make better decisions with confidence.",
        metric: "Kg Harvested"
      },
      automation: {
        title: "Smart Planning & Alerts",
        description: "Plan ahead and receive automatic reminders and alerts whenever attention is needed — so nothing slips through.",
        notifications: {
          meeting: "Meeting reminder",
          treatment: "Treatment reminder: Block 1",
          lowStock: "Alert: Low stock levels"
        }
      }
    },
    scrollVelocity: {
      line1: "Ulture Software",
      line2: "Control",
      line3: "Analysis",
      line4: "Automation",
      line5: "Efficiency",
      line6: "Integration",
      line7: "Innovation",
      line8: "Growth"
    },
    featuresPreview: {
      titleLine1: "Grow your business.\nImprove visibility.\nStay in control.",
      titleLine2: "We make it easy.",
      subtitle: "As teams, fields, and activities increase, information becomes harder to track and daily work more difficult to manage.",
      exploreSolutions: "Explore Our Solutions",
      viewAll: "View All Features",
      highlights: {
        monitoring: "Spray logs as PDF. Instantly.",
        alerts: "Automatic SMS to workers for tasks and alerts.",
        analytics: "Every task tied to a block."
      },
      seeItTitle: "See it in",
      seeItHighlight: "action",
      dashboardDescription: "A unified dashboard that gives you complete visibility into your greenhouse operations.",
      stats: {
        pesticide: "Less pesticide use",
        reporting: "Faster reporting",
        monitoring: "Monitoring"
      },
      joinText: "Join hundreds of greenhouse operators who have transformed their pest management with our platform. From small family farms to large commercial operations, our platform adapts to your needs.",
      features: {
        control: { title: "Full Control, Every Day", description: "Manage your greenhouses, track every pest, and log all treatments from one unified platform. Know exactly what's happening across your entire operation." },
        costs: { title: "Know Where Your Money Goes", description: "Track every expense, see your cost per hectare, and understand where your money goes. No more guessing—just clear numbers that help you make better decisions." },
        oversight: { title: "Always One Step Ahead", description: "Automated alerts, threshold monitoring, and smart reminders ensure you never miss a critical treatment or deadline. Stay ahead of problems before they grow." }
      },
      cards: {
        card1: { title: "Costs and Profits Are Hard to Track", description: "Without reliable data from daily operations, understanding real costs and profitability becomes difficult." },
        card2: { title: "Too Many Tools, No Overview", description: "Information lives in spreadsheets, messages, notebooks, and software that don't talk to each other." },
        card3: { title: "Issues Are Caught Too Late", description: "Missed activities, shortages, or errors are often discovered only after they've already impacted operations." },
        card4: { title: "Unreliable Records & Reports", description: "Records and reports take time to prepare and are often incomplete or inaccurate because information is scattered across different sources." },
        card5: { title: "Always Focused on Today", description: "Daily priorities leave little room to think ahead or improve how work is organized." }
      }
    },
    featuresPreviewReversed: {
      title: "Every business has different needs.",
      card1: {
        title: "Explore our personalised software solutions",
        description: "Proven software designed for everyday agricultural operations.",
        cta: "Find out more"
      },
      card2: {
        title: "Build your own software with us",
        description: "Custom software shaped to your workflows, crops, and structure.",
        cta: "Get started"
      },
      card3: {
        title: "Always improving. Always building.",
        description: "We keep building and improving tools to support your operation over time."
      }
    },
    softwareShowcase: {
      ctaButton: "Get started",
      previewButton: "Preview",
      closePreview: "Close preview"
    },
    caseStudies: {
      title: "Case Studies",
      subtitle: "Real results from real projects",
      sectionTitle: "Real Projects",
      sectionSubtitle: "Custom software designed, built, and already running in real operations.",
      softwareCard: {
        title: "The Idea",
        description: "Tomatico needed a clearer way to organise its growth as an agricultural brand and improve the internal management of its operations.\n\nThe goal was to develop custom software that would centralise information and simplify daily project management.\n\nThe system needed to:\n\t•\torganise stock and product control\n\t•\tmanage events and promotional activities\n\t•\tcreate an active community around Tomatico\n\nThe idea was to build a simple tool that would connect all these areas in a single system."
      },
      impactCard: {
        title: "The Result",
        description: "Tomatico Software was developed — an internal platform that centralises the brand's operational management and enables a much more organised way of working.\n\nThanks to the system:\n\t•\tthe team knows exactly what tasks to carry out and when\n\t•\tinformation is centralised and accessible\n\t•\tdaily operations flow with greater clarity\n\nThe result is a system that allows Tomatico to manage its growth in a more structured way, maintaining control over its community, events, and product."
      },
      results: {
        eyebrow: "The impact",
        stats: [
          { value: "3", label: "areas in one system" },
          { value: "1", label: "custom-built platform" },
          { value: "100%", label: "centralised information" },
          { value: "24/7", label: "team access" },
        ],
      },
      bridgeTitle: "Ready to build yours?",
      bridgeSubtitle: "Every solution starts with understanding your unique challenges.",
      ctaButton: "Start Now"
    },
    ulturePest: {
      hero: {
        titleLine1: "Less improvising.",
        titleLine2: "Better planning.",
        subtitle: "By farmers, for farmers.",
        bookCall: "Book a Call",
        startFree: "Start for Free",
        stat1Label: "Traceable Treatments",
        stat2Label: "Exact Cost per m²",
        stat3Label: "Pesticide use"
      },
      features: {
        titleLine1: "Focus on your farm.",
        titleLine2: "We make it easy.",
        subtitle: "A single system to plan work, track treatments, control stock and understand the real costs of your farm.",
        viewAll: "View All Features",
        bullets: {
          bullet1: "Spray logs as PDF. Instantly.",
          bullet2: "Automatic SMS to workers for tasks and alerts.",
          bullet3: "Every task tied to a block."
        },
        assistant: { title: "Smart assistant for your day-to-day.", description: "Use audio, photos, or video to log issues, create tasks, or analyse crop status. The assistant detects pests, generates recommendations based on weather and field data, assigns work to your team, and helps you make faster decisions." },
        farm: { title: "Your farm, structured the right way.", description: "Organise locations, greenhouses, blocks, and sectors to match how your farm actually works. Everything else — treatments, costs, cycles — builds on this structure." },
        prevention: { title: "Smart pest prevention.", description: "Stay ahead of problems before they appear. Analyse growing conditions, weather, and pest pressure to detect early risks. Receive preventive alerts and clear recommendations to act in time and protect your production." },
        spraylogs: { title: "Spray logs, ready when you need them.", description: "Create logs in seconds, filter by any criteria, and export clean, audit-ready PDFs from any device. Clear, consistent reports you can share instantly for audits or daily operations." },
        treatments: { title: "Track what's applied, where it matters.", description: "Monitor treatments, pest pressure, and crop health by block and cycle. Keep full traceability of biological and chemical applications over time." },
        stock: { title: "Inventory that updates itself.", description: "Import your stock in seconds and let quantities update automatically when treatments are completed. Set reorder levels and get notified before you run out — not after." },
        tasks: { title: "Turn plans into action.", description: "Assign tasks to workers and locations, track execution, and stay aligned with SMS reminders. Workers see only what they need, exactly when they need it." },
        cycles: { title: "Plan cycles with clarity.", description: "Create custom crop cycles, apply them across multiple blocks, and manage them with ease. Compare costs and performance per cycle to make better decisions season after season." }
      },
      video: {
        title: "See it in",
        titleHighlight: "action",
        subtitle: "Watch how greenhouse managers use our platform daily.",
        stat1Label: "Efficiency gain",
        stat2Label: "Faster reporting",
        stat3Label: "Data access"
      }
    }
  },
  
  es: {
    nav: {
      homepage: "Inicio",
      aboutUs: "Sobre Nosotros",
      software: "Nuestras Apps",
      personalize: "Soluciones a Medida",
      caseStudies: "Casos de Éxito",
      blog: "Blog",
      contactUs: "Contacto",
      bookingCall: "Reservar Llamada"
    },
    personalize: {
      title: "Creamos tu propio",
      subtitle: "software",
      swipeHint: "Desliza para explorar →",
      dashboardPreview: "Vista previa del panel",
      whatWeBuildTitle: "Lo que construimos",
      whatWeBuildSubtitle: "Software a medida, diseñado, desarrollado y ya en uso en operaciones reales.",
      bridgeSection: {
        title: "No es una plantilla. No es una plataforma genérica.",
        subtitle: "Construido alrededor de tus procesos. Pensado para el uso diario, no para demos."
      },
      softwareCard: {
        title: "La Idea",
        description: "Tomatico necesitaba una forma más clara de organizar su crecimiento como marca agrícola y mejorar la gestión interna de sus operaciones.\n\nEl objetivo era desarrollar un software propio que permitiera centralizar la información y facilitar la gestión diaria del proyecto.\n\nEl sistema debía permitir:\n\t•\torganizar stock y control de producto\n\t•\tgestionar eventos y acciones promocionales\n\t•\tcrear una comunidad activa alrededor de Tomatico\n\nLa idea era construir una herramienta simple que conectara todas estas áreas en un único sistema."
      },
      impactCard: {
        title: "El Resultado",
        description: "Se desarrolló Tomatico Software, una plataforma interna que centraliza la gestión operativa de la marca y permite trabajar de forma mucho más organizada.\n\nGracias al sistema:\n\t•\tel equipo tiene claro qué tareas realizar y cuándo\n\t•\tla información está centralizada y accesible\n\t•\tlas operaciones diarias fluyen con mayor claridad\n\nEl resultado es un sistema que permite a Tomatico gestionar su crecimiento de forma más estructurada, manteniendo el control sobre su comunidad, sus eventos y su producto."
      },
      newCard1: {
        title: "Tu Visión",
        description: "Nadie entiende tu negocio mejor que tú.\nSabes qué funciona, qué ralentiza el trabajo y dónde fallan las herramientas actuales.\n\nTu objetivo no es añadir más software, sino hacer el trabajo diario más claro, rápido y fácil de gestionar.\n\nAhí es donde empezamos.",
        keyPoints: ["Tus flujos de trabajo", "Tus limitaciones reales", "Tus prioridades operativas"]
      },
      newCard2: {
        title: "Nuestra Solución",
        description: "Convertimos tu visión en software que encaja con la forma real en que funciona tu negocio.\n\nNada de plantillas ni plataformas genéricas.\nDesarrollamos herramientas simples, prácticas y pensadas para el uso diario, ajustadas contigo a medida que el sistema evoluciona.",
        keyPoints: ["Diseñado para operaciones reales", "Fácil de usar desde el primer día", "Preparado para crecer con tu negocio"]
      },
      buttons: {
        startBuilding: "Empieza a crear tu solución",
        viewCaseStudies: "Ver lo que hemos creado"
      },
      mobileButtons: {
        startBuilding: "Empezar",
        viewCaseStudies: "Nuestro trabajo"
      },
      workProcess: {
        sectionTitle: "Cómo trabajamos",
        sectionTitleHighlight: "contigo",
        sectionSubtitle: "De la idea, a un software listo para producción, con un proceso claro y transparente. Trabajamos codo a codo contigo, de forma sencilla, estructurada y enfocada en resultados reales.",
        steps: {
          step1: {
            week: "Semana 1",
            title: "Entendemos tu",
            highlight: "operación",
            description: "Empezamos por comprender cómo funciona realmente tu negocio. Esta fase se centra en escuchar, mapear procesos y definir qué debe hacer el software — y qué no.",
            features: ["Sesiones de análisis con tu equipo", "Revisión de flujos de trabajo y herramientas actuales", "Definición de objetivos, alcance y prioridades"]
          },
          step2: {
            week: "Semanas 2-4",
            title: "Desarrollo y mejora del",
            highlight: "software",
            description: "Con nuestra plataforma interna, empezamos a construir rápidamente e iteramos con feedback real. Ves avances cada semana y puedes ajustar el rumbo cuando sea necesario.",
            features: ["Desarrollo ágil con herramientas propias", "Revisiones semanales y demos en vivo", "Pruebas y ajustes continuos"]
          },
          step3: {
            week: "Semana 5",
            title: "Lanzamiento y",
            highlight: "entrega",
            description: "Cuando el software está listo, lo preparamos todo para su uso real. Formamos a tu equipo, configuramos los datos y lanzamos el sistema con nuestro apoyo.",
            features: ["Formación y onboarding del equipo", "Configuración o migración de datos", "Puesta en producción con seguimiento cercano"]
          },
          step4: {
            week: "Continuo",
            title: "Soporte y mejora",
            highlight: "constante",
            description: "El lanzamiento no es el final. Seguimos contigo para que el software evolucione con tu negocio.",
            features: ["Mejoras y ajustes continuos", "Nuevas funcionalidades según necesidades", "Soporte técnico fiable cuando lo necesites"]
          }
        },
        cta: {
          title: "¿Listo para",
          titleHighlight: "Empezar",
          subtitle: "Agenda tu llamada gratuita y empieza a transformar tu negocio.",
          buttonText: "Agendar Llamada Gratuita"
        }
      }
    },
    hero: {
      badge: "Plataforma de Agricultura Digital",
      title: "Creado por Agricultores,\npara Agricultores.",
      subtitle: "Desde pedidos y facturas hasta control de producción, te ayudamos a organizar y automatizar tu negocio agrícola.",
      heroSubtitle: "Menos improvisación. Mejor planificación.",
      cta: "Descubre nuestras soluciones",
      ctaSecondary: "Reserva una demo gratuita"
    },
    whatIsUlture: {
      badge: "¿Qué es Ulture?",
      title: "Cansados de no tener control",
      titleHighlight: "de todo lo que pasa",
      subtitle: "Perder tiempo, dinero y trabajo porque nada está en un solo lugar. Eso es lo que resolvemos.",
      painPoints: {
        time: { title: "Tiempo perdido", description: "Saltando entre hojas de cálculo, papeles y apps que no se entienden." },
        money: { title: "Dinero fugado", description: "Sin visibilidad real de costes, márgenes ni dónde se escapa la rentabilidad." },
        work: { title: "Trabajo duplicado", description: "Introduciendo los mismos datos una y otra vez porque nada está conectado." }
      },
      cta: "Descubre cómo lo resolvemos"
    },
    metrics: {
      problemsSolved: "Problemas Resueltos",
      savedPerYear: "Ahorro Anual",
      efficiency: "Eficiencia Operativa"
    },
    categories: {
      title: "Todo lo que Necesitas para",
      titleHighlight: "Hacer Crecer Tu Negocio",
      subtitle: "Seis áreas clave donde Ulture te ayuda a ahorrar tiempo, reducir costes y aumentar la eficiencia.",
      administration: { title: "Administración y Contabilidad", description: "Automatiza facturas, contratos y seguimiento financiero con flujos inteligentes." },
      workers: { title: "Trabajadores y Tareas", description: "Gestiona equipos, asigna tareas y rastrea la productividad en tiempo real." },
      production: { title: "Producción y Stock", description: "Monitorea niveles de inventario, ciclos de producción y logística." },
      costs: { title: "Costes y Beneficios", description: "Obtén información sobre gastos, márgenes y análisis de rentabilidad." },
      clients: { title: "Clientes y Pedidos", description: "Optimiza la gestión de pedidos y mantén relaciones sólidas con clientes." },
      marketing: { title: "Marketing e Imagen", description: "Construye tu presencia de marca y alcanza nuevos mercados efectivamente." }
    },
    footer: {
      copyright: "© 2026 Ulture",
      tagline: "Hecho con cariño para quienes alimentan al mundo.",
      product: "Producto",
      company: "Empresa",
      legal: "Legal",
      privacy: "Política de Privacidad",
      terms: "Términos de Servicio",
      cookiePolicy: "Política de Cookies",
      dpa: "DPA"
    },
    whyWeExist: {
      badge: "Nuestra Historia",
      title: "Creado por agricultores",
      titleHighlightBlue: "para agricultores",
      subtitle: "Un camino de descubrimiento hacia la transformación digital agrícola",
      steps: {
        challenge: { number: "01", title: "El Reto", text: "Los agricultores son expertos como nadie en producir frutas y verduras, pero no en contabilidad, gestión de datos o marketing. Nos dimos cuenta de que mientras sus campos prosperaban, sus sistemas de gestión estaban obsoletos." },
        revelation: { number: "02", title: "La Revelación", text: "Descubrimos que el 70% del tiempo administrativo se pierde navegando entre herramientas incompatibles y duplicando datos." },
        solution: { number: "03", title: "La Solución", text: "Creamos un ecosistema unificado donde cada herramienta se comunica perfectamente, eliminando fricciones y automatizando procesos." },
        result: { number: "04", title: "El Resultado", text: "Agricultores que recuperan horas de su día, reducen errores y toman decisiones basadas en datos reales en tiempo real." }
      },
      mission: {
        badge: "Nuestra Misión",
        title: "Acelerar la Transformación Digital",
        titleHighlight: "de la Agricultura",
        subtitle: "Construyendo tecnología que entiende las necesidades reales del campo"
      },
      pillars: {
        automation: { label: "Automatización", description: "Procesos sin fricción" },
        integration: { label: "Integración", description: "Todo conectado" },
        intelligence: { label: "Inteligencia", description: "IA adaptativa" },
        efficiency: { label: "Eficiencia", description: "Resultados reales" }
      }
    },
    macbook: {
      title: "Software Potente. Interfaz Simple.",
      subtitle: "Gestiona toda tu operación agrícola desde una plataforma"
    },
    about: {
      badge: "Sobre Ulture / Ulture",
      valueLabel: "Valor",
      hero: {
        title: "Redefiniendo Cómo Opera la Agricultura",
        subtitle: "Somos un equipo apasionado por la tecnología y la agricultura, trabajando para digitalizar y optimizar negocios agrícolas en todo el mundo."
      },
      story: {
        title: "Nuestra Historia",
        paragraph1: "Ulture nació de una profunda comprensión de los desafíos que enfrentan los agricultores cada día. Nuestros fundadores, con raíces tanto en la agricultura como en la tecnología, vieron una oportunidad para cerrar la brecha entre la agricultura tradicional y las soluciones digitales modernas.",
        paragraph2: "Desde 2020, hemos desarrollado herramientas que simplifican la complejidad de la gestión agrícola. Desde pequeñas granjas familiares hasta grandes empresas agrícolas, nuestra plataforma se adapta a las necesidades únicas de cada negocio.",
        paragraph3: "Hoy, ayudamos a cientos de agricultores en Europa y América Latina a gestionar sus operaciones de manera más eficiente, reducir costes y aumentar su rentabilidad.",
        years: "Años de Experiencia"
      },
      mvv: {
        title: "Misión, Visión y Valores",
        subtitle: "Los principios que guían todo lo que hacemos"
      },
      mission: {
        title: "Nuestra Misión",
        description: "Empoderar a agricultores y empresas agrícolas con herramientas digitales intuitivas que simplifican la gestión, aumentan la eficiencia y promueven el crecimiento sostenible."
      },
      vision: {
        title: "Nuestra Visión",
        description: "Convertirnos en la plataforma de referencia global para la digitalización agrícola, donde cada agricultor tenga acceso a tecnología que antes solo estaba disponible para grandes corporaciones."
      },
      values: {
        innovation: { title: "Innovación", description: "Buscamos constantemente nuevas formas de resolver problemas antiguos, aprovechando tecnología de vanguardia para crear soluciones prácticas." },
        commitment: { title: "Compromiso", description: "Estamos profundamente comprometidos con el éxito de nuestros clientes. Su crecimiento es nuestro crecimiento, y los acompañamos en cada paso." },
        collaboration: { title: "Colaboración", description: "Creemos en el poder del trabajo en equipo. Nos asociamos con agricultores, agrónomos y expertos de la industria para construir mejores soluciones." }
      },
      team: {
        title: "Conoce a nuestro equipo fundador",
        subtitle: "Las personas detrás de Ulture: con raíces en el campo y pasión por la tecnología.",
        headline: "El equipo",
        description: "Somos un equipo apasionado por la tecnología y la eficiencia, trabajando para ayudar a empresas de todo el mundo a operar de manera más inteligente.",
        ceo: "Growth & Strategy",
        cto: "CTO y Co-Fundadora",
        coo: "Director de Operaciones",
        head_product: "Directora de Producto",
         roles: {
           ceoCofounder: "Growth & Strategy",
           ctoCofounder: "Finance & Operations",
           headProduct: "Engineering & Product",
           headDevelopment: "Brand & Growth"
         }
      },
      timeline: {
        title: "Del campo, para el campo",
        subtitle: "Hitos clave en nuestra historia",
        welcomeText: "Esta es ",
        flipWords: ["Ulture", "Nuestra historia"],
        milestone1: { date: "1974", title: "Los orígenes de un negocio familiar", description: "Nuestra historia nace del campo. En 1974, nuestros abuelos levantaron uno de los primeros invernaderos de Almería, España: un trabajo hecho a mano, sostenido por la experiencia, el esfuerzo y un profundo vínculo con la tierra." },
        milestone2: { date: "2002", title: "El crecimiento trae nuevos desafíos", description: "Con los años, el negocio familiar se expandió. Los invernaderos modernos y las nuevas tecnologías impulsaron la producción, pero la gestión operativa, administrativa y de costes se volvió cada vez más compleja." },
        milestone3: { date: "2024", title: "El problema se hace evidente", description: "Trabajar a pie de explotación dejó algo claro: la tecnología avanzaba, pero la gestión del día a día no. La información estaba dispersa y los procesos seguían dependiendo demasiado del trabajo manual." },
        milestone4: { date: "Mediados de 2025", title: "Nacido del uso real", description: "Desarrollamos la primera versión del software y la ponemos a prueba directamente en los invernaderos familiares: una herramienta práctica, pensada para resolver problemas reales." },
        milestone5: { date: "Febrero 2026", title: "Lanzamiento de Ulture", description: "Tras meses de uso real y mejoras continuas, Ulture se lanza oficialmente, fiel a un principio claro: simplicidad y utilidad." },
        milestone6: { date: "Abril 2026", title: "Ulture Pest", description: "Presentamos Ulture Pest, nuestra primera solución especializada, centrada en el control de plagas, los tratamientos y los registros de aplicación." },
        milestone7: { date: "2026...", title: "Lo que viene", description: "Ulture sigue evolucionando. Nuevas soluciones están en camino, siempre nacidas de las necesidades reales del campo." },
      },
      cta: {
        title: "¿Listo para Transformar tu Granja?",
        description: "Únete a cientos de agricultores que ya están ahorrando tiempo y dinero con Ulture.",
        contact: "Contáctanos",
        demo: "Solicitar Demo"
      }
    },
    aboutStory: {
      sectionTitle: "Nuestra Historia",
      sectionSubtitle: "Nuestra historia, construida desde el campo",
      intro: {
        title: "Construido desde la agricultura real",
        subtitle: "No empezamos con presentaciones ni teoría: empezamos en el campo, enfrentándonos a procesos manuales, ineficiencias y falta de control."
      },
      challenge: {
        title: "El Desafío",
        text: "Los productores son expertos como nadie en cultivar frutas y verduras — pero no en contabilidad, logística o gestión de datos. Mientras sus campos prosperaban, sus sistemas de gestión estaban desactualizados."
      },
      realization: {
        title: "De problemas reales a software útil",
        text: "Cada herramienta que creamos nace de un problema operativo real: gestión de tratamientos, planificación, pedidos, trazabilidad y automatización — todo pensado para funcionar en el campo, no en teoría."
      },
      solution: {
        title: "Construido rápido, probado en campo",
        text: "En los primeros días creamos nuestras primeras herramientas internas. En las semanas siguientes iteramos rápido — probando, ajustando y mejorando directamente sobre flujos reales de trabajo agrícola."
      },
      transformation: {
        title: "La Transformación",
        text: "Construimos software igual que se trabaja en agricultura: paso a paso, campaña a campaña, con resultados reales."
      },
      mission: {
        title: "Nuestra Misión",
        subtitle: "Acelerar la transformación digital de la agricultura",
        pillars: {
          automation: { title: "Automatización", text: "Haciendo cada proceso más eficiente — desde producción hasta ventas" },
          integration: { title: "Integración", text: "Conectando todas las partes de tu operación en un solo lugar" },
          dataIntelligence: { title: "Inteligencia de Datos", text: "Transformando información en decisiones estratégicas" }
        }
      }
    },
    blog: {
      badge: "Últimas novedades",
    title: "Blog y recursos",
    subtitle: "Descubre las últimas novedades, consejos e historias sobre agricultura digital y gestión agrícola.",
    heroTitle: "Las últimas novedades agrícolas",
    searchPlaceholder: "Buscar artículos...",
      postTitle: "Cómo la tecnología está transformando la agricultura",
      postExcerpt: "Explora cómo las herramientas digitales modernas ayudan a los agricultores a aumentar la eficiencia y reducir costes.",
      readMore: "Leer más",
      comingSoonTitle: "¡Más contenido próximamente!",
      comingSoonText: "Estamos trabajando para traerte más información valiosa y recursos."
    },
    contact: {
      badge: "Contáctanos",
      title: "Contáctanos",
      subtitle: "¿Tienes preguntas sobre nuestra plataforma? Nos encantaría saber de ti.",
      emailLabel: "Email",
      phoneLabel: "Teléfono",
      locationLabel: "Ubicación",
      infoTitle: "Hablemos",
      infoSubtitle: "Tienes dudas sobre el producto, los precios o simplemente quieres saber más — estamos aquí.",
      responseTime: "Normalmente respondemos en 24 horas",
      successTitle: "¡Mensaje enviado!",
      successMessage: "Te responderemos pronto.",
      thankYouTitle: "¡Gracias!",
      thankYouMessage: "Tu mensaje ha sido enviado correctamente. Te responderemos pronto.",
      sendAnother: "Enviar otro mensaje",
      nameLabel: "Nombre",
      namePlaceholder: "Tu nombre",
      emailFormLabel: "Email",
      emailPlaceholder: "tu@email.com",
      companyLabel: "Empresa (opcional)",
      companyPlaceholder: "Tu empresa",
      messageLabel: "Mensaje",
      messagePlaceholder: "Cuéntanos cómo podemos ayudarte...",
      sending: "Enviando...",
      submitButton: "Enviar Mensaje"
    },
    software: {
      badge: "Nuestra Plataforma",
      title: "Software Potente para la Agricultura Moderna",
      subtitle: "Una plataforma para gestionar toda tu operación agrícola — del campo a las finanzas.",
      cta: "Empezar",
      ctaSecondary: "Solicitar Demo",
      screenshotAlt: "Panel de control de Ulture",
      featuresTitle: "Todo lo que necesitas para gestionar tu granja",
      featuresSubtitle: "Nuestra suite completa de herramientas cubre todos los aspectos de la gestión agrícola.",
      features: {
        administration: { title: "Administración y Contabilidad", description: "Automatiza facturas, contratos y seguimiento financiero con flujos inteligentes." },
        workers: { title: "Trabajadores y Tareas", description: "Gestiona equipos, asigna tareas y rastrea la productividad en tiempo real." },
        production: { title: "Producción y Stock", description: "Monitorea niveles de inventario, ciclos de producción y logística." },
        costs: { title: "Costes y Beneficios", description: "Obtén información sobre gastos, márgenes y análisis de rentabilidad." },
        analytics: { title: "Panel de Análisis", description: "Información en tiempo real e informes personalizados para tomar decisiones basadas en datos." },
        automation: { title: "Automatización Inteligente", description: "Flujos de trabajo con IA que ahorran tiempo y reducen errores manuales." }
      },
      benefitsTitle: "¿Por qué elegir Ulture?",
      benefitsSubtitle: "",
      benefits: {
        item1: "Ahorra más de 10 horas semanales en tareas administrativas",
        item3: "Visibilidad en tiempo real de todas las operaciones",
        item4: "Integración perfecta con herramientas existentes",
        item5: "Soporte y asistencia constante de nuestro equipo",
        item6: "Actualizaciones regulares con nuevas funciones"
      },
      stats: {
        farms: "Granjas usando Ulture",
        savings: "Ahorro promedio de costes",
        time: "Tiempo semanal ahorrado",
        satisfaction: "Satisfacción del cliente"
      },
      connectivity: {
        badge: "Red Global",
        title: "Conectividad global",
        description: "Gestiona tu operación agrícola desde cualquier lugar del mundo.",
        heroTitle: "Conéctate con tu agricultura desde cualquier lugar del mundo",
        heroSubtitle: "Una plataforma global para gestionar, analizar y tomar decisiones agrícolas sin fronteras.",
        stats: {
          countries: "Países",
          farms: "Granjas",
          availability: "Disponibilidad",
          uptime: "Tiempo activo"
        }
      },
      ctaTitle: "¿Listo para transformar tu granja?",
      ctaSubtitle: "Comienza tu prueba gratuita hoy y descubre cómo Ulture puede ayudarte a ahorrar tiempo y dinero.",
      ctaButton: "Iniciar Prueba Gratuita",
      ctaButtonSecondary: "Hablar con Ventas"
    },
    heroInfrastructure: {
      title: "Conéctate desde cualquier lugar",
      subtitle: "Una infraestructura digital viva que conecta campos, equipos y datos en tiempo real.",
      tag1: "Datos en tiempo real",
      tag2: "Decisiones con IA",
      tag3: "Escala global",
      tag4: "Automatización primero"
    },
    operations: {
      title: "Una plataforma. Control operativo total.",
      subtitle: "Gestiona todos los aspectos de tu operación agrícola desde un solo sistema conectado",
      footer: "Una plataforma. Una única fuente de verdad. Control operativo total.",
      learnMore: "Explorar Operaciones",
      intro: {
        title: "Una Plataforma, Control Operacional Completo",
        text: "Gestionar una operación agrícola no debería requerir docenas de herramientas diferentes. Nuestra plataforma centraliza todo lo que necesitas en un único sistema integrado, eliminando la complejidad y multiplicando tu eficiencia. Desde la planificación hasta el control de costos, cada módulo trabaja en conjunto para darte una visión completa y control total de tu operación. Ya no más datos fragmentados, hojas de cálculo desordenadas o información perdida entre sistemas.",
        bulletsTitle: "Con nuestra plataforma única obtienes:",
        bullets: {
          visibility: { title: "Visibilidad total", desc: "Todos tus datos operacionales en un solo lugar, actualizados en tiempo real" },
          decisions: { title: "Decisiones más inteligentes", desc: "Herramientas de forecasting y análisis que predicen tendencias y optimizan recursos" },
          savings: { title: "Ahorro automático", desc: "Control de costos integrado que identifica oportunidades de optimización sin esfuerzo manual" },
          duplication: { title: "Cero duplicación", desc: "Introduce la información una vez y se sincroniza automáticamente en todos los módulos" },
          scalability: { title: "Escalabilidad garantizada", desc: "Crece de 10 hectáreas a 1,000 sin cambiar de sistema" }
        }
      },
      tabs: {
        mission: {
          title: "Nuestra Misión",
          heading: "Nuestra Misión",
          description: "Empoderar a las empresas con herramientas digitales intuitivas que simplifican las operaciones, aumentan la eficiencia e impulsan el crecimiento sostenible."
        },
        vision: {
          title: "Nuestra Visión",
          heading: "Nuestra Visión",
          description: "Convertirnos en la plataforma de referencia para operaciones empresariales, donde cada equipo tenga acceso a herramientas de nivel empresarial."
        },
        innovation: {
          title: "Innovación",
          heading: "Innovación",
          description: "Buscamos constantemente nuevas formas de resolver problemas complejos, aprovechando la tecnología de vanguardia para crear soluciones prácticas."
        },
        commitment: {
          title: "Compromiso",
          heading: "Compromiso",
          description: "Estamos profundamente comprometidos con el éxito de nuestros clientes. Su crecimiento es nuestro crecimiento, y los apoyamos en cada paso."
        },
        collaboration: {
          title: "Colaboración",
          heading: "Colaboración",
          description: "Creemos en el poder de trabajar juntos. Nos asociamos con equipos, desarrolladores y expertos de la industria para construir mejores soluciones."
        }
      }
    },
    blogPosts: {
      post1: {
        title: "Cómo la IA está Revolucionando la Agricultura Moderna",
        subtitle: "Tecnología inteligente para una agricultura sostenible",
        category: "IA y Tecnología",
        excerpt: "Desde el monitoreo de cultivos en tiempo real hasta análisis predictivos, la IA está transformando cómo cultivamos y gestionamos operaciones agrícolas.",
        readTime: "6 min de lectura"
      },
      post2: {
        title: "Tomando Decisiones Agrícolas Basadas en Datos",
        subtitle: "Convierte información en acción",
        category: "Gestión de Datos",
        excerpt: "Aprende cómo los sistemas centralizados de datos ayudan a los productores a tomar mejores decisiones, reducir costes y mejorar la rentabilidad.",
        readTime: "5 min de lectura"
      },
      post3: {
        title: "Agricultura Sostenible a Través de la Tecnología",
        subtitle: "Mejor para el planeta, mejor para el negocio",
        category: "Sostenibilidad",
        excerpt: "Descubre cómo la tecnología inteligente permite prácticas sostenibles que benefician tanto al medio ambiente como a tu cuenta de resultados.",
        readTime: "7 min de lectura"
      },
      post4: {
        title: "Automatización en Operaciones Agrícolas",
        subtitle: "Trabaja de forma más inteligente, no más dura",
        category: "Automatización",
        excerpt: "Explora cómo la automatización reduce el trabajo manual, elimina errores y permite a los equipos centrarse en actividades estratégicas.",
        readTime: "6 min de lectura"
      },
      post5: {
        title: "Previsión de Producción con Confianza",
        subtitle: "Planifica con datos, no con suposiciones",
        category: "Planificación y Previsión",
        excerpt: "Aprende cómo los modelos predictivos ayudan a los productores a anticipar producción, gestionar inventario y cumplir compromisos con clientes.",
        readTime: "7 min de lectura"
      },
      post6: {
        title: "Control de Costes y Rentabilidad en Agricultura",
        subtitle: "Conoce tus números, mejora tus márgenes",
        category: "Gestión Financiera",
        excerpt: "Entiende cómo la asignación y análisis adecuado de costes ayuda a identificar ineficiencias y aumentar la rentabilidad.",
        readTime: "8 min de lectura"
      }
    },
    blogPage: {
      notFound: "Artículo no encontrado",
      backToBlog: "Volver al blog",
      quickInsight: "En resumen",
      readNext: "Siguiente artículo",
      readArticle: "Leer artículo",
      allRights: "Todos los derechos reservados."
    },
    buttons: {
      startNow: "Comenzar Ahora",
      learnMore: "Explorar Soluciones",
      getStarted: "Empezar",
      bookDemo: "Reservar Demo",
      contactUs: "Contactar",
      viewMore: "Ver Más"
    },
    wobbleCards: {
      card1: {
        title: "Control Digital Completo",
        description: "Gestiona toda tu operación agrícola desde una sola plataforma. Desde pedidos y facturas hasta seguimiento de producción y análisis de costes.",
        imageAlt: "Dashboard de la plataforma Ulture"
      },
      card2: {
        title: "Automatización Inteligente",
        description: "Reduce el trabajo manual y elimina errores. Nuestros flujos inteligentes se adaptan a tu operación."
      },
      card3: {
        title: "Conectividad Global",
        description: "Accede a tus datos desde cualquier lugar. Sincronización en tiempo real en todos tus dispositivos y equipos.",
        imageAlt: "Dashboard de la plataforma Ulture"
      }
    },
    ourStory: {
      mainTitle: "Estábamos cansados de ver ineficiencias en la agricultura, así que un día decidimos cambiar las cosas.",
      challenge: {
        badge: "El Desafío",
        title: "El Desafío",
        description: "Los productores son expertos como nadie en cultivar frutas y verduras — pero no en contabilidad, logística o gestión de datos. Nos dimos cuenta de que mientras sus campos prosperaban, sus sistemas de gestión estaban desactualizados."
      },
      realization: {
        badge: "La Realización",
        title: "La Realización",
        description: "La mayoría de las empresas de software construían herramientas desde oficinas, no desde el campo. Ninguna de ellas entendía realmente lo que los productores necesitaban. Después de años trabajando codo a codo con agricultores, decidimos diseñar soluciones basadas en experiencia real, no en teoría."
      },
      solution: {
        badge: "La Solución",
        title: "La Solución",
        description: "Construimos una plataforma que automatiza operaciones, centraliza información y transforma la gestión agrícola en un sistema conectado, digital y centrado en las personas."
      },
      stats: {
        betterFruits: { title: "Mejores Frutos", subtitle: "Calidad que puedes medir" },
        betterEfficiency: { title: "Mejor Eficiencia", subtitle: "Ahorro de tiempo y costes" },
        betterLives: { title: "Mejores Vidas", subtitle: "Para quienes alimentan al mundo" }
      }
    },
    techSection: {
      eyebrow: "PARA AGRICULTURA REAL Y OPERACIONES DIFÍCILES DE DIGITALIZAR",
      headlinePart1: "Construimos nuestra propia tecnología para ",
      headlineHighlight: "construir tu propio software.",
      subheadlineHighlight: "No somos un SaaS genérico.",
      subheadline: "Diseñamos y desarrollamos soluciones de software a medida adaptadas a las necesidades reales de cada operación agrícola — desde pequeños productores hasta negocios complejos y multicanal.",
      block1: {
        title: "Construimos nuestra propia tecnología",
        description: "Hemos desarrollado nuestra propia plataforma interna y herramientas para diseñar, desplegar y evolucionar software de forma eficiente.",
        bullets: [
          "Agilidad sin comprometer la calidad del producto final",
          "Soluciones personalizadas para casos de uso específicos"
        ]
      },
      block2: {
        title: "Software adaptado a cada operación",
        description: "Cada granja, empresa y operación es diferente. Por eso no vendemos software de talla única.",
        bullets: [
          "Analizamos tamaño, cultivos y flujos de trabajo existentes",
          "Adaptamos al nivel de madurez digital del equipo"
        ]
      },
      block3: {
        title: "No limitado a una industria",
        description: "Nuestra tecnología nos permite construir software para industrias con operaciones complejas.",
        bullets: [
          "Complejidad operativa y procesos manuales intensivos",
          "Control total y automatización inteligente"
        ]
      },
      supportingSentence: "No pedimos a las operaciones que se adapten al software. Construimos software que se adapta a las operaciones.",
      cta: "Háblanos de tu operación"
    },
    magicBento: {
      title: "Funcionalidades",
      titleHighlight: "Clave",
      subtitle: "Todo lo que necesitas para gestionar tu negocio de forma eficiente.",
      cta: "Saber más",
      documents: { 
        title: "Registros e Informes", 
        description: "Registra todas las actividades y genera informes en segundos. Filtra por campo, cultivo o fecha para encontrar lo que necesitas.",
        reportFiles: [
          { name: "Uso de Tratamientos Bloque 1", date: "Mayo 2026", type: "pdf" },
          { name: "Informe de Costos de Inventario", date: "Abril 2026", type: "xlsx" },
          { name: "Resumen de Cosecha T1", date: "2026", type: "pdf" },
          { name: "Registro de Actividades de Campo", date: "Junio 2026", type: "docx" },
          { name: "Informe de Aplicación de Plaguicidas", date: "Mayo 2026", type: "pdf" }
        ]
      },
      notifications: { 
        title: "Visión clara", 
        description: "Visualiza lo que ocurre en campos y actividades para tomar mejores decisiones.",
        notificationItems: [
          { text: "Tratamiento Bloque 1 ejecutado ✓", icon: "✓", color: "#00C9A7" },
          { text: "+50kg comprados: Producto A", icon: "📦", color: "#1E86FF" },
          { text: "Nivel de stock: Bueno", icon: "📊", color: "#00C9A7" },
          { text: "Informe mensual automático generado", icon: "📄", color: "#FFB800" }
        ]
      },
      integrations: { title: "Integraciones", description: "Compatible con más de 100 integraciones." },
      calendar: { title: "Calendario", description: "Usa el calendario para organizar tus tareas." }
    },
    aiWorkflowBento: {
      title: "diseñada en torno a tus operaciones",
      titleHighlight: "Tecnología",
      titlePart1: "diseñada en torno a",
      titlePart2: "Tus Operaciones",
      subtitle: "",
      cta: "Saber más",
      collaboration: {
        title: "Digitalización Operativa",
        description: "Estructuramos y digitalizamos las operaciones agrícolas diarias para un mayor control y eficiencia.",
        menuItems: {
          planTask: "Planificar Tarea",
          assignTask: "Asignar Tarea",
          manageTeam: "Gestionar Equipo",
          analytics: "Analíticas"
        }
      },
      integrations: {
        title: "Operaciones Conectadas",
        description: "Conectamos herramientas, datos y equipos para que la información fluya con claridad y nada se pierda entre sistemas."
      },
      insights: {
        title: "Claridad y Control",
        description: "Obtén una visión clara y en tiempo real de las operaciones, los recursos y el rendimiento, para tomar mejores decisiones con confianza.",
        metric: "Kg Cosechados"
      },
      automation: {
        title: "Planificación Inteligente y Alertas",
        description: "Planifica con antelación y recibe recordatorios y alertas automáticos cuando se requiere atención, para que nada se pase por alto.",
        notifications: {
          meeting: "Recordatorio de reunión",
          treatment: "Recordatorio de tratamiento: Bloque 1",
          lowStock: "Alerta: Nivel de stock bajo"
        }
      }
    },
    scrollVelocity: {
      line1: "Ulture Software",
      line2: "Control",
      line3: "Análisis",
      line4: "Automatización",
      line5: "Eficiencia",
      line6: "Integración",
      line7: "Innovación",
      line8: "Crecimiento"
    },
    featuresPreview: {
      titleLine1: "Crece tu empresa.\nMejora la eficiencia.\nMantén el control.",
      titleLine2: "Nosotros lo hacemos fácil.",
      subtitle: "A medida que aumentan los equipos, las parcelas y las actividades, la información es más difícil de seguir y el trabajo diario más complejo de gestionar.",
      exploreSolutions: "Descubre nuestras soluciones",
      viewAll: "Ver Todas las Funciones",
      highlights: {
        monitoring: "Registros de pulverización en PDF. Al instante.",
        alerts: "SMS automático a trabajadores para tareas y alertas.",
        analytics: "Cada tarea vinculada a una parcela."
      },
      seeItTitle: "Míralo en",
      seeItHighlight: "acción",
      dashboardDescription: "Un panel unificado que te da visibilidad completa de tus operaciones en invernadero.",
      stats: {
        pesticide: "Menos uso de pesticidas",
        reporting: "Reportes más rápidos",
        monitoring: "Monitoreo"
      },
      joinText: "Únete a cientos de operadores de invernadero que han transformado su gestión de plagas con nuestra plataforma. Desde pequeñas explotaciones familiares hasta grandes operaciones comerciales, nuestra plataforma se adapta a tus necesidades.",
      features: {
        control: { title: "Control Total, Cada Día", description: "Gestiona tus invernaderos, sigue cada plaga y registra todos los tratamientos desde una plataforma unificada. Sabe exactamente qué está pasando en toda tu operación." },
        costs: { title: "Sabe A Dónde Va Tu Dinero", description: "Sigue cada gasto, ve tu coste por hectárea y entiende a dónde va tu dinero. Sin más adivinanzas—solo números claros que te ayudan a tomar mejores decisiones." },
        oversight: { title: "Siempre Un Paso Adelante", description: "Alertas automatizadas, monitoreo de umbrales y recordatorios inteligentes aseguran que nunca pierdas un tratamiento crítico o fecha límite. Mantente adelante de los problemas antes de que crezcan." }
      },
      cards: {
        card1: { title: "Costes y Beneficios Difíciles de Controlar", description: "Sin datos fiables de las operaciones diarias, resulta difícil entender los costes reales y la rentabilidad." },
        card2: { title: "Demasiadas Herramientas, Sin Visión Global", description: "La información está repartida entre Excel, mensajes, cuadernos y programas que no se comunican entre sí." },
        card3: { title: "Los Problemas Se Detectan Demasiado Tarde", description: "Actividades olvidadas, faltas de stock o errores suelen detectarse cuando ya han afectado a la operación." },
        card4: { title: "Registros e Informes Poco Fiables", description: "Los registros e informes requieren tiempo y suelen ser incompletos o imprecisos debido a información dispersa." },
        card5: { title: "Siempre Centrados en el Día a Día", description: "Las prioridades diarias dejan poco margen para anticiparse o mejorar la forma de trabajar." }
      }
    },
    featuresPreviewReversed: {
      title: "Cada empresa tiene necesidades diferentes.",
      card1: {
        title: "Descubre nuestras soluciones de software personalizadas",
        description: "Software probado, diseñado para las operaciones agrícolas del día a día.",
        cta: "Descubre más"
      },
      card2: {
        title: "Crea tu propio software con nosotros",
        description: "Software a medida adaptado a tus procesos, cultivos y estructura.",
        cta: "Empieza ahora"
      },
      card3: {
        title: "Siempre mejorando. Siempre construyendo.",
        description: "Seguimos desarrollando y mejorando herramientas para acompañar tu operación a lo largo del tiempo."
      }
    },
    softwareShowcase: {
      ctaButton: "Empieza ahora",
      previewButton: "Vista previa",
      closePreview: "Cerrar vista previa"
    },
    caseStudies: {
      title: "Casos de éxito",
      subtitle: "Resultados reales de proyectos reales",
      sectionTitle: "Proyectos reales",
      sectionSubtitle: "Software a medida, diseñado, desarrollado y ya en uso en operaciones reales.",
      softwareCard: {
        title: "La idea",
        description: "Tomatico necesitaba una forma más clara de organizar su crecimiento como marca agrícola y mejorar la gestión interna de sus operaciones.\n\nEl objetivo era desarrollar un software propio que permitiera centralizar la información y facilitar la gestión diaria del proyecto.\n\nEl sistema debía permitir:\n\t•\torganizar stock y control de producto\n\t•\tgestionar eventos y acciones promocionales\n\t•\tcrear una comunidad activa alrededor de Tomatico\n\nLa idea era construir una herramienta simple que conectara todas estas áreas en un único sistema."
      },
      impactCard: {
        title: "El resultado",
        description: "Se desarrolló Tomatico Software, una plataforma interna que centraliza la gestión operativa de la marca y permite trabajar de forma mucho más organizada.\n\nGracias al sistema:\n\t•\tel equipo tiene claro qué tareas realizar y cuándo\n\t•\tla información está centralizada y accesible\n\t•\tlas operaciones diarias fluyen con mayor claridad\n\nEl resultado es un sistema que permite a Tomatico gestionar su crecimiento de forma más estructurada, manteniendo el control sobre su comunidad, sus eventos y su producto."
      },
      results: {
        eyebrow: "El impacto",
        stats: [
          { value: "3", label: "áreas en un solo sistema" },
          { value: "1", label: "plataforma a medida" },
          { value: "100%", label: "información centralizada" },
          { value: "24/7", label: "acceso del equipo" },
        ],
      },
      bridgeTitle: "¿Listo para construir el tuyo?",
      bridgeSubtitle: "Cada solución comienza por entender tus desafíos únicos.",
      ctaButton: "Empezar ahora"
    },
    ulturePest: {
      hero: {
        titleLine1: "Menos improvisación.",
        titleLine2: "Mejor planificación.",
        subtitle: "Hecho por agricultores, pensado para el campo.",
        bookCall: "Reservar Llamada",
        startFree: "Empezar Gratis",
        stat1Label: "Tratamientos Trazables",
        stat2Label: "Coste Exacto por m²",
        stat3Label: "Uso de pesticidas"
      },
      features: {
        titleLine1: "Dedícate al campo.",
        titleLine2: "Nosotros del resto.",
        subtitle: "Un solo sistema para planificar el trabajo, registrar los tratamientos, controlar el inventario y conocer los costes reales de tu explotación agrícola.",
        viewAll: "Ver Todas las Funciones",
        bullets: {
          bullet1: "Registros de tratamientos en PDF. Al instante.",
          bullet2: "SMS automáticos a los operarios para tareas y avisos.",
          bullet3: "Cada tarea vinculada a un bloque."
        },
        assistant: { title: "Asistente inteligente para el día a día.", description: "Usa audio, fotos o vídeo para registrar incidencias, crear tareas o analizar el estado del cultivo. El asistente detecta plagas, genera recomendaciones basadas en clima y datos de campo, asigna trabajo a tu equipo y te ayuda a tomar decisiones más rápido." },
        farm: { title: "Tu finca, organizada como en la realidad.", description: "Estructura ubicaciones, invernaderos, bloques y sectores según el funcionamiento real de tu explotación. Tratamientos, costes y ciclos se apoyan en esta base." },
        prevention: { title: "Prevención inteligente de plagas.", description: "Anticípate a los problemas antes de que aparezcan. Analiza condiciones de cultivo, clima y presión de plagas para detectar riesgos tempranos. Recibe alertas preventivas y recomendaciones claras para actuar a tiempo y proteger la producción." },
        spraylogs: { title: "Registros de tratamientos, siempre listos.", description: "Crea registros en segundos, filtra por cualquier criterio y exporta PDFs claros y listos para auditorías. Informes fiables para el uso diario o inspecciones oficiales." },
        treatments: { title: "Controla lo que se aplica,\npor localización.", description: "Supervisa tratamientos, presión de plagas y estado del cultivo por bloque y ciclo. Trazabilidad completa de aplicaciones biológicas y químicas." },
        stock: { title: "Un inventario que se actualiza solo.", description: "Importa tu stock en segundos y deja que se actualice automáticamente al completar tratamientos. Define niveles mínimos y recibe avisos antes de quedarte sin producto." },
        tasks: { title: "Convierte los planes en acción.", description: "Asigna tareas a trabajadores y ubicaciones, controla la ejecución y mantente al día con recordatorios por SMS. Cada trabajador ve solo lo que necesita, en el momento justo." },
        cycles: { title: "Planifica ciclos con claridad.", description: "Crea ciclos personalizados, aplícalos a varios bloques y gestiónalos fácilmente. Compara costes y resultados por ciclo, campaña tras campaña." }
      },
      video: {
        title: "Míralo en",
        titleHighlight: "acción",
        subtitle: "Observa cómo los gestores de invernaderos usan nuestra plataforma a diario.",
        stat1Label: "Ganancia de eficiencia",
        stat2Label: "Reportes más rápidos",
        stat3Label: "Acceso a datos"
      }
    }
  },
  
  it: {
    nav: {
      homepage: "Home",
      aboutUs: "Chi Siamo",
      software: "Our Apps",
      personalize: "Soluzioni Personalizzate",
      caseStudies: "Casi di Studio",
      blog: "Blog",
      contactUs: "Contatti",
      bookingCall: "Prenota Chiamata"
    },
    personalize: {
      title: "Crea il tuo",
      subtitle: "software",
      swipeHint: "Scorri per esplorare →",
      dashboardPreview: "Anteprima dashboard",
      whatWeBuildTitle: "Cosa costruiamo",
      whatWeBuildSubtitle: "Software su misura, progettato, sviluppato e già operativo in contesti reali.",
      bridgeSection: {
        title: "Non è un modello. Non è una piattaforma generica.",
        subtitle: "Costruito attorno ai tuoi flussi di lavoro. Pensato per l'uso quotidiano, non per le demo."
      },
      softwareCard: {
        title: "Software",
        description: "Abbiamo sviluppato un software su misura per gestire la manutenzione dei macchinari, le sedi, i guasti e gli interventi programmati in un unico sistema. Ogni macchina aveva uno storico completo, con attività assegnate automaticamente e priorità sempre chiare."
      },
      impactCard: {
        title: "Risultati",
        description: "L'azienda ha ridotto i fermi macchina, migliorato i tempi di intervento e ottenuto piena visibilità sui costi e sui problemi ricorrenti. La manutenzione è diventata più semplice, ordinata e sotto controllo."
      },
      newCard1: {
        title: "La Tua Visione",
        description: "Conosci la tua attività meglio di chiunque altro.\nSai cosa deve migliorare, cosa rallenta le cose e dove gli strumenti attuali non bastano.\n\nLa tua visione non riguarda l'aggiunta di software — si tratta di rendere il lavoro quotidiano più chiaro, semplice e facile da gestire.\n\nÈ da qui che iniziamo.",
        keyPoints: ["I tuoi flussi di lavoro", "I tuoi vincoli", "Le tue priorità"]
      },
      newCard2: {
        title: "La Nostra Soluzione",
        description: "Trasformiamo quella visione in software costruito attorno a come funziona realmente la tua azienda.\n\nNessun template. Nessuna piattaforma generica.\nSolo strumenti pratici progettati per l'uso quotidiano, plasmati attraverso collaborazione e feedback reale.",
        keyPoints: ["Costruito su operazioni reali", "Progettato per l'uso quotidiano", "Cresce con la tua azienda"]
      },
      buttons: {
        startBuilding: "Inizia a creare la tua soluzione",
        viewCaseStudies: "Scopri cosa abbiamo realizzato"
      },
      mobileButtons: {
        startBuilding: "Inizia",
        viewCaseStudies: "Il nostro lavoro"
      },
      workProcess: {
        sectionTitle: "Come lavoriamo",
        sectionTitleHighlight: "con te",
        sectionSubtitle: "Dall'idea a un software pronto per la produzione, con un processo chiaro e trasparente. Lavoriamo fianco a fianco con te, in modo semplice, strutturato e orientato a risultati concreti.",
        steps: {
          step1: {
            week: "Settimana 1",
            title: "Comprendiamo la tua",
            highlight: "attività",
            description: "Partiamo capendo come funziona davvero la tua azienda. Questa fase serve per ascoltare, mappare i processi e definire cosa deve fare il software — e cosa no.",
            features: ["Sessioni di analisi con il tuo team", "Revisione dei flussi di lavoro e degli strumenti attuali", "Definizione di obiettivi, ambito e priorità"]
          },
          step2: {
            week: "Settimane 2-4",
            title: "Sviluppo e",
            highlight: "perfezionamento",
            description: "Con la nostra piattaforma interna iniziamo a sviluppare rapidamente, iterando su feedback reali. Vedi i progressi ogni settimana e puoi correggere la direzione se necessario.",
            features: ["Sviluppo rapido con strumenti proprietari", "Check settimanali e demo live", "Test e miglioramenti continui"]
          },
          step3: {
            week: "Settimana 5",
            title: "Lancio e",
            highlight: "consegna",
            description: "Quando il software è pronto, prepariamo tutto per l'uso operativo. Formiamo il team, configuriamo i dati e andiamo online con il nostro supporto.",
            features: ["Onboarding e formazione del team", "Configurazione o migrazione dei dati", "Lancio in produzione con monitoraggio"]
          },
          step4: {
            week: "Continuativo",
            title: "Supporto e miglioramento",
            highlight: "continuo",
            description: "Il lancio non è la fine. Restiamo coinvolti mentre il software cresce con la tua azienda.",
            features: ["Miglioramenti e adattamenti continui", "Nuove funzionalità al cambiare delle esigenze", "Supporto tecnico affidabile quando serve"]
          }
        },
        cta: {
          title: "Pronto a",
          titleHighlight: "Iniziare",
          subtitle: "Prenota la tua chiamata gratuita e inizia a trasformare la tua azienda.",
          buttonText: "Prenota una Chiamata Gratuita"
        }
      }
    },
    hero: {
      badge: "Piattaforma di Agricoltura Digitale",
      title: "Pensa ai Campi.\nAl Resto pensiamo noi.",
      subtitle: "Dagli ordini e fatture al controllo della produzione, ti aiutiamo a organizzare e automatizzare la tua azienda agricola.",
      heroSubtitle: "Software, soluzioni su misura e consulenza per portare chiarezza e controllo nelle operazioni agricole.",
      cta: "Scopri le nostre soluzioni",
      ctaSecondary: "Prenota una demo gratuita"
    },
    whatIsUlture: {
      badge: "Cos'è Ulture?",
      title: "Stanchi di non avere il controllo",
      titleHighlight: "di tutto ciò che succede",
      subtitle: "Perdere tempo, denaro e lavoro perché niente è in un unico posto. Questo è ciò che risolviamo.",
      painPoints: {
        time: { title: "Tempo perso", description: "Saltando tra fogli di calcolo, carta e app che non comunicano." },
        money: { title: "Denaro disperso", description: "Senza visibilità reale sui costi, i margini e dove scappa la redditività." },
        work: { title: "Lavoro duplicato", description: "Inserendo gli stessi dati più e più volte perché niente è connesso." }
      },
      cta: "Scopri come lo risolviamo"
    },
    metrics: {
      problemsSolved: "Problemi Risolti",
      savedPerYear: "Risparmiati all'Anno",
      efficiency: "Efficienza Operativa"
    },
    categories: {
      title: "Tutto ciò di cui hai bisogno per",
      titleHighlight: "Far Crescere il Tuo Business",
      subtitle: "Sei aree chiave in cui Ulture ti aiuta a risparmiare tempo, ridurre i costi e aumentare l'efficienza.",
      administration: { title: "Amministrazione e Contabilità", description: "Automatizza fatture, contratti e monitoraggio finanziario con flussi intelligenti." },
      workers: { title: "Lavoratori e Compiti", description: "Gestisci team, assegna compiti e monitora la produttività in tempo reale." },
      production: { title: "Produzione e Magazzino", description: "Monitora livelli di inventario, cicli di produzione e logistica." },
      costs: { title: "Costi e Profitti", description: "Ottieni informazioni su spese, margini e analisi della redditività." },
      clients: { title: "Clienti e Ordini", description: "Ottimizza la gestione degli ordini e mantieni solide relazioni con i clienti." },
      marketing: { title: "Marketing e Immagine", description: "Costruisci la tua presenza di marca e raggiungi nuovi mercati efficacemente." }
    },
    footer: {
      copyright: "© 2026 Ulture",
      tagline: "Realizzato con cura per chi nutre il mondo.",
      product: "Prodotto",
      company: "Azienda",
      legal: "Legale",
      privacy: "Informativa sulla Privacy",
      terms: "Termini di Servizio",
      cookiePolicy: "Politica sui Cookie",
      dpa: "DPA"
    },
    whyWeExist: {
      badge: "La Nostra Storia",
      title: "Creato da agricoltori",
      titleHighlightBlue: "per agricoltori",
      subtitle: "Un percorso di scoperta verso la trasformazione digitale agricola",
      steps: {
        challenge: { number: "01", title: "La Sfida", text: "Gli agricoltori sono esperti come nessun altro nella produzione di frutta e verdura, ma non in contabilità, gestione dati o marketing. Ci siamo resi conto che mentre i loro campi prosperavano, i loro sistemi di gestione erano obsoleti." },
        revelation: { number: "02", title: "La Rivelazione", text: "Abbiamo scoperto che il 70% del tempo amministrativo viene perso navigando tra strumenti incompatibili e duplicando dati." },
        solution: { number: "03", title: "La Soluzione", text: "Abbiamo creato un ecosistema unificato dove ogni strumento comunica perfettamente, eliminando attriti e automatizzando i processi." },
        result: { number: "04", title: "Il Risultato", text: "Agricoltori che recuperano ore della loro giornata, riducono gli errori e prendono decisioni basate su dati reali in tempo reale." }
      },
      mission: {
        badge: "La Nostra Missione",
        title: "Accelerare la Trasformazione Digitale",
        titleHighlight: "dell'Agricoltura",
        subtitle: "Costruendo tecnologia che comprende le reali esigenze del campo"
      },
      pillars: {
        automation: { label: "Automazione", description: "Processi senza attrito" },
        integration: { label: "Integrazione", description: "Tutto connesso" },
        intelligence: { label: "Intelligenza", description: "IA adattiva" },
        efficiency: { label: "Efficienza", description: "Risultati reali" }
      }
    },
    macbook: {
      title: "Software Potente. Interfaccia Semplice.",
      subtitle: "Gestisci tutta la tua operazione agricola da una piattaforma"
    },
    about: {
      badge: "Chi è Ulture / Ulture",
      valueLabel: "Valore",
      hero: {
        title: "Ridefinire il Funzionamento dell'Agricoltura",
        subtitle: "Siamo un team appassionato di tecnologia e agricoltura, che lavora per digitalizzare e ottimizzare le aziende agricole in tutto il mondo."
      },
      story: {
        title: "La Nostra Storia",
        paragraph1: "Ulture è nato da una profonda comprensione delle sfide che gli agricoltori affrontano ogni giorno. I nostri fondatori, con radici sia nell'agricoltura che nella tecnologia, hanno visto un'opportunità per colmare il divario tra l'agricoltura tradizionale e le soluzioni digitali moderne.",
        paragraph2: "Dal 2020, sviluppiamo strumenti che semplificano la complessità della gestione agricola. Dalle piccole aziende familiari alle grandi imprese agricole, la nostra piattaforma si adatta alle esigenze uniche di ogni attività.",
        paragraph3: "Oggi aiutiamo centinaia di agricoltori in Europa e America Latina a gestire le loro operazioni in modo più efficiente, ridurre i costi e aumentare la redditività.",
        years: "Anni di Esperienza"
      },
      mvv: {
        title: "Missione, Visione e Valori",
        subtitle: "I principi che guidano tutto ciò che facciamo"
      },
      mission: {
        title: "La Nostra Missione",
        description: "Potenziare agricoltori e aziende agricole con strumenti digitali intuitivi che semplificano la gestione, aumentano l'efficienza e promuovono la crescita sostenibile."
      },
      vision: {
        title: "La Nostra Visione",
        description: "Diventare la piattaforma di riferimento globale per la digitalizzazione agricola, dove ogni agricoltore abbia accesso alla tecnologia che una volta era disponibile solo per le grandi aziende."
      },
      values: {
        innovation: { title: "Innovazione", description: "Cerchiamo costantemente nuovi modi per risolvere vecchi problemi, sfruttando tecnologie all'avanguardia per creare soluzioni pratiche." },
        commitment: { title: "Impegno", description: "Siamo profondamente impegnati nel successo dei nostri clienti. La loro crescita è la nostra crescita, e li accompagniamo in ogni passo." },
        collaboration: { title: "Collaborazione", description: "Crediamo nel potere del lavoro di squadra. Collaboriamo con agricoltori, agronomi ed esperti del settore per costruire soluzioni migliori." }
      },
      team: {
        title: "Conosci il nostro team fondatore",
        subtitle: "Le persone dietro Ulture: radici nel campo e passione per la tecnologia.",
        headline: "Il team",
        description: "Siamo un team appassionato di tecnologia ed efficienza, che lavora per aiutare le aziende di tutto il mondo a operare in modo più intelligente.",
        ceo: "Growth & Strategy",
        cto: "CTO e Co-Fondatrice",
        coo: "Direttore Operativo",
        head_product: "Responsabile Prodotto",
        roles: {
          ceoCofounder: "Growth & Strategy",
          ctoCofounder: "CTO e Co-Fondatore",
          headProduct: "Responsabile Prodotto",
          headDevelopment: "Responsabile Sviluppo"
        }
      },
      timeline: {
        title: "Dal campo, per il campo",
        subtitle: "Tappe fondamentali nella nostra storia",
        welcomeText: "Questa è ",
        flipWords: ["Ulture", "La nostra storia"],
        milestone1: { date: "1974", title: "Le radici dell'azienda di famiglia", description: "La nostra storia nasce dall'agricoltura. Nel 1974, i nostri nonni costruirono una delle prime serre ad Almeria, in Spagna. Un lavoro fatto a mano, basato sull'esperienza, sull'impegno e su un forte legame con la terra." },
        milestone2: { date: "2020", title: "La crescita porta nuove sfide", description: "Con il tempo, l'azienda di famiglia è cresciuta. Serre moderne e nuove tecnologie hanno migliorato la produzione, ma la gestione operativa, amministrativa e dei costi è diventata sempre più complessa." },
        milestone3: { date: "2024", title: "Il problema diventa evidente", description: "Lavorare direttamente all'interno dell'azienda ha reso chiaro un aspetto fondamentale: la tecnologia delle serre era avanzata, ma la gestione delle attività quotidiane no. Le informazioni erano frammentate e i processi troppo manuali." },
        milestone4: { date: "Metà 2025", title: "Sviluppato per l'uso reale", description: "La prima versione del software viene sviluppata e utilizzata direttamente nelle serre di famiglia. Non come prodotto, ma come strumento pratico per risolvere problemi quotidiani." },
        milestone5: { date: "Febbraio 2026", title: "Nasce Ulture", description: "Dopo mesi di utilizzo reale e miglioramenti continui, Ulture viene lanciato ufficialmente, mantenendo un focus chiaro su semplicità e concretezza." },
        milestone6: { date: "Aprile 2026", title: "Ulture Pest", description: "Ulture Pest viene introdotto come prima soluzione dedicata al monitoraggio dei parassiti, ai trattamenti e ai registri di irrorazione." },
        milestone7: { date: "2026", title: "E ora", description: "Ulture continua a crescere. Nuovi strumenti e soluzioni sono in sviluppo, guidati dalle esigenze reali del campo." },
      },
      cta: {
        title: "Pronto a Trasformare la Tua Azienda?",
        description: "Unisciti a centinaia di agricoltori che stanno già risparmiando tempo e denaro con Ulture.",
        contact: "Contattaci",
        demo: "Richiedi una Demo"
      }
    },
    aboutStory: {
      sectionTitle: "La Nostra Storia",
      sectionSubtitle: "La nostra storia, costruita dal campo",
      intro: {
        title: "Eravamo stanchi di vedere inefficienze nell'agricoltura",
        subtitle: "così un giorno abbiamo deciso di cambiare le cose."
      },
      challenge: {
        title: "La Sfida",
        text: "I coltivatori sono esperti come nessun altro nella produzione di frutta e verdura — ma non in contabilità, logistica o gestione dei dati. Ci siamo resi conto che mentre i loro campi prosperavano, i loro sistemi di gestione erano obsoleti."
      },
      realization: {
        title: "La Consapevolezza",
        text: "La maggior parte delle aziende software costruiva strumenti dagli uffici, non dai campi. Nessuna di loro capiva veramente di cosa avessero bisogno i coltivatori. Dopo anni di lavoro fianco a fianco con gli agricoltori, abbiamo deciso di progettare soluzioni basate su esperienza reale, non teoria."
      },
      solution: {
        title: "La Soluzione",
        text: "Abbiamo costruito una piattaforma che automatizza le operazioni, centralizza le informazioni e trasforma la gestione agricola in un sistema connesso, digitale e incentrato sulle persone."
      },
      transformation: {
        title: "La Trasformazione",
        text: "Quando i coltivatori si concentrano sulla coltivazione e noi ci occupiamo del resto — tecnologia, dati, coordinamento — i risultati sono straordinari. Frutti migliori, maggiore efficienza, vite migliori per chi nutre il mondo."
      },
      mission: {
        title: "La Nostra Missione",
        subtitle: "Accelerare la trasformazione digitale dell'agricoltura",
        pillars: {
          automation: { title: "Automazione", text: "Rendere ogni processo più efficiente — dalla produzione alle vendite" },
          integration: { title: "Integrazione", text: "Collegare tutte le parti della tua operazione in un unico posto" },
          dataIntelligence: { title: "Intelligenza dei Dati", text: "Trasformare le informazioni in decisioni strategiche" }
        }
      }
    },
    blog: {
      badge: "Ultime novità",
      title: "Blog e risorse",
      subtitle: "Scopri le ultime novità, consigli e storie sull'agricoltura digitale e la gestione agricola.",
      heroTitle: "Le ultime novità agricole",
      searchPlaceholder: "Cerca articoli...",
      postTitle: "Come la tecnologia sta trasformando l'agricoltura",
      postExcerpt: "Esplora come gli strumenti digitali moderni aiutano gli agricoltori ad aumentare l'efficienza e ridurre i costi.",
      readMore: "Leggi di più",
      comingSoonTitle: "Altri contenuti in arrivo!",
      comingSoonText: "Stiamo lavorando per offrirti più informazioni e risorse preziose."
    },
    contact: {
      badge: "Contattaci",
      title: "Contatti",
      subtitle: "Hai domande sulla nostra piattaforma? Ci piacerebbe sentirti.",
      emailLabel: "Email",
      phoneLabel: "Telefono",
      locationLabel: "Sede",
      infoTitle: "Parliamo",
      infoSubtitle: "Che tu abbia domande su funzionalità, prezzi o altro, il nostro team è pronto a rispondere.",
      responseTime: "Di solito rispondiamo entro 24 ore",
      successTitle: "Messaggio inviato!",
      successMessage: "Ti risponderemo presto.",
      thankYouTitle: "Grazie!",
      thankYouMessage: "Il tuo messaggio è stato inviato con successo. Ti risponderemo presto.",
      sendAnother: "Invia un altro messaggio",
      nameLabel: "Nome",
      namePlaceholder: "Il tuo nome",
      emailFormLabel: "Email",
      emailPlaceholder: "tua@email.com",
      companyLabel: "Azienda (opzionale)",
      companyPlaceholder: "La tua azienda",
      messageLabel: "Messaggio",
      messagePlaceholder: "Dicci come possiamo aiutarti...",
      sending: "Invio in corso...",
      submitButton: "Invia Messaggio"
    },
    software: {
      badge: "La Nostra Piattaforma",
      title: "Software Potente per l'Agricoltura Moderna",
      subtitle: "Una piattaforma per gestire tutta la tua operazione agricola — dal campo alle finanze.",
      cta: "Inizia",
      ctaSecondary: "Richiedi Demo",
      screenshotAlt: "Dashboard di Ulture",
      featuresTitle: "Tutto ciò che ti serve per gestire la tua azienda",
      featuresSubtitle: "La nostra suite completa di strumenti copre ogni aspetto della gestione agricola.",
      features: {
        administration: { title: "Amministrazione e Contabilità", description: "Automatizza fatture, contratti e monitoraggio finanziario con flussi intelligenti." },
        workers: { title: "Lavoratori e Compiti", description: "Gestisci team, assegna compiti e monitora la produttività in tempo reale." },
        production: { title: "Produzione e Magazzino", description: "Monitora livelli di inventario, cicli di produzione e logistica." },
        costs: { title: "Costi e Profitti", description: "Ottieni informazioni su spese, margini e analisi della redditività." },
        analytics: { title: "Dashboard Analitica", description: "Informazioni in tempo reale e report personalizzati per decisioni basate sui dati." },
        automation: { title: "Automazione Intelligente", description: "Flussi di lavoro con IA che fanno risparmiare tempo e riducono gli errori manuali." }
      },
      benefitsTitle: "Perché scegliere Ulture?",
      benefitsSubtitle: "",
      benefits: {
        item1: "Risparmia più di 10 ore settimanali in attività amministrative",
        item3: "Visibilità in tempo reale su tutte le operazioni",
        item4: "Integrazione perfetta con gli strumenti esistenti",
        item5: "Supporto e assistenza costante dal nostro team",
        item6: "Aggiornamenti regolari con nuove funzionalità"
      },
      stats: {
        farms: "Aziende che usano Ulture",
        savings: "Risparmio medio sui costi",
        time: "Tempo settimanale risparmiato",
        satisfaction: "Soddisfazione del cliente"
      },
      connectivity: {
        badge: "Rete Globale",
        title: "Connettività globale",
        description: "Gestisci la tua operazione agricola da qualsiasi parte del mondo.",
        heroTitle: "Connettiti con la tua agricoltura da qualsiasi parte del mondo",
        heroSubtitle: "Una piattaforma globale per gestire, analizzare e prendere decisioni agricole senza confini.",
        stats: {
          countries: "Paesi",
          farms: "Aziende",
          availability: "Disponibilità",
          uptime: "Tempo attivo"
        }
      },
      ctaTitle: "Pronto a trasformare la tua azienda?",
      ctaSubtitle: "Inizia la tua prova gratuita oggi e scopri come Ulture può aiutarti a risparmiare tempo e denaro.",
      ctaButton: "Inizia Prova Gratuita",
      ctaButtonSecondary: "Parla con le Vendite"
    },
    heroInfrastructure: {
      title: "Connettiti da ovunque",
      subtitle: "Un'infrastruttura digitale vivente che collega campi, team e dati in tempo reale.",
      tag1: "Dati in tempo reale",
      tag2: "Decisioni basate su IA",
      tag3: "Scala globale",
      tag4: "Automazione prima"
    },
    operations: {
      title: "Una piattaforma. Controllo operativo completo.",
      subtitle: "Gestisci ogni aspetto della tua operazione agricola da un unico sistema connesso",
      footer: "Una piattaforma. Un'unica fonte di verità. Controllo operativo completo.",
      learnMore: "Esplora le Operazioni",
      intro: {
        title: "Una Piattaforma, Controllo Operativo Completo",
        text: "Gestire un'operazione agricola non dovrebbe richiedere decine di strumenti diversi. La nostra piattaforma centralizza tutto ciò di cui hai bisogno in un unico sistema integrato, eliminando la complessità e moltiplicando la tua efficienza. Dalla pianificazione al controllo dei costi, ogni modulo lavora insieme per darti visibilità completa e controllo totale della tua operazione. Niente più dati frammentati, fogli di calcolo disordinati o informazioni perse tra sistemi.",
        bulletsTitle: "Con la nostra piattaforma unificata ottieni:",
        bullets: {
          visibility: { title: "Visibilità totale", desc: "Tutti i tuoi dati operativi in un unico posto, aggiornati in tempo reale" },
          decisions: { title: "Decisioni più intelligenti", desc: "Strumenti di previsione e analisi che prevedono le tendenze e ottimizzano le risorse" },
          savings: { title: "Risparmio automatico", desc: "Controllo dei costi integrato che identifica opportunità di ottimizzazione senza sforzo manuale" },
          duplication: { title: "Zero duplicazione", desc: "Inserisci le informazioni una volta e si sincronizzano automaticamente in tutti i moduli" },
          scalability: { title: "Scalabilità garantita", desc: "Cresci da 10 ettari a 1.000 senza cambiare sistema" }
        }
      },
      tabs: {
        mission: {
          title: "La Nostra Missione",
          heading: "La Nostra Missione",
          description: "Dare potere alle aziende con strumenti digitali intuitivi che semplificano le operazioni, aumentano l'efficienza e guidano la crescita sostenibile."
        },
        vision: {
          title: "La Nostra Visione",
          heading: "La Nostra Visione",
          description: "Diventare la piattaforma di riferimento per le operazioni aziendali, dove ogni team ha accesso a strumenti di livello enterprise."
        },
        innovation: {
          title: "Innovazione",
          heading: "Innovazione",
          description: "Cerchiamo costantemente nuovi modi per risolvere problemi complessi, sfruttando la tecnologia all'avanguardia per creare soluzioni pratiche."
        },
        commitment: {
          title: "Impegno",
          heading: "Impegno",
          description: "Siamo profondamente impegnati nel successo dei nostri clienti. La loro crescita è la nostra crescita, e li supportiamo in ogni passo."
        },
        collaboration: {
          title: "Collaborazione",
          heading: "Collaborazione",
          description: "Crediamo nel potere di lavorare insieme. Collaboriamo con team, sviluppatori ed esperti del settore per costruire soluzioni migliori."
        }
      }
    },
    blogPosts: {
      post1: {
        title: "Come l'IA sta Rivoluzionando l'Agricoltura Moderna",
        subtitle: "Tecnologia intelligente per un'agricoltura sostenibile",
        category: "IA e Tecnologia",
        excerpt: "Dal monitoraggio delle colture in tempo reale all'analisi predittiva, l'IA sta trasformando il modo in cui coltiviamo e gestiamo le operazioni agricole.",
        readTime: "6 min di lettura"
      },
      post2: {
        title: "Decisioni Agricole Basate sui Dati",
        subtitle: "Trasforma le informazioni in azione",
        category: "Gestione Dati",
        excerpt: "Scopri come i sistemi di dati centralizzati aiutano i coltivatori a prendere decisioni migliori, ridurre i costi e migliorare la redditività.",
        readTime: "5 min di lettura"
      },
      post3: {
        title: "Agricoltura Sostenibile Attraverso la Tecnologia",
        subtitle: "Meglio per il pianeta, meglio per il business",
        category: "Sostenibilità",
        excerpt: "Scopri come la tecnologia intelligente consente pratiche sostenibili che beneficiano sia l'ambiente che il tuo bilancio.",
        readTime: "7 min di lettura"
      },
      post4: {
        title: "Automazione nelle Operazioni Agricole",
        subtitle: "Lavora in modo più intelligente, non più duro",
        category: "Automazione",
        excerpt: "Esplora come l'automazione riduce il lavoro manuale, elimina gli errori e permette ai team di concentrarsi su attività strategiche.",
        readTime: "6 min di lettura"
      },
      post5: {
        title: "Previsione della Produzione con Fiducia",
        subtitle: "Pianifica con i dati, non con le supposizioni",
        category: "Pianificazione e Previsioni",
        excerpt: "Scopri come i modelli predittivi aiutano i coltivatori ad anticipare la produzione, gestire l'inventario e rispettare gli impegni con i clienti.",
        readTime: "7 min di lettura"
      },
      post6: {
        title: "Controllo Costi e Redditività in Agricoltura",
        subtitle: "Conosci i tuoi numeri, migliora i tuoi margini",
        category: "Gestione Finanziaria",
        excerpt: "Comprendi come l'allocazione e l'analisi corretta dei costi aiutano a identificare inefficienze e aumentare la redditività.",
        readTime: "8 min di lettura"
      }
    },
    blogPage: {
      notFound: "Articolo non trovato",
      backToBlog: "Torna al blog",
      quickInsight: "In breve",
      readNext: "Articolo successivo",
      readArticle: "Leggi l'articolo",
      allRights: "Tutti i diritti riservati."
    },
    buttons: {
      startNow: "Inizia Ora",
      learnMore: "Esplora le Soluzioni",
      getStarted: "Comincia",
      bookDemo: "Prenota Demo",
      contactUs: "Contattaci",
      viewMore: "Vedi Altro"
    },
    wobbleCards: {
      card1: {
        title: "Controllo Digitale Completo",
        description: "Gestisci l'intera operazione agricola da un'unica piattaforma. Da ordini e fatture al monitoraggio della produzione e analisi dei costi.",
        imageAlt: "Dashboard della piattaforma Ulture"
      },
      card2: {
        title: "Automazione Intelligente",
        description: "Riduci il lavoro manuale ed elimina gli errori. I nostri flussi intelligenti si adattano alla tua operazione."
      },
      card3: {
        title: "Connettività Globale",
        description: "Accedi ai tuoi dati ovunque. Sincronizzazione in tempo reale su tutti i tuoi dispositivi e team.",
        imageAlt: "Dashboard della piattaforma Ulture"
      }
    },
    ourStory: {
      mainTitle: "Eravamo stanchi di vedere inefficienze nell'agricoltura, così un giorno abbiamo deciso di cambiare le cose.",
      challenge: {
        badge: "La Sfida",
        title: "La Sfida",
        description: "I coltivatori sono esperti come nessun altro nella produzione di frutta e verdura — ma non in contabilità, logistica o gestione dati. Ci siamo resi conto che mentre i loro campi prosperavano, i loro sistemi di gestione erano obsoleti."
      },
      realization: {
        badge: "La Consapevolezza",
        title: "La Consapevolezza",
        description: "La maggior parte delle aziende software costruiva strumenti dagli uffici, non dai campi. Nessuna di loro capiva veramente cosa servisse ai coltivatori. Dopo anni di lavoro fianco a fianco con gli agricoltori, abbiamo deciso di progettare soluzioni basate su esperienza reale, non teoria."
      },
      solution: {
        badge: "La Soluzione",
        title: "La Soluzione",
        description: "Abbiamo costruito una piattaforma che automatizza le operazioni, centralizza le informazioni e trasforma la gestione agricola in un sistema connesso, digitale e incentrato sulle persone."
      },
      stats: {
        betterFruits: { title: "Frutti Migliori", subtitle: "Qualità che puoi misurare" },
        betterEfficiency: { title: "Migliore Efficienza", subtitle: "Risparmio di tempo e costi" },
        betterLives: { title: "Vite Migliori", subtitle: "Per chi nutre il mondo" }
      }
    },
    techSection: {
      eyebrow: "PER L'AGRICOLTURA REALE E OPERAZIONI DIFFICILI DA DIGITALIZZARE",
      headlinePart1: "Costruiamo la nostra tecnologia per ",
      headlineHighlight: "costruire il tuo software.",
      subheadlineHighlight: "Non siamo un SaaS generico.",
      subheadline: "Progettiamo e sviluppiamo soluzioni software su misura adattate alle reali esigenze di ogni operazione agricola — dai piccoli produttori alle aziende complesse e multicanale.",
      block1: {
        title: "Costruiamo la nostra tecnologia",
        description: "Abbiamo sviluppato la nostra piattaforma interna e strumenti per progettare, distribuire e far evolvere il software in modo efficiente.",
        bullets: [
          "Muoversi velocemente senza sacrificare l'affidabilità",
          "Adattare soluzioni a casi d'uso molto specifici",
          "Costruire software che si adatta a come le persone lavorano realmente sul campo"
        ]
      },
      block2: {
        title: "Software adattato a ogni operazione",
        description: "Ogni azienda agricola, impresa e operazione è diversa. Per questo non vendiamo software universale.",
        bullets: [
          "La dimensione dell'operazione",
          "Il tipo di colture o prodotti",
          "Flussi di lavoro e strumenti esistenti",
          "Il livello di maturità digitale del team"
        ]
      },
      block3: {
        title: "Non limitato a un'industria",
        description: "Sebbene le nostre radici siano nell'agricoltura, la nostra tecnologia ci permette di costruire software personalizzato per altre industrie con operazioni complesse.",
        bullets: [
          "Complessità operativa",
          "Processi manuali",
          "Dati disconnessi",
          "Necessità di controllo e automazione"
        ]
      },
      supportingSentence: "Non chiediamo alle operazioni di adattarsi al software. Costruiamo software che si adatta alle operazioni.",
      cta: "Parlaci della tua operazione"
    },
    magicBento: {
      title: "Funzionalità",
      titleHighlight: "Chiave",
      subtitle: "Tutto ciò di cui hai bisogno per gestire il tuo business in modo efficiente.",
      cta: "Scopri di più",
      documents: { 
        title: "Registri e Report", 
        description: "Tieni traccia di tutte le attività e genera report in pochi secondi. Filtra per campo, coltura o data per trovare ciò che serve.",
        reportFiles: [
          { name: "Utilizzo Trattamenti Blocco 1", date: "Maggio 2026", type: "pdf" },
          { name: "Report Costi Inventario", date: "Aprile 2026", type: "xlsx" },
          { name: "Riepilogo Raccolta T1", date: "2026", type: "pdf" },
          { name: "Registro Attività Campo", date: "Giugno 2026", type: "docx" },
          { name: "Report Applicazione Pesticidi", date: "Maggio 2026", type: "pdf" }
        ]
      },
      notifications: { 
        title: "Visione chiara", 
        description: "Vedi cosa succede tra campi e attività per supportare decisioni migliori.",
        notificationItems: [
          { text: "Trattamento Blocco 1 eseguito ✓", icon: "✓", color: "#00C9A7" },
          { text: "+50kg acquistati: Prodotto A", icon: "📦", color: "#1E86FF" },
          { text: "Livello stock: Buono", icon: "📊", color: "#00C9A7" },
          { text: "Report mensile automatico generato", icon: "📄", color: "#FFB800" }
        ]
      },
      integrations: { title: "Integrazioni", description: "Compatibile con oltre 100 integrazioni." },
      calendar: { title: "Calendario", description: "Usa il calendario per organizzare le tue attività." }
    },
    aiWorkflowBento: {
      title: "progettata attorno alle tue operazioni",
      titleHighlight: "Tecnologia",
      titlePart1: "Progettata Attorno alle",
      titlePart2: "Tue Operazioni",
      subtitle: "",
      cta: "Scopri di più",
      collaboration: {
        title: "Digitalizzazione Operativa",
        description: "Strutturiamo e digitalizziamo le operazioni agricole quotidiane per un maggiore controllo ed efficienza.",
        menuItems: {
          planTask: "Pianifica Attività",
          assignTask: "Assegna Attività",
          manageTeam: "Gestisci Team",
          analytics: "Analisi"
        }
      },
      integrations: {
        title: "Operazioni Connesse",
        description: "Colleghiamo strumenti, dati e team affinché le informazioni circolino in modo chiaro e nulla vada perso tra i diversi sistemi."
      },
      insights: {
        title: "Chiarezza e Controllo",
        description: "Ottieni una visione chiara e in tempo reale di operazioni, risorse e performance, per prendere decisioni migliori con sicurezza.",
        metric: "Kg Raccolti"
      },
      automation: {
        title: "Pianificazione Intelligente e Avvisi",
        description: "Pianifica in anticipo e ricevi promemoria e avvisi automatici quando è necessaria attenzione, così nulla viene trascurato.",
        notifications: {
          meeting: "Promemoria riunione",
          treatment: "Promemoria trattamento: Blocco 1",
          lowStock: "Avviso: Livelli di scorte bassi"
        }
      }
    },
    scrollVelocity: {
      line1: "Ulture Software",
      line2: "Controllo",
      line3: "Analisi",
      line4: "Automazione",
      line5: "Efficienza",
      line6: "Integrazione",
      line7: "Innovazione",
      line8: "Crescita"
    },
    featuresPreview: {
      titleLine1: "Fai crescere la tua azienda.\nMigliora la visibilità.\nMantieni il controllo.",
      titleLine2: "Noi lo rendiamo facile.",
      subtitle: "Con l'aumento di team, campi e attività, le informazioni diventano più difficili da seguire e il lavoro quotidiano più complesso da gestire.",
      exploreSolutions: "Scopri le nostre soluzioni",
      viewAll: "Vedi Tutte le Funzionalità",
      highlights: {
        monitoring: "Registri di spruzzatura in PDF. Istantaneamente.",
        alerts: "SMS automatici ai lavoratori per compiti e avvisi.",
        analytics: "Ogni compito collegato a un blocco."
      },
      seeItTitle: "Guardalo in",
      seeItHighlight: "azione",
      dashboardDescription: "Una dashboard unificata che ti dà visibilità completa sulle tue operazioni in serra.",
      stats: {
        pesticide: "Meno uso di pesticidi",
        reporting: "Report più veloci",
        monitoring: "Monitoraggio"
      },
      joinText: "Unisciti a centinaia di operatori di serre che hanno trasformato la loro gestione dei parassiti con la nostra piattaforma. Dalle piccole aziende familiari alle grandi operazioni commerciali, la nostra piattaforma si adatta alle tue esigenze.",
      features: {
        control: { title: "Controllo Totale, Ogni Giorno", description: "Gestisci le tue serre, traccia ogni parassita e registra tutti i trattamenti da una piattaforma unificata. Sai esattamente cosa sta succedendo in tutta la tua operazione." },
        costs: { title: "Sai Dove Vanno i Tuoi Soldi", description: "Traccia ogni spesa, vedi il tuo costo per ettaro e comprendi dove vanno i tuoi soldi. Niente più supposizioni—solo numeri chiari che ti aiutano a prendere decisioni migliori." },
        oversight: { title: "Sempre Un Passo Avanti", description: "Avvisi automatizzati, monitoraggio delle soglie e promemoria intelligenti assicurano che non ti perda mai un trattamento critico o una scadenza. Resta avanti ai problemi prima che crescano." }
      },
      cards: {
        card1: { title: "Costi e Profitti Difficili da Monitorare", description: "Senza dati affidabili dalle operazioni quotidiane, diventa difficile comprendere i costi reali e la reale redditività." },
        card2: { title: "Troppi Strumenti, Nessuna Visione d'Insieme", description: "Le informazioni sono distribuite tra file Excel, messaggi, appunti e software che non comunicano tra loro." },
        card3: { title: "I Problemi Emergono Troppo Tardi", description: "Attività mancate, carenze o errori vengono spesso scoperti solo dopo aver già inciso sulle operazioni." },
        card4: { title: "Registri e Report Poco Affidabili", description: "La preparazione di registri e report richiede tempo ed è spesso incompleta o imprecisa, a causa di informazioni frammentate." },
        card5: { title: "Sempre Concentrati sul Presente", description: "Le priorità quotidiane lasciano poco spazio per pianificare in anticipo o migliorare l'organizzazione del lavoro." }
      }
    },
    featuresPreviewReversed: {
      title: "Ogni azienda ha esigenze diverse.",
      card1: {
        title: "Scopri le nostre soluzioni software personalizzate",
        description: "Software collaudato progettato per le operazioni agricole quotidiane.",
        cta: "Scopri di più"
      },
      card2: {
        title: "Crea il tuo software insieme a noi",
        description: "Software personalizzato adattato ai tuoi flussi di lavoro, colture e struttura.",
        cta: "Inizia subito"
      },
      card3: {
        title: "Sempre in miglioramento. Sempre in costruzione.",
        description: "Continuiamo a sviluppare e migliorare strumenti per supportare la tua operazione nel tempo."
      }
    },
    softwareShowcase: {
      ctaButton: "Inizia subito",
      previewButton: "Anteprima",
      closePreview: "Chiudi anteprima"
    },
    caseStudies: {
      title: "Casi di successo",
      subtitle: "Risultati reali da progetti reali",
      sectionTitle: "Progetti reali",
      sectionSubtitle: "Software su misura, progettato, sviluppato e già operativo in contesti reali.",
      softwareCard: {
        title: "L'idea",
        description: "Tomatico aveva bisogno di un modo più chiaro per organizzare la propria crescita come marchio agricolo e migliorare la gestione interna delle operazioni.\n\nL'obiettivo era sviluppare un software proprio che permettesse di centralizzare le informazioni e semplificare la gestione quotidiana del progetto.\n\nIl sistema doveva permettere di:\n\t•\torganizzare stock e controllo prodotto\n\t•\tgestire eventi e azioni promozionali\n\t•\tcreare una comunità attiva intorno a Tomatico\n\nL'idea era costruire uno strumento semplice che collegasse tutte queste aree in un unico sistema."
      },
      impactCard: {
        title: "Il risultato",
        description: "È stato sviluppato Tomatico Software, una piattaforma interna che centralizza la gestione operativa del marchio e permette di lavorare in modo molto più organizzato.\n\nGrazie al sistema:\n\t•\til team sa esattamente quali attività svolgere e quando\n\t•\tle informazioni sono centralizzate e accessibili\n\t•\tle operazioni quotidiane scorrono con maggiore chiarezza\n\nIl risultato è un sistema che permette a Tomatico di gestire la propria crescita in modo più strutturato, mantenendo il controllo sulla comunità, gli eventi e il prodotto."
      },
      results: {
        eyebrow: "L'impatto",
        stats: [
          { value: "3", label: "aree in un unico sistema" },
          { value: "1", label: "piattaforma su misura" },
          { value: "100%", label: "informazioni centralizzate" },
          { value: "24/7", label: "accesso del team" },
        ],
      },
      bridgeTitle: "Pronto a costruire il tuo?",
      bridgeSubtitle: "Ogni soluzione inizia dalla comprensione delle tue sfide uniche.",
      ctaButton: "Inizia ora"
    },
    ulturePest: {
      hero: {
        titleLine1: "Meno improvvisazione.",
        titleLine2: "Più pianificazione.",
        subtitle: "Dagli agricoltori, per gli agricoltori.",
        bookCall: "Prenota una Chiamata",
        startFree: "Inizia Gratis",
        stat1Label: "Trattamenti Tracciabili",
        stat2Label: "Costo Esatto per m²",
        stat3Label: "Uso di pesticidi"
      },
      features: {
        titleLine1: "Pensa alla tua azienda.",
        titleLine2: "Al resto pensiamo noi.",
        subtitle: "Un unico sistema per pianificare il lavoro, tracciare i trattamenti, controllare il magazzino e conoscere i costi reali della tua azienda agricola.",
        viewAll: "Vedi Tutte le Funzionalità",
        bullets: {
          bullet1: "Registri dei trattamenti in PDF. Subito.",
          bullet2: "SMS automatici agli operatori per attività e avvisi.",
          bullet3: "Ogni attività collegata a un appezzamento."
        },
        assistant: { title: "Assistente intelligente per il quotidiano.", description: "Usa audio, foto o video per registrare problemi, creare attività o analizzare lo stato delle colture. L'assistente rileva parassiti, genera raccomandazioni basate su clima e dati di campo, assegna il lavoro al team e ti aiuta a prendere decisioni più rapide." },
        farm: { title: "La tua azienda agricola, strutturata nel modo giusto.", description: "Organizza sedi, serre, blocchi e settori seguendo la struttura reale della tua azienda. Tutto il resto — trattamenti, costi, cicli — parte da qui." },
        prevention: { title: "Prevenzione intelligente dei parassiti.", description: "Anticipa i problemi prima che si manifestino. Analizza condizioni di coltivazione, clima e pressione dei parassiti per individuare rischi precoci. Ricevi avvisi preventivi e raccomandazioni chiare per agire in tempo e proteggere la produzione." },
        spraylogs: { title: "Registri dei trattamenti, pronti quando servono.", description: "Crea registri in pochi secondi, filtra per qualsiasi criterio ed esporta PDF chiari e pronti per le ispezioni. Documentazione affidabile per audit e gestione quotidiana." },
        treatments: { title: "Controlla ciò che viene applicato, dove conta.", description: "Monitora trattamenti, presenza di parassiti e salute delle colture per blocco e ciclo. Tracciabilità completa delle applicazioni biologiche e chimiche." },
        stock: { title: "Un magazzino che si aggiorna da solo.", description: "Importa le scorte in pochi secondi e aggiornale automaticamente quando i trattamenti vengono completati. Imposta soglie minime e ricevi avvisi prima di rimanere senza." },
        tasks: { title: "Trasforma i piani in azioni.", description: "Assegna attività a operatori e aree, monitora l'esecuzione e resta aggiornato con promemoria SMS. Ogni lavoratore vede solo ciò che serve, quando serve." },
        cycles: { title: "Pianifica i cicli con chiarezza.", description: "Crea cicli personalizzati, applicali a più blocchi e gestiscili con semplicità. Confronta costi e performance per ciclo, stagione dopo stagione." }
      },
      video: {
        title: "Guardalo in",
        titleHighlight: "azione",
        subtitle: "Guarda come i gestori di serre usano la nostra piattaforma ogni giorno.",
        stat1Label: "Guadagno di efficienza",
        stat2Label: "Report più veloci",
        stat3Label: "Accesso ai dati"
      }
    }
  },
  
  fr: {
    nav: {
      homepage: "Accueil",
      aboutUs: "À Propos",
      software: "Our Apps",
      personalize: "Solutions sur mesure",
      caseStudies: "Études de Cas",
      blog: "Blog",
      contactUs: "Contact",
      bookingCall: "Réserver un Appel"
    },
    personalize: {
      title: "Créez votre propre",
      subtitle: "logiciel",
      swipeHint: "Glissez pour explorer →",
      dashboardPreview: "Aperçu du tableau de bord",
      whatWeBuildTitle: "Ce que nous construisons",
      whatWeBuildSubtitle: "Des logiciels sur mesure, conçus, développés et déjà utilisés dans des opérations réelles.",
      bridgeSection: {
        title: "Ni un modèle. Ni une plateforme générique.",
        subtitle: "Conçu autour de vos processus. Pensé pour l'usage quotidien, pas pour les démonstrations."
      },
      softwareCard: {
        title: "Logiciel",
        description: "Nous avons développé un logiciel sur mesure pour gérer la maintenance des machines, les emplacements, les pannes et les interventions planifiées dans un seul système. Chaque équipement disposait de son historique, avec des tâches générées automatiquement et une vue claire des priorités."
      },
      impactCard: {
        title: "Résultats",
        description: "L'entreprise a réduit les arrêts imprévus, amélioré les temps de réaction et obtenu une vision claire des coûts et des problèmes récurrents. La maintenance est passée d'un mode réactif à un processus structuré et maîtrisé."
      },
      newCard1: {
        title: "Votre Vision",
        description: "Vous comprenez votre entreprise mieux que quiconque.\nVous savez ce qui doit s'améliorer, ce qui ralentit les choses et où les outils actuels échouent.\n\nVotre vision ne consiste pas à ajouter du logiciel — il s'agit de rendre le travail quotidien plus clair, simple et facile à gérer.\n\nC'est là que nous commençons.",
        keyPoints: ["Vos processus", "Vos contraintes", "Vos priorités"]
      },
      newCard2: {
        title: "Notre Solution",
        description: "Nous transformons cette vision en logiciel construit autour du fonctionnement réel de votre entreprise.\n\nPas de modèles. Pas de plateformes génériques.\nJuste des outils pratiques conçus pour l'usage quotidien, façonnés par la collaboration et les retours réels.",
        keyPoints: ["Construit autour d'opérations réelles", "Conçu pour l'usage quotidien", "Évolue avec votre entreprise"]
      },
      buttons: {
        startBuilding: "Commencez à créer votre solution",
        viewCaseStudies: "Découvrez ce que nous avons construit"
      },
      mobileButtons: {
        startBuilding: "Commencer",
        viewCaseStudies: "Nos réalisations"
      },
      workProcess: {
        sectionTitle: "Comment nous travaillons",
        sectionTitleHighlight: "avec vous",
        sectionSubtitle: "De l'idée à un logiciel prêt pour la production, avec un processus clair et transparent. Nous travaillons à vos côtés, de manière simple, structurée et orientée vers des résultats concrets.",
        steps: {
          step1: {
            week: "Semaine 1",
            title: "Comprendre votre",
            highlight: "activité",
            description: "Nous commençons par comprendre comment fonctionne réellement votre entreprise. Cette phase consiste à écouter, cartographier les processus et définir ce que le logiciel doit faire — et ce qu'il ne doit pas faire.",
            features: ["Sessions d'analyse avec votre équipe", "Revue des flux de travail et outils existants", "Définition des objectifs, du périmètre et des priorités"]
          },
          step2: {
            week: "Semaines 2-4",
            title: "Développement et",
            highlight: "ajustements",
            description: "Grâce à notre plateforme interne, nous développons rapidement et itérons à partir de retours réels. Vous suivez l'avancement chaque semaine et ajustez si nécessaire.",
            features: ["Développement rapide avec nos outils propriétaires", "Points hebdomadaires et démonstrations en direct", "Tests et améliorations continues"]
          },
          step3: {
            week: "Semaine 5",
            title: "Lancement et",
            highlight: "mise en service",
            description: "Une fois le logiciel prêt, nous préparons son utilisation en conditions réelles. Votre équipe est formée, les données sont configurées et le système est lancé avec notre accompagnement.",
            features: ["Formation et onboarding de l'équipe", "Configuration ou migration des données", "Mise en production avec suivi rapproché"]
          },
          step4: {
            week: "En continu",
            title: "Support et amélioration",
            highlight: "continue",
            description: "Le lancement n'est pas une fin. Nous restons impliqués pour accompagner l'évolution de votre activité.",
            features: ["Améliorations et ajustements continus", "Nouvelles fonctionnalités selon les besoins", "Support technique fiable à tout moment"]
          }
        },
        cta: {
          title: "Prêt à",
          titleHighlight: "Commencer",
          subtitle: "Réservez votre appel gratuit et commencez à transformer votre entreprise.",
          buttonText: "Réserver un Appel Gratuit"
        }
      }
    },
    hero: {
      badge: "Plateforme d'Agriculture Numérique",
      title: "Occupez-vous du Terrain.\nNous nous occupons du Reste.",
      subtitle: "Des commandes et factures au contrôle de production, nous vous aidons à organiser et automatiser votre entreprise agricole.",
      heroSubtitle: "Logiciels, solutions sur mesure et conseil pour apporter clarté et contrôle aux opérations agricoles.",
      cta: "Découvrez nos solutions",
      ctaSecondary: "Réserver une démo gratuite"
    },
    whatIsUlture: {
      badge: "Qu'est-ce qu'Ulture ?",
      title: "Fatigués de ne pas avoir le contrôle",
      titleHighlight: "de tout ce qui se passe",
      subtitle: "Perdre du temps, de l'argent et du travail parce que rien n'est en un seul endroit. C'est ce que nous résolvons.",
      painPoints: {
        time: { title: "Temps perdu", description: "Naviguer entre des feuilles de calcul, des papiers et des applications qui ne communiquent pas." },
        money: { title: "Argent évaporé", description: "Sans visibilité réelle sur les coûts, les marges et où la rentabilité s'échappe." },
        work: { title: "Travail dupliqué", description: "Saisir les mêmes données encore et encore parce que rien n'est connecté." }
      },
      cta: "Découvrez comment nous le résolvons"
    },
    metrics: {
      problemsSolved: "Problèmes Résolus",
      savedPerYear: "Économisés par An",
      efficiency: "Efficacité Opérationnelle"
    },
    categories: {
      title: "Tout ce dont vous avez besoin pour",
      titleHighlight: "Développer Votre Entreprise",
      subtitle: "Six domaines clés où Ulture vous aide à gagner du temps, réduire les coûts et augmenter l'efficacité.",
      administration: { title: "Administration et Comptabilité", description: "Automatisez factures, contrats et suivi financier avec des flux intelligents." },
      workers: { title: "Travailleurs et Tâches", description: "Gérez les équipes, assignez des tâches et suivez la productivité en temps réel." },
      production: { title: "Production et Stock", description: "Surveillez les niveaux de stock, les cycles de production et la logistique." },
      costs: { title: "Coûts et Profits", description: "Obtenez des informations sur les dépenses, marges et analyses de rentabilité." },
      clients: { title: "Clients et Commandes", description: "Optimisez la gestion des commandes et maintenez de solides relations clients." },
      marketing: { title: "Marketing et Image", description: "Construisez votre présence de marque et atteignez de nouveaux marchés efficacement." }
    },
    footer: {
      copyright: "© 2026 Ulture",
      tagline: "Conçu avec soin pour celles et ceux qui nourrissent le monde.",
      product: "Produit",
      company: "Entreprise",
      legal: "Légal",
      privacy: "Politique de Confidentialité",
      terms: "Conditions d'Utilisation",
      cookiePolicy: "Politique des Cookies",
      dpa: "DPA"
    },
    whyWeExist: {
      badge: "Notre Histoire",
      title: "Créé par des agriculteurs",
      titleHighlightBlue: "pour des agriculteurs",
      subtitle: "Un chemin de découverte vers la transformation numérique agricole",
      steps: {
        challenge: { number: "01", title: "Le Défi", text: "Les agriculteurs sont des experts comme personne d'autre dans la production de fruits et légumes, mais pas en comptabilité, gestion de données ou marketing. Nous avons réalisé que leurs champs prospéraient, mais leurs systèmes de gestion étaient obsolètes." },
        revelation: { number: "02", title: "La Révélation", text: "Nous avons découvert que 70% du temps administratif est perdu à naviguer entre des outils incompatibles et à dupliquer des données." },
        solution: { number: "03", title: "La Solution", text: "Nous avons créé un écosystème unifié où chaque outil communique parfaitement, éliminant les frictions et automatisant les processus." },
        result: { number: "04", title: "Le Résultat", text: "Des agriculteurs qui récupèrent des heures de leur journée, réduisent les erreurs et prennent des décisions basées sur des données réelles en temps réel." }
      },
      mission: {
        badge: "Notre Mission",
        title: "Accélérer la Transformation Numérique",
        titleHighlight: "de l'Agriculture",
        subtitle: "Construire une technologie qui comprend les vrais besoins du terrain"
      },
      pillars: {
        automation: { label: "Automatisation", description: "Processus sans friction" },
        integration: { label: "Intégration", description: "Tout connecté" },
        intelligence: { label: "Intelligence", description: "IA adaptative" },
        efficiency: { label: "Efficacité", description: "Résultats réels" }
      }
    },
    macbook: {
      title: "Logiciel Puissant. Interface Simple.",
      subtitle: "Gérez toute votre exploitation agricole depuis une plateforme"
    },
    about: {
      badge: "À Propos d'Ulture / Ulture",
      valueLabel: "Valeur",
      hero: {
        title: "Redéfinir le Fonctionnement de l'Agriculture",
        subtitle: "Nous sommes une équipe passionnée par la technologie et l'agriculture, travaillant à numériser et optimiser les entreprises agricoles du monde entier."
      },
      story: {
        title: "Notre Histoire",
        paragraph1: "Ulture est né d'une compréhension profonde des défis auxquels les agriculteurs font face chaque jour. Nos fondateurs, enracinés à la fois dans l'agriculture et la technologie, ont vu une opportunité de combler le fossé entre l'agriculture traditionnelle et les solutions numériques modernes.",
        paragraph2: "Depuis 2020, nous développons des outils qui simplifient la complexité de la gestion agricole. Des petites exploitations familiales aux grandes entreprises agricoles, notre plateforme s'adapte aux besoins uniques de chaque entreprise.",
        paragraph3: "Aujourd'hui, nous aidons des centaines d'agriculteurs en Europe et en Amérique latine à gérer leurs opérations plus efficacement, réduire les coûts et augmenter leur rentabilité.",
        years: "Années d'Expérience"
      },
      mvv: {
        title: "Mission, Vision et Valeurs",
        subtitle: "Les principes qui guident tout ce que nous faisons"
      },
      mission: {
        title: "Notre Mission",
        description: "Donner aux agriculteurs et aux entreprises agricoles des outils numériques intuitifs qui simplifient la gestion, augmentent l'efficacité et favorisent une croissance durable."
      },
      vision: {
        title: "Notre Vision",
        description: "Devenir la plateforme de référence mondiale pour la numérisation agricole, où chaque agriculteur a accès à une technologie autrefois réservée aux grandes entreprises."
      },
      values: {
        innovation: { title: "Innovation", description: "Nous cherchons constamment de nouvelles façons de résoudre d'anciens problèmes, en utilisant des technologies de pointe pour créer des solutions pratiques." },
        commitment: { title: "Engagement", description: "Nous sommes profondément engagés dans la réussite de nos clients. Leur croissance est notre croissance, et nous les accompagnons à chaque étape." },
        collaboration: { title: "Collaboration", description: "Nous croyons au pouvoir du travail d'équipe. Nous collaborons avec des agriculteurs, des agronomes et des experts du secteur pour construire de meilleures solutions." }
      },
      team: {
        title: "Rencontrez notre équipe fondatrice",
        subtitle: "Les personnes derrière Ulture : enracinées dans les champs, passionnées de technologie.",
        headline: "L'équipe",
        description: "Nous sommes une équipe passionnée par la technologie et l'efficacité, travaillant pour aider les entreprises du monde entier à fonctionner plus intelligemment.",
        ceo: "Growth & Strategy",
        cto: "CTO et Co-Fondatrice",
        coo: "Directeur des Opérations",
        head_product: "Responsable Produit",
        roles: {
          ceoCofounder: "Growth & Strategy",
          ctoCofounder: "CTO et Co-Fondateur",
          headProduct: "Responsable Produit",
          headDevelopment: "Responsable Développement"
        }
      },
      timeline: {
        title: "Du champ, pour le champ",
        subtitle: "Étapes clés de notre histoire",
        welcomeText: "C'est ",
        flipWords: ["Ulture", "Notre histoire"],
        milestone1: { date: "1974", title: "Les racines de l'exploitation familiale", description: "Notre histoire commence avec l'agriculture. En 1974, nos grands-parents ont construit l'une des premières serres à Almeria, en Espagne. Un travail manuel, fondé sur l'expérience, l'effort et le lien avec la terre." },
        milestone2: { date: "2020", title: "La croissance apporte de nouveaux défis", description: "Avec le temps, l'exploitation familiale s'est développée. Les serres modernes ont amélioré la production, mais la gestion des opérations, de l'administration et des coûts est devenue plus complexe." },
        milestone3: { date: "2024", title: "Le problème devient évident", description: "Le travail quotidien au sein de l'exploitation a mis en évidence un décalage : la technologie évoluait, mais la gestion opérationnelle restait en retard. Les informations étaient dispersées et les processus trop manuels." },
        milestone4: { date: "Mi-2025", title: "Conçu pour un usage réel", description: "La première version du logiciel est développée et utilisée directement dans les serres familiales, comme un outil concret pour résoudre des problèmes quotidiens." },
        milestone5: { date: "Février 2026", title: "Lancement d'Ulture", description: "Après plusieurs mois d'utilisation réelle et d'améliorations, Ulture est officiellement lancé, avec une priorité donnée à la clarté et à la simplicité." },
        milestone6: { date: "Avril 2026", title: "Ulture Pest", description: "Ulture Pest devient la première solution, dédiée au suivi des ravageurs, aux traitements et aux registres phytosanitaires." },
        milestone7: { date: "2026", title: "La suite", description: "Ulture continue d'évoluer. De nouvelles solutions sont en préparation, guidées par les besoins réels du terrain." },
      },
      cta: {
        title: "Prêt à Transformer Votre Exploitation ?",
        description: "Rejoignez des centaines d'agriculteurs qui économisent déjà du temps et de l'argent avec Ulture.",
        contact: "Contactez-nous",
        demo: "Demander une Démo"
      }
    },
    aboutStory: {
      sectionTitle: "Notre Histoire",
      sectionSubtitle: "Notre histoire, construite depuis le terrain",
      intro: {
        title: "Nous étions fatigués de voir des inefficacités dans l'agriculture",
        subtitle: "alors un jour nous avons décidé de changer les choses."
      },
      challenge: {
        title: "Le Défi",
        text: "Les producteurs sont des experts inégalés dans la culture de fruits et légumes — mais pas en comptabilité, logistique ou gestion de données. Nous avons réalisé que pendant que leurs champs prospéraient, leurs systèmes de gestion étaient obsolètes."
      },
      realization: {
        title: "La Prise de Conscience",
        text: "La plupart des entreprises de logiciels construisaient des outils depuis leurs bureaux, pas depuis les champs. Aucune d'entre elles ne comprenait vraiment ce dont les producteurs avaient besoin. Après des années à travailler aux côtés des agriculteurs, nous avons décidé de concevoir des solutions basées sur l'expérience réelle, pas la théorie."
      },
      solution: {
        title: "La Solution",
        text: "Nous avons construit une plateforme qui automatise les opérations, centralise les informations et transforme la gestion agricole en un système connecté, numérique et centré sur l'humain."
      },
      transformation: {
        title: "La Transformation",
        text: "Quand les producteurs se concentrent sur la culture et que nous nous occupons du reste — technologie, données, coordination — les résultats sont extraordinaires. De meilleurs fruits, une meilleure efficacité, de meilleures vies pour ceux qui nourrissent le monde."
      },
      mission: {
        title: "Notre Mission",
        subtitle: "Accélérer la transformation numérique de l'agriculture",
        pillars: {
          automation: { title: "Automatisation", text: "Rendre chaque processus plus efficace — de la production aux ventes" },
          integration: { title: "Intégration", text: "Connecter toutes les parties de votre exploitation en un seul endroit" },
          dataIntelligence: { title: "Intelligence des Données", text: "Transformer les informations en décisions stratégiques" }
        }
      }
    },
    blog: {
      badge: "Dernières actualités",
      title: "Blog et ressources",
      subtitle: "Découvrez les dernières actualités, conseils et histoires sur l'agriculture numérique et la gestion agricole.",
      heroTitle: "Toute l'actualité agricole",
      searchPlaceholder: "Rechercher des articles...",
      postTitle: "Comment la technologie transforme l'agriculture",
      postExcerpt: "Explorez comment les outils numériques modernes aident les agriculteurs à augmenter l'efficacité et réduire les coûts.",
      readMore: "Lire la suite",
      comingSoonTitle: "Plus de contenu à venir !",
      comingSoonText: "Nous travaillons pour vous apporter plus d'informations et de ressources précieuses."
    },
    contact: {
      badge: "Contactez-nous",
      title: "Contact",
      subtitle: "Vous avez des questions sur notre plateforme ? Nous serions ravis d'avoir de vos nouvelles.",
      emailLabel: "Email",
      phoneLabel: "Téléphone",
      locationLabel: "Adresse",
      infoTitle: "Parlons",
      infoSubtitle: "Que vous ayez des questions sur les fonctionnalités, les prix ou autre chose, notre équipe est prête à répondre.",
      responseTime: "Nous répondons généralement sous 24 heures",
      successTitle: "Message envoyé !",
      successMessage: "Nous vous répondrons bientôt.",
      thankYouTitle: "Merci !",
      thankYouMessage: "Votre message a été envoyé avec succès. Nous vous répondrons bientôt.",
      sendAnother: "Envoyer un autre message",
      nameLabel: "Nom",
      namePlaceholder: "Votre nom",
      emailFormLabel: "Email",
      emailPlaceholder: "votre@email.com",
      companyLabel: "Entreprise (optionnel)",
      companyPlaceholder: "Votre entreprise",
      messageLabel: "Message",
      messagePlaceholder: "Dites-nous comment nous pouvons vous aider...",
      sending: "Envoi en cours...",
      submitButton: "Envoyer le Message"
    },
    software: {
      badge: "Notre Plateforme",
      title: "Logiciel Puissant pour l'Agriculture Moderne",
      subtitle: "Une plateforme pour gérer toute votre exploitation agricole — du champ aux finances.",
      cta: "Commencer",
      ctaSecondary: "Demander une Démo",
      screenshotAlt: "Tableau de bord Ulture",
      featuresTitle: "Tout ce dont vous avez besoin pour gérer votre exploitation",
      featuresSubtitle: "Notre suite complète d'outils couvre tous les aspects de la gestion agricole.",
      features: {
        administration: { title: "Administration et Comptabilité", description: "Automatisez factures, contrats et suivi financier avec des flux intelligents." },
        workers: { title: "Travailleurs et Tâches", description: "Gérez les équipes, assignez des tâches et suivez la productivité en temps réel." },
        production: { title: "Production et Stock", description: "Surveillez les niveaux de stock, les cycles de production et la logistique." },
        costs: { title: "Coûts et Profits", description: "Obtenez des informations sur les dépenses, marges et analyses de rentabilité." },
        analytics: { title: "Tableau de Bord Analytique", description: "Informations en temps réel et rapports personnalisés pour des décisions basées sur les données." },
        automation: { title: "Automatisation Intelligente", description: "Flux de travail avec IA qui font gagner du temps et réduisent les erreurs manuelles." }
      },
      benefitsTitle: "Pourquoi choisir Ulture ?",
      benefitsSubtitle: "",
      benefits: {
        item1: "Économisez plus de 10 heures par semaine sur les tâches administratives",
        item3: "Visibilité en temps réel sur toutes les opérations",
        item4: "Intégration parfaite avec les outils existants",
        item5: "Support et assistance constants de notre équipe",
        item6: "Mises à jour régulières avec de nouvelles fonctionnalités"
      },
      stats: {
        farms: "Exploitations utilisant Ulture",
        savings: "Économies moyennes sur les coûts",
        time: "Temps hebdomadaire économisé",
        satisfaction: "Satisfaction client"
      },
      connectivity: {
        badge: "Réseau Mondial",
        title: "Connectivité mondiale",
        description: "Gérez votre exploitation agricole depuis n'importe où dans le monde.",
        heroTitle: "Connectez-vous à votre agriculture depuis n'importe où dans le monde",
        heroSubtitle: "Une plateforme mondiale pour gérer, analyser et prendre des décisions agricoles sans frontières.",
        stats: {
          countries: "Pays",
          farms: "Exploitations",
          availability: "Disponibilité",
          uptime: "Temps de fonctionnement"
        }
      },
      ctaTitle: "Prêt à transformer votre exploitation ?",
      ctaSubtitle: "Commencez votre essai gratuit aujourd'hui et découvrez comment Ulture peut vous aider à économiser du temps et de l'argent.",
      ctaButton: "Démarrer l'Essai Gratuit",
      ctaButtonSecondary: "Parler aux Ventes"
    },
    heroInfrastructure: {
      title: "Connectez-vous de n'importe où",
      subtitle: "Une infrastructure numérique vivante qui connecte les champs, les équipes et les données en temps réel.",
      tag1: "Données en temps réel",
      tag2: "Décisions basées sur l'IA",
      tag3: "Échelle mondiale",
      tag4: "Automatisation d'abord"
    },
    operations: {
      title: "Une plateforme. Contrôle opérationnel total.",
      subtitle: "Gérez tous les aspects de votre exploitation agricole depuis un seul système connecté",
      footer: "Une plateforme. Une seule source de vérité. Contrôle opérationnel total.",
      learnMore: "Explorer les Opérations",
      intro: {
        title: "Une Plateforme, Contrôle Opérationnel Complet",
        text: "Gérer une exploitation agricole ne devrait pas nécessiter des dizaines d'outils différents. Notre plateforme centralise tout ce dont vous avez besoin dans un seul système intégré, éliminant la complexité et multipliant votre efficacité. De la planification au contrôle des coûts, chaque module travaille ensemble pour vous donner une visibilité complète et un contrôle total de votre exploitation. Plus de données fragmentées, de feuilles de calcul désordonnées ou d'informations perdues entre les systèmes.",
        bulletsTitle: "Avec notre plateforme unifiée vous obtenez:",
        bullets: {
          visibility: { title: "Visibilité totale", desc: "Toutes vos données opérationnelles en un seul endroit, mises à jour en temps réel" },
          decisions: { title: "Décisions plus intelligentes", desc: "Outils de prévision et d'analyse qui prédisent les tendances et optimisent les ressources" },
          savings: { title: "Économies automatiques", desc: "Contrôle des coûts intégré qui identifie les opportunités d'optimisation sans effort manuel" },
          duplication: { title: "Zéro duplication", desc: "Entrez les informations une fois et elles se synchronisent automatiquement dans tous les modules" },
          scalability: { title: "Scalabilité garantie", desc: "Passez de 10 hectares à 1 000 sans changer de système" }
        }
      },
      tabs: {
        mission: {
          title: "Notre Mission",
          heading: "Notre Mission",
          description: "Donner aux entreprises des outils numériques intuitifs qui simplifient les opérations, augmentent l'efficacité et stimulent une croissance durable."
        },
        vision: {
          title: "Notre Vision",
          heading: "Notre Vision",
          description: "Devenir la plateforme de référence pour les opérations commerciales, où chaque équipe a accès à des outils de niveau entreprise."
        },
        innovation: {
          title: "Innovation",
          heading: "Innovation",
          description: "Nous cherchons constamment de nouvelles façons de résoudre des problèmes complexes, en exploitant une technologie de pointe pour créer des solutions pratiques."
        },
        commitment: {
          title: "Engagement",
          heading: "Engagement",
          description: "Nous sommes profondément engagés dans le succès de nos clients. Leur croissance est notre croissance, et nous les accompagnons à chaque étape."
        },
        collaboration: {
          title: "Collaboration",
          heading: "Collaboration",
          description: "Nous croyons au pouvoir de travailler ensemble. Nous nous associons avec des équipes, des développeurs et des experts de l'industrie pour construire de meilleures solutions."
        }
      }
    },
    blogPosts: {
      post1: {
        title: "Comment l'IA Révolutionne l'Agriculture Moderne",
        subtitle: "Technologie intelligente pour une agriculture durable",
        category: "IA et Technologie",
        excerpt: "Du suivi des cultures en temps réel à l'analyse prédictive, l'IA transforme notre façon de cultiver et de gérer les opérations agricoles.",
        readTime: "6 min de lecture"
      },
      post2: {
        title: "Prendre des Décisions Agricoles Basées sur les Données",
        subtitle: "Transformez l'information en action",
        category: "Gestion des Données",
        excerpt: "Découvrez comment les systèmes de données centralisés aident les producteurs à prendre de meilleures décisions, réduire les coûts et améliorer la rentabilité.",
        readTime: "5 min de lecture"
      },
      post3: {
        title: "Agriculture Durable Grâce à la Technologie",
        subtitle: "Meilleur pour la planète, meilleur pour les affaires",
        category: "Durabilité",
        excerpt: "Découvrez comment la technologie intelligente permet des pratiques durables qui profitent à la fois à l'environnement et à votre résultat net.",
        readTime: "7 min de lecture"
      },
      post4: {
        title: "Automatisation des Opérations Agricoles",
        subtitle: "Travaillez plus intelligemment, pas plus dur",
        category: "Automatisation",
        excerpt: "Explorez comment l'automatisation réduit le travail manuel, élimine les erreurs et permet aux équipes de se concentrer sur les activités stratégiques.",
        readTime: "6 min de lecture"
      },
      post5: {
        title: "Prévision de Production en Toute Confiance",
        subtitle: "Planifiez avec des données, pas des suppositions",
        category: "Planification et Prévisions",
        excerpt: "Découvrez comment les modèles prédictifs aident les producteurs à anticiper la production, gérer les stocks et respecter les engagements clients.",
        readTime: "7 min de lecture"
      },
      post6: {
        title: "Contrôle des Coûts et Rentabilité en Agriculture",
        subtitle: "Connaissez vos chiffres, améliorez vos marges",
        category: "Gestion Financière",
        excerpt: "Comprenez comment l'allocation et l'analyse correctes des coûts aident à identifier les inefficacités et augmenter la rentabilité.",
        readTime: "8 min de lecture"
      }
    },
    blogPage: {
      notFound: "Article introuvable",
      backToBlog: "Retour au blog",
      quickInsight: "En bref",
      readNext: "Article suivant",
      readArticle: "Lire l'article",
      allRights: "Tous droits réservés."
    },
    buttons: {
      startNow: "Commencer",
      learnMore: "Explorer les Solutions",
      getStarted: "Démarrer",
      bookDemo: "Réserver Démo",
      contactUs: "Nous Contacter",
      viewMore: "Voir Plus"
    },
    wobbleCards: {
      card1: {
        title: "Contrôle Numérique Complet",
        description: "Gérez l'ensemble de votre exploitation agricole depuis une seule plateforme. Des commandes et factures au suivi de production et analyse des coûts.",
        imageAlt: "Tableau de bord de la plateforme Ulture"
      },
      card2: {
        title: "Automatisation Intelligente",
        description: "Réduisez le travail manuel et éliminez les erreurs. Nos workflows intelligents s'adaptent à votre exploitation."
      },
      card3: {
        title: "Connectivité Mondiale",
        description: "Accédez à vos données de n'importe où. Synchronisation en temps réel sur tous vos appareils et équipes.",
        imageAlt: "Tableau de bord de la plateforme Ulture"
      }
    },
    ourStory: {
      mainTitle: "Nous étions fatigués de voir des inefficacités dans l'agriculture, alors un jour nous avons décidé de changer les choses.",
      challenge: {
        badge: "Le Défi",
        title: "Le Défi",
        description: "Les producteurs sont des experts comme personne d'autre dans la production de fruits et légumes — mais pas en comptabilité, logistique ou gestion des données. Nous avons réalisé que si leurs champs prospéraient, leurs systèmes de gestion étaient obsolètes."
      },
      realization: {
        badge: "La Prise de Conscience",
        title: "La Prise de Conscience",
        description: "La plupart des entreprises de logiciels construisaient des outils depuis des bureaux, pas depuis les champs. Aucune d'entre elles ne comprenait vraiment ce dont les producteurs avaient besoin. Après des années de travail aux côtés des agriculteurs, nous avons décidé de concevoir des solutions basées sur une expérience réelle, pas sur la théorie."
      },
      solution: {
        badge: "La Solution",
        title: "La Solution",
        description: "Nous avons construit une plateforme qui automatise les opérations, centralise les informations et transforme la gestion agricole en un système connecté, numérique et centré sur l'humain."
      },
      stats: {
        betterFruits: { title: "Meilleurs Fruits", subtitle: "Une qualité mesurable" },
        betterEfficiency: { title: "Meilleure Efficacité", subtitle: "Économies de temps et de coûts" },
        betterLives: { title: "Meilleures Vies", subtitle: "Pour ceux qui nourrissent le monde" }
      }
    },
    techSection: {
      eyebrow: "POUR L'AGRICULTURE RÉELLE ET LES OPÉRATIONS DIFFICILES À DIGITALISER",
      headlinePart1: "Nous construisons notre propre technologie pour ",
      headlineHighlight: "construire votre logiciel.",
      subheadlineHighlight: "Nous ne sommes pas un SaaS générique.",
      subheadline: "Nous concevons et développons des solutions logicielles sur mesure adaptées aux besoins réels de chaque exploitation agricole — des petits producteurs aux entreprises complexes et multicanales.",
      block1: {
        title: "Nous construisons notre propre technologie",
        description: "Nous avons développé notre propre plateforme interne et outils pour concevoir, déployer et faire évoluer les logiciels efficacement.",
        bullets: [
          "Avancer vite sans sacrifier la fiabilité",
          "Adapter les solutions à des cas d'usage très spécifiques",
          "Construire des logiciels qui s'adaptent à la façon dont les gens travaillent réellement sur le terrain"
        ]
      },
      block2: {
        title: "Logiciel adapté à chaque exploitation",
        description: "Chaque ferme, entreprise et exploitation est différente. C'est pourquoi nous ne vendons pas de logiciel universel.",
        bullets: [
          "La taille de l'exploitation",
          "Le type de cultures ou produits",
          "Les flux de travail et outils existants",
          "Le niveau de maturité numérique de l'équipe"
        ]
      },
      block3: {
        title: "Pas limité à une industrie",
        description: "Bien que nos racines soient dans l'agriculture, notre technologie nous permet de construire des logiciels personnalisés pour d'autres industries avec des opérations complexes.",
        bullets: [
          "Complexité opérationnelle",
          "Processus manuels",
          "Données déconnectées",
          "Besoin de contrôle et d'automatisation"
        ]
      },
      supportingSentence: "Nous ne demandons pas aux opérations de s'adapter au logiciel. Nous construisons des logiciels qui s'adaptent aux opérations.",
      cta: "Parlez-nous de votre exploitation"
    },
    magicBento: {
      title: "Fonctionnalités",
      titleHighlight: "Clés",
      subtitle: "Tout ce dont vous avez besoin pour gérer votre entreprise efficacement.",
      cta: "En savoir plus",
      documents: { 
        title: "Registres et Rapports", 
        description: "Enregistrez toutes les activités et générez des rapports en quelques secondes. Filtrez par parcelle, culture ou date pour trouver l'information.",
        reportFiles: [
          { name: "Utilisation Traitements Bloc 1", date: "Mai 2026", type: "pdf" },
          { name: "Rapport Coûts Inventaire", date: "Avril 2026", type: "xlsx" },
          { name: "Résumé Récolte T1", date: "2026", type: "pdf" },
          { name: "Journal Activités Parcelle", date: "Juin 2026", type: "docx" },
          { name: "Rapport Application Pesticides", date: "Mai 2026", type: "pdf" }
        ]
      },
      notifications: { 
        title: "Vue d'ensemble claire", 
        description: "Visualisez ce qui se passe entre parcelles et activités pour prendre de meilleures décisions.",
        notificationItems: [
          { text: "Traitement Bloc 1 exécuté ✓", icon: "✓", color: "#00C9A7" },
          { text: "+50kg achetés: Produit A", icon: "📦", color: "#1E86FF" },
          { text: "Niveau de stock: Bon", icon: "📊", color: "#00C9A7" },
          { text: "Rapport mensuel automatique généré", icon: "📄", color: "#FFB800" }
        ]
      },
      integrations: { title: "Intégrations", description: "Compatible avec plus de 100 intégrations." },
      calendar: { title: "Calendrier", description: "Utilisez le calendrier pour organiser vos tâches." }
    },
    aiWorkflowBento: {
      title: "conçue autour de vos opérations",
      titleHighlight: "Technologie",
      titlePart1: "Conçue Autour de",
      titlePart2: "Vos Opérations",
      subtitle: "",
      cta: "En savoir plus",
      collaboration: {
        title: "Digitalisation Opérationnelle",
        description: "Nous structurons et numérisons les opérations agricoles quotidiennes pour un meilleur contrôle et une meilleure efficacité.",
        menuItems: {
          planTask: "Planifier Tâche",
          assignTask: "Assigner Tâche",
          manageTeam: "Gérer l'Équipe",
          analytics: "Analytiques"
        }
      },
      integrations: {
        title: "Opérations Connectées",
        description: "Nous connectons les outils, les données et les équipes afin que l'information circule clairement et que rien ne se perde entre les systèmes."
      },
      insights: {
        title: "Clarté et Contrôle",
        description: "Obtenez une vision claire et en temps réel des opérations, des ressources et des performances, afin de prendre de meilleures décisions en toute confiance.",
        metric: "Kg Récoltés"
      },
      automation: {
        title: "Planification Intelligente et Alertes",
        description: "Anticipez et recevez des rappels et des alertes automatiques dès qu'une action est nécessaire, afin que rien ne vous échappe.",
        notifications: {
          meeting: "Rappel de réunion",
          treatment: "Rappel de traitement : Bloc 1",
          lowStock: "Alerte : Niveaux de stock bas"
        }
      }
    },
    scrollVelocity: {
      line1: "Ulture Software",
      line2: "Contrôle",
      line3: "Analyse",
      line4: "Automatisation",
      line5: "Efficacité",
      line6: "Intégration",
      line7: "Innovation",
      line8: "Croissance"
    },
    featuresPreview: {
      titleLine1: "Développez votre entreprise.\nAméliorez la visibilité.\nGardez le contrôle.",
      titleLine2: "Nous vous simplifions la vie.",
      subtitle: "À mesure que les équipes, les parcelles et les activités augmentent, l'information devient plus difficile à suivre et le travail quotidien plus complexe à gérer.",
      exploreSolutions: "Découvrez nos solutions",
      viewAll: "Voir Toutes les Fonctionnalités",
      highlights: {
        monitoring: "Journaux de pulvérisation en PDF. Instantanément.",
        alerts: "SMS automatique aux travailleurs pour les tâches et alertes.",
        analytics: "Chaque tâche liée à une parcelle."
      },
      seeItTitle: "Voyez-le en",
      seeItHighlight: "action",
      dashboardDescription: "Un tableau de bord unifié qui vous donne une visibilité complète sur vos opérations en serre.",
      stats: {
        pesticide: "Moins d'utilisation de pesticides",
        reporting: "Rapports plus rapides",
        monitoring: "Surveillance"
      },
      joinText: "Rejoignez des centaines d'opérateurs de serres qui ont transformé leur gestion des parasites avec notre plateforme. Des petites exploitations familiales aux grandes opérations commerciales, notre plateforme s'adapte à vos besoins.",
      features: {
        control: { title: "Contrôle Total, Chaque Jour", description: "Gérez vos serres, suivez chaque parasite et enregistrez tous les traitements depuis une plateforme unifiée. Sachez exactement ce qui se passe dans toute votre exploitation." },
        costs: { title: "Sachez Où Va Votre Argent", description: "Suivez chaque dépense, voyez votre coût par hectare et comprenez où va votre argent. Plus de devinettes—juste des chiffres clairs qui vous aident à prendre de meilleures décisions." },
        oversight: { title: "Toujours Une Longueur d'Avance", description: "Des alertes automatisées, une surveillance des seuils et des rappels intelligents garantissent que vous ne manquerez jamais un traitement critique ou une échéance. Restez en avance sur les problèmes avant qu'ils ne grandissent." }
      },
      cards: {
        card1: { title: "Coûts et Marges Difficiles à Suivre", description: "Sans données fiables issues des opérations quotidiennes, il devient difficile de comprendre les coûts réels et la rentabilité." },
        card2: { title: "Trop d'Outils, Aucune Vue d'Ensemble", description: "Les informations sont dispersées entre Excel, messages, notes et logiciels qui ne communiquent pas entre eux." },
        card3: { title: "Les Problèmes Sont Détectés Trop Tard", description: "Les oublis, ruptures ou erreurs sont souvent découverts après avoir déjà impacté les opérations." },
        card4: { title: "Registres et Rapports Peu Fiables", description: "Les registres et rapports prennent du temps à être préparés et sont souvent incomplets ou imprécis à cause d'informations dispersées." },
        card5: { title: "Toujours Pris par le Quotidien", description: "Les priorités quotidiennes laissent peu de place à l'anticipation ou à l'amélioration de l'organisation du travail." }
      }
    },
    featuresPreviewReversed: {
      title: "Chaque entreprise a des besoins différents.",
      card1: {
        title: "Découvrez nos solutions logicielles personnalisées",
        description: "Logiciels éprouvés conçus pour les opérations agricoles quotidiennes.",
        cta: "En savoir plus"
      },
      card2: {
        title: "Créez votre propre logiciel avec nous",
        description: "Logiciels sur mesure adaptés à vos processus, cultures et organisation.",
        cta: "Commencer"
      },
      card3: {
        title: "Toujours en amélioration. Toujours en construction.",
        description: "Nous continuons à développer et améliorer des outils pour accompagner votre exploitation dans le temps."
      }
    },
    softwareShowcase: {
      ctaButton: "Commencer",
      previewButton: "Aperçu",
      closePreview: "Fermer l'aperçu"
    },
    caseStudies: {
      title: "Cas clients",
      subtitle: "Résultats réels de projets réels",
      sectionTitle: "Projets réels",
      sectionSubtitle: "Des logiciels sur mesure, conçus, développés et déjà utilisés dans des opérations réelles.",
      softwareCard: {
        title: "L'idée",
        description: "Tomatico avait besoin d'une manière plus claire d'organiser sa croissance en tant que marque agricole et d'améliorer la gestion interne de ses opérations.\n\nL'objectif était de développer un logiciel propre permettant de centraliser les informations et de simplifier la gestion quotidienne du projet.\n\nLe système devait permettre de :\n\t•\torganiser le stock et le contrôle des produits\n\t•\tgérer les événements et les actions promotionnelles\n\t•\tcréer une communauté active autour de Tomatico\n\nL'idée était de construire un outil simple reliant toutes ces fonctions dans un seul système."
      },
      impactCard: {
        title: "Le résultat",
        description: "Tomatico Software a été développé — une plateforme interne qui centralise la gestion opérationnelle de la marque et permet de travailler de façon beaucoup plus organisée.\n\nGrâce au système :\n\t•\tl'équipe sait exactement quelles tâches effectuer et quand\n\t•\tles informations sont centralisées et accessibles\n\t•\tles opérations quotidiennes se déroulent avec plus de clarté\n\nLe résultat est un système qui permet à Tomatico de gérer sa croissance de manière plus structurée, en gardant le contrôle sur sa communauté, ses événements et ses produits."
      },
      results: {
        eyebrow: "L'impact",
        stats: [
          { value: "3", label: "domaines dans un seul système" },
          { value: "1", label: "plateforme sur mesure" },
          { value: "100%", label: "informations centralisées" },
          { value: "24/7", label: "accès de l'équipe" },
        ],
      },
      bridgeTitle: "Prêt à construire le vôtre ?",
      bridgeSubtitle: "Chaque solution commence par la compréhension de vos défis uniques.",
      ctaButton: "Commencer"
    },
    ulturePest: {
      hero: {
        titleLine1: "Moins d'improvisation.",
        titleLine2: "Plus de planification.",
        subtitle: "Par des agriculteurs, pour des agriculteurs.",
        bookCall: "Réserver un Appel",
        startFree: "Commencer Gratuitement",
        stat1Label: "Traitements Traçables",
        stat2Label: "Coût Exact par m²",
        stat3Label: "Utilisation de pesticides"
      },
      features: {
        titleLine1: "Concentrez-vous sur votre exploitation.",
        titleLine2: "Nous facilitons le reste.",
        subtitle: "Un seul système pour planifier le travail, suivre les traitements, contrôler les stocks et comprendre les coûts réels de votre exploitation.",
        viewAll: "Voir Toutes les Fonctionnalités",
        bullets: {
          bullet1: "Registres de traitement en PDF. Instantanément.",
          bullet2: "SMS automatiques aux opérateurs pour tâches et alertes.",
          bullet3: "Chaque tâche liée à une parcelle."
        },
        assistant: { title: "Assistant intelligent pour le quotidien.", description: "Utilisez audio, photos ou vidéo pour signaler des incidents, créer des tâches ou analyser l'état des cultures. L'assistant détecte les ravageurs, génère des recommandations basées sur le climat et les données de terrain, assigne le travail à votre équipe et vous aide à prendre des décisions plus rapidement." },
        farm: { title: "Votre exploitation, structurée correctement.", description: "Organisez sites, serres, blocs et secteurs selon la réalité du terrain. Traitements, coûts et cycles reposent sur cette structure." },
        prevention: { title: "Prévention intelligente des ravageurs.", description: "Anticipez les problèmes avant qu'ils n'apparaissent. Analysez les conditions de culture, le climat et la pression des ravageurs pour détecter les risques précoces. Recevez des alertes préventives et des recommandations claires pour agir à temps et protéger votre production." },
        spraylogs: { title: "Registres de traitements, toujours disponibles.", description: "Créez des registres en quelques secondes, filtrez selon vos besoins et exportez des PDF prêts pour les audits. Des rapports clairs pour les contrôles et le suivi quotidien." },
        treatments: { title: "Suivez les applications, là où cela compte.", description: "Surveillez traitements, pression des ravageurs et état des cultures par bloc et par cycle. Une traçabilité complète des applications biologiques et chimiques." },
        stock: { title: "Un inventaire qui se met à jour automatiquement.", description: "Importez vos stocks rapidement et laissez les quantités s'actualiser après chaque traitement. Définissez des seuils et soyez alerté avant la rupture." },
        tasks: { title: "Passez de la planification à l'action.", description: "Attribuez des tâches aux équipes et aux zones, suivez l'exécution et utilisez les rappels SMS. Chaque opérateur voit uniquement ce qui le concerne." },
        cycles: { title: "Planifiez les cycles en toute clarté.", description: "Créez des cycles personnalisés, appliquez-les à plusieurs blocs et gérez-les facilement. Comparez coûts et performances d'un cycle à l'autre." }
      },
      video: {
        title: "Voyez-le en",
        titleHighlight: "action",
        subtitle: "Regardez comment les gestionnaires de serres utilisent notre plateforme au quotidien.",
        stat1Label: "Gain d'efficacité",
        stat2Label: "Rapports plus rapides",
        stat3Label: "Accès aux données"
      }
    }
  },
  
  pt: {
    nav: {
      homepage: "Início",
      aboutUs: "Sobre Nós",
      software: "Our Apps",
      personalize: "Soluções Personalizadas",
      caseStudies: "Casos de Sucesso",
      blog: "Blog",
      contactUs: "Contato",
      bookingCall: "Agendar Chamada"
    },
    personalize: {
      title: "Crie seu próprio",
      subtitle: "software",
      swipeHint: "Deslize para explorar →",
      dashboardPreview: "Prévia do painel",
      whatWeBuildTitle: "O que construímos",
      whatWeBuildSubtitle: "Software personalizado, concebido, desenvolvido e já em funcionamento em operações reais.",
      bridgeSection: {
        title: "Não é um modelo. Nem uma plataforma genérica.",
        subtitle: "Criado em torno dos seus fluxos de trabalho. Feito para o uso diário, não para demonstrações."
      },
      softwareCard: {
        title: "Software",
        description: "Criámos um sistema personalizado para gerir a manutenção de máquinas, localizações, avarias e intervenções programadas num único local. Cada equipamento tinha o seu histórico completo, com tarefas atribuídas automaticamente e estados visíveis em tempo real."
      },
      impactCard: {
        title: "Resultados",
        description: "A empresa reduziu o tempo de paragem, respondeu mais rapidamente a falhas e passou a ter controlo real sobre custos e alertas recorrentes. A manutenção deixou de ser improvisada e passou a ser previsível e organizada."
      },
      newCard1: {
        title: "Sua Visão",
        description: "Você entende seu negócio melhor do que ninguém.\nVocê sabe o que precisa melhorar, o que atrasa as coisas e onde as ferramentas atuais falham.\n\nSua visão não é sobre adicionar software — é sobre tornar o trabalho diário mais claro, simples e fácil de gerir.\n\nÉ aí que começamos.",
        keyPoints: ["Seus fluxos de trabalho", "Suas limitações", "Suas prioridades"]
      },
      newCard2: {
        title: "Nossa Solução",
        description: "Transformamos essa visão em software construído em torno de como seu negócio realmente funciona.\n\nSem templates. Sem plataformas genéricas.\nApenas ferramentas práticas desenhadas para uso diário, moldadas através de colaboração e feedback real.",
        keyPoints: ["Construído para operações reais", "Feito para uso diário", "Cresce com o seu negócio"]
      },
      buttons: {
        startBuilding: "Comece a criar a sua solução",
        viewCaseStudies: "Veja o que já construímos"
      },
      mobileButtons: {
        startBuilding: "Começar",
        viewCaseStudies: "O nosso trabalho"
      },
      workProcess: {
        sectionTitle: "Como trabalhamos",
        sectionTitleHighlight: "consigo",
        sectionSubtitle: "Da ideia a um software pronto para produção, com um processo claro e transparente. Trabalhamos lado a lado consigo, de forma simples, estruturada e focada em resultados reais.",
        steps: {
          step1: {
            week: "Semana 1",
            title: "Compreender a sua",
            highlight: "operação",
            description: "Começamos por entender como o seu negócio funciona na prática. Esta fase é dedicada a ouvir, mapear processos e definir o que o software deve fazer — e o que não deve.",
            features: ["Sessões de análise com a sua equipa", "Revisão dos fluxos de trabalho e ferramentas atuais", "Definição de objetivos, escopo e prioridades"]
          },
          step2: {
            week: "Semanas 2-4",
            title: "Desenvolvimento e",
            highlight: "melhoria",
            description: "Com a nossa plataforma interna, começamos a desenvolver rapidamente e a iterar com base em feedback real. Acompanha o progresso semanalmente e ajusta quando necessário.",
            features: ["Desenvolvimento rápido com ferramentas proprietárias", "Reuniões semanais e demos ao vivo", "Testes e melhorias contínuas"]
          },
          step3: {
            week: "Semana 5",
            title: "Lançamento e",
            highlight: "entrega",
            description: "Quando o software está pronto, preparamos tudo para uso real. A equipa é formada, os dados configurados e o sistema entra em produção com o nosso apoio.",
            features: ["Onboarding e formação da equipa", "Configuração ou migração de dados", "Lançamento em produção com monitorização"]
          },
          step4: {
            week: "Contínuo",
            title: "Suporte e evolução",
            highlight: "contínua",
            description: "O lançamento não é o fim. Mantemo-nos envolvidos à medida que o seu negócio evolui.",
            features: ["Melhorias e ajustes contínuos", "Novas funcionalidades conforme as necessidades", "Suporte técnico fiável quando necessário"]
          }
        },
        cta: {
          title: "Pronto para",
          titleHighlight: "Começar",
          subtitle: "Agende sua chamada gratuita e comece a transformar seu negócio.",
          buttonText: "Agendar Chamada Gratuita"
        }
      }
    },
    hero: {
      badge: "Plataforma de Agricultura Digital",
      title: "Cuide do Campo.\nDo Resto cuidamos nós.",
      subtitle: "Desde pedidos e faturas até controle de produção, ajudamos você a organizar e automatizar seu negócio agrícola.",
      heroSubtitle: "Software, soluções à medida e consultoria para trazer clareza e controlo às operações agrícolas.",
      cta: "Descubra as nossas soluções",
      ctaSecondary: "Agende uma demonstração gratuita"
    },
    whatIsUlture: {
      badge: "O que é a Ulture?",
      title: "Cansados de não ter controle",
      titleHighlight: "sobre tudo o que acontece",
      subtitle: "Perder tempo, dinheiro e trabalho porque nada está num só lugar. É isso que resolvemos.",
      painPoints: {
        time: { title: "Tempo perdido", description: "Saltando entre planilhas, papéis e aplicativos que não se conversam." },
        money: { title: "Dinheiro fugido", description: "Sem visibilidade real dos custos, margens e onde a rentabilidade escapa." },
        work: { title: "Trabalho duplicado", description: "Inserindo os mesmos dados uma e outra vez porque nada está conectado." }
      },
      cta: "Descubra como resolvemos"
    },
    metrics: {
      problemsSolved: "Problemas Resolvidos",
      savedPerYear: "Economizados por Ano",
      efficiency: "Eficiência Operacional"
    },
    categories: {
      title: "Tudo o que você precisa para",
      titleHighlight: "Crescer Seu Negócio",
      subtitle: "Seis áreas-chave onde Ulture ajuda você a economizar tempo, reduzir custos e aumentar a eficiência.",
      administration: { title: "Administração e Contabilidade", description: "Automatize faturas, contratos e acompanhamento financeiro com fluxos inteligentes." },
      workers: { title: "Trabalhadores e Tarefas", description: "Gerencie equipes, atribua tarefas e acompanhe a produtividade em tempo real." },
      production: { title: "Produção e Estoque", description: "Monitore níveis de inventário, ciclos de produção e logística." },
      costs: { title: "Custos e Lucros", description: "Obtenha insights sobre despesas, margens e análises de rentabilidade." },
      clients: { title: "Clientes e Pedidos", description: "Otimize a gestão de pedidos e mantenha relacionamentos sólidos com clientes." },
      marketing: { title: "Marketing e Imagem", description: "Construa sua presença de marca e alcance novos mercados efetivamente." }
    },
    footer: {
      copyright: "© 2026 Ulture",
      tagline: "Feito com carinho para quem alimenta o mundo.",
      product: "Produto",
      company: "Empresa",
      legal: "Legal",
      privacy: "Política de Privacidade",
      terms: "Termos de Serviço",
      cookiePolicy: "Política de Cookies",
      dpa: "DPA"
    },
    whyWeExist: {
      badge: "Nossa História",
      title: "Criado por agricultores",
      titleHighlightBlue: "para agricultores",
      subtitle: "Um caminho de descoberta rumo à transformação digital agrícola",
      steps: {
        challenge: { number: "01", title: "O Desafio", text: "Os agricultores são especialistas como ninguém na produção de frutas e vegetais, mas não em contabilidade, gestão de dados ou marketing. Percebemos que enquanto seus campos prosperavam, seus sistemas de gestão estavam desatualizados." },
        revelation: { number: "02", title: "A Revelação", text: "Descobrimos que 70% do tempo administrativo é perdido navegando entre ferramentas incompatíveis e duplicando dados." },
        solution: { number: "03", title: "A Solução", text: "Criamos um ecossistema unificado onde cada ferramenta se comunica perfeitamente, eliminando atritos e automatizando processos." },
        result: { number: "04", title: "O Resultado", text: "Agricultores que recuperam horas do seu dia, reduzem erros e tomam decisões baseadas em dados reais em tempo real." }
      },
      mission: {
        badge: "Nossa Missão",
        title: "Acelerar a Transformação Digital",
        titleHighlight: "da Agricultura",
        subtitle: "Construindo tecnologia que entende as necessidades reais do campo"
      },
      pillars: {
        automation: { label: "Automação", description: "Processos sem atrito" },
        integration: { label: "Integração", description: "Tudo conectado" },
        intelligence: { label: "Inteligência", description: "IA adaptativa" },
        efficiency: { label: "Eficiência", description: "Resultados reais" }
      }
    },
    macbook: {
      title: "Software Poderoso. Interface Simples.",
      subtitle: "Gerencie toda a sua operação agrícola de uma plataforma"
    },
    about: {
      badge: "Sobre a Ulture / Ulture",
      valueLabel: "Valor",
      hero: {
        title: "Redefinindo Como a Agricultura Opera",
        subtitle: "Somos uma equipe apaixonada por tecnologia e agricultura, trabalhando para digitalizar e otimizar negócios agrícolas em todo o mundo."
      },
      story: {
        title: "Nossa História",
        paragraph1: "A Ulture nasceu de uma profunda compreensão dos desafios que os agricultores enfrentam todos os dias. Nossos fundadores, com raízes tanto na agricultura quanto na tecnologia, viram uma oportunidade de fechar a lacuna entre a agricultura tradicional e as soluções digitais modernas.",
        paragraph2: "Desde 2020, desenvolvemos ferramentas que simplificam a complexidade da gestão agrícola. De pequenas fazendas familiares a grandes empresas agrícolas, nossa plataforma se adapta às necessidades únicas de cada negócio.",
        paragraph3: "Hoje, ajudamos centenas de agricultores na Europa e América Latina a gerenciar suas operações de forma mais eficiente, reduzir custos e aumentar sua rentabilidade.",
        years: "Anos de Experiência"
      },
      mvv: {
        title: "Missão, Visão e Valores",
        subtitle: "Os princípios que guiam tudo o que fazemos"
      },
      mission: {
        title: "Nossa Missão",
        description: "Capacitar agricultores e empresas agrícolas com ferramentas digitais intuitivas que simplificam a gestão, aumentam a eficiência e promovem o crescimento sustentável."
      },
      vision: {
        title: "Nossa Visão",
        description: "Tornar-se a plataforma de referência global para digitalização agrícola, onde cada agricultor tenha acesso à tecnologia que antes estava disponível apenas para grandes corporações."
      },
      values: {
        innovation: { title: "Inovação", description: "Buscamos constantemente novas formas de resolver problemas antigos, aproveitando tecnologia de ponta para criar soluções práticas." },
        commitment: { title: "Compromisso", description: "Estamos profundamente comprometidos com o sucesso de nossos clientes. O crescimento deles é o nosso crescimento, e os acompanhamos em cada passo." },
        collaboration: { title: "Colaboração", description: "Acreditamos no poder do trabalho em equipe. Fazemos parcerias com agricultores, agrônomos e especialistas do setor para construir soluções melhores." }
      },
      team: {
        title: "Conheça a nossa equipa fundadora",
        subtitle: "As pessoas por trás da Ulture: com raízes no campo e paixão pela tecnologia.",
        headline: "A equipa",
        description: "Somos uma equipe apaixonada por tecnologia e eficiência, trabalhando para ajudar empresas em todo o mundo a operar de forma mais inteligente.",
        ceo: "Growth & Strategy",
        cto: "CTO e Co-Fundadora",
        coo: "Diretor de Operações",
        head_product: "Diretora de Produto",
        roles: {
          ceoCofounder: "Growth & Strategy",
          ctoCofounder: "CTO e Co-Fundador",
          headProduct: "Diretor de Produto",
          headDevelopment: "Diretor de Desenvolvimento"
        }
      },
      timeline: {
        title: "Do campo, para o campo",
        subtitle: "Marcos importantes em nossa história",
        welcomeText: "Isto é ",
        flipWords: ["Ulture", "A nossa história"],
        milestone1: { date: "1974", title: "As origens da exploração familiar", description: "A nossa história começa na agricultura. Em 1974, os nossos avós construíram uma das primeiras estufas em Almeria, Espanha. Um trabalho manual, baseado na experiência, no esforço e na ligação à terra." },
        milestone2: { date: "2020", title: "O crescimento traz novos desafios", description: "Com o crescimento do negócio familiar, as estufas modernas melhoraram a produção, mas a gestão operacional, administrativa e dos custos tornou-se mais complexa." },
        milestone3: { date: "2024", title: "O problema torna-se claro", description: "O trabalho diário na exploração revelou um desequilíbrio: a tecnologia avançava, mas a forma de gerir o dia a dia não. A informação estava dispersa e os processos eram demasiado manuais." },
        milestone4: { date: "Meados de 2025", title: "Desenvolvido para uso real", description: "A primeira versão do software é desenvolvida e utilizada diretamente nas estufas da família, como uma ferramenta prática para resolver problemas reais." },
        milestone5: { date: "Fevereiro 2026", title: "Lançamento da Ulture", description: "Após meses de utilização real e melhorias contínuas, a Ulture é lançada oficialmente, mantendo o foco na simplicidade e utilidade." },
        milestone6: { date: "Abril 2026", title: "Ulture Pest", description: "A Ulture Pest é apresentada como a primeira solução, dedicada ao controlo de pragas, tratamentos e registos de aplicação." },
        milestone7: { date: "2026", title: "O que vem a seguir", description: "A Ulture continua a evoluir. Novas soluções estão em desenvolvimento, sempre orientadas pelas necessidades reais do campo." },
      },
      cta: {
        title: "Pronto para Transformar sua Fazenda?",
        description: "Junte-se a centenas de agricultores que já estão economizando tempo e dinheiro com a Ulture.",
        contact: "Entre em Contato",
        demo: "Solicitar Demo"
      }
    },
    aboutStory: {
      sectionTitle: "Nossa História",
      sectionSubtitle: "Nossa história, construída do campo",
      intro: {
        title: "Estávamos cansados de ver ineficiências na agricultura",
        subtitle: "então um dia decidimos mudar as coisas."
      },
      challenge: {
        title: "O Desafio",
        text: "Os produtores são especialistas como ninguém em cultivar frutas e verduras — mas não em contabilidade, logística ou gestão de dados. Percebemos que enquanto seus campos prosperavam, seus sistemas de gestão estavam desatualizados."
      },
      realization: {
        title: "A Percepção",
        text: "A maioria das empresas de software construía ferramentas de escritórios, não dos campos. Nenhuma delas realmente entendia o que os produtores precisavam. Depois de anos trabalhando lado a lado com agricultores, decidimos projetar soluções baseadas em experiência real, não em teoria."
      },
      solution: {
        title: "A Solução",
        text: "Construímos uma plataforma que automatiza operações, centraliza informações e transforma a gestão agrícola em um sistema conectado, digital e centrado nas pessoas."
      },
      transformation: {
        title: "A Transformação",
        text: "Quando os produtores se concentram em cultivar e nós cuidamos do resto — tecnologia, dados, coordenação — os resultados são extraordinários. Melhores frutos, melhor eficiência, melhores vidas para quem alimenta o mundo."
      },
      mission: {
        title: "Nossa Missão",
        subtitle: "Acelerar a transformação digital da agricultura",
        pillars: {
          automation: { title: "Automação", text: "Tornando cada processo mais eficiente — da produção às vendas" },
          integration: { title: "Integração", text: "Conectando todas as partes da sua operação em um só lugar" },
          dataIntelligence: { title: "Inteligência de Dados", text: "Transformando informação em decisões estratégicas" }
        }
      }
    },
    blog: {
      badge: "Últimas novidades",
      title: "Blog e recursos",
      subtitle: "Descubra as últimas novidades, dicas e histórias sobre agricultura digital e gestão agrícola.",
      heroTitle: "As últimas novidades agrícolas",
      searchPlaceholder: "Pesquisar artigos...",
      postTitle: "Como a tecnologia está transformando a agricultura",
      postExcerpt: "Explore como as ferramentas digitais modernas ajudam os agricultores a aumentar a eficiência e reduzir custos.",
      readMore: "Leia mais",
      comingSoonTitle: "Mais conteúdo em breve!",
      comingSoonText: "Estamos trabalhando para trazer mais informações e recursos valiosos."
    },
    contact: {
      badge: "Entre em Contato",
      title: "Contato",
      subtitle: "Tem dúvidas sobre nossa plataforma? Adoraríamos ouvir você.",
      emailLabel: "Email",
      phoneLabel: "Telefone",
      locationLabel: "Localização",
      infoTitle: "Vamos conversar",
      infoSubtitle: "Seja sobre funcionalidades, preços ou qualquer outra coisa, nossa equipe está pronta para responder.",
      responseTime: "Geralmente respondemos em 24 horas",
      successTitle: "Mensagem enviada!",
      successMessage: "Entraremos em contato em breve.",
      thankYouTitle: "Obrigado!",
      thankYouMessage: "Sua mensagem foi enviada com sucesso. Entraremos em contato em breve.",
      sendAnother: "Enviar outra mensagem",
      nameLabel: "Nome",
      namePlaceholder: "Seu nome",
      emailFormLabel: "Email",
      emailPlaceholder: "seu@email.com",
      companyLabel: "Empresa (opcional)",
      companyPlaceholder: "Sua empresa",
      messageLabel: "Mensagem",
      messagePlaceholder: "Diga-nos como podemos ajudar...",
      sending: "Enviando...",
      submitButton: "Enviar Mensagem"
    },
    software: {
      badge: "Nossa Plataforma",
      title: "Software Poderoso para Agricultura Moderna",
      subtitle: "Uma plataforma para gerenciar toda sua operação agrícola — do campo às finanças.",
      cta: "Começar",
      ctaSecondary: "Solicitar Demo",
      screenshotAlt: "Painel de controle Ulture",
      featuresTitle: "Tudo que você precisa para gerenciar sua fazenda",
      featuresSubtitle: "Nossa suite completa de ferramentas cobre todos os aspectos da gestão agrícola.",
      features: {
        administration: { title: "Administração e Contabilidade", description: "Automatize faturas, contratos e acompanhamento financeiro com fluxos inteligentes." },
        workers: { title: "Trabalhadores e Tarefas", description: "Gerencie equipes, atribua tarefas e acompanhe a produtividade em tempo real." },
        production: { title: "Produção e Estoque", description: "Monitore níveis de inventário, ciclos de produção e logística." },
        costs: { title: "Custos e Lucros", description: "Obtenha insights sobre despesas, margens e análises de rentabilidade." },
        analytics: { title: "Painel Analítico", description: "Informações em tempo real e relatórios personalizados para decisões baseadas em dados." },
        automation: { title: "Automação Inteligente", description: "Fluxos de trabalho com IA que economizam tempo e reduzem erros manuais." }
      },
      benefitsTitle: "Por que escolher a Ulture?",
      benefitsSubtitle: "",
      benefits: {
        item1: "Economize mais de 10 horas por semana em tarefas administrativas",
        item3: "Visibilidade em tempo real de todas as operações",
        item4: "Integração perfeita com ferramentas existentes",
        item5: "Suporte e assistência constantes da nossa equipe",
        item6: "Atualizações regulares com novos recursos"
      },
      stats: {
        farms: "Fazendas usando Ulture",
        savings: "Economia média de custos",
        time: "Tempo semanal economizado",
        satisfaction: "Satisfação do cliente"
      },
      connectivity: {
        badge: "Rede Global",
        title: "Conectividade global",
        description: "Gerencie sua operação agrícola de qualquer lugar do mundo.",
        heroTitle: "Conecte-se com sua agricultura de qualquer lugar do mundo",
        heroSubtitle: "Uma plataforma global para gerenciar, analisar e tomar decisões agrícolas sem fronteiras.",
        stats: {
          countries: "Países",
          farms: "Fazendas",
          availability: "Disponibilidade",
          uptime: "Tempo ativo"
        }
      },
      ctaTitle: "Pronto para transformar sua fazenda?",
      ctaSubtitle: "Comece seu teste gratuito hoje e descubra como a Ulture pode ajudá-lo a economizar tempo e dinheiro.",
      ctaButton: "Iniciar Teste Gratuito",
      ctaButtonSecondary: "Falar com Vendas"
    },
    heroInfrastructure: {
      title: "Conecte-se de qualquer lugar",
      subtitle: "Uma infraestrutura digital viva que conecta campos, equipes e dados em tempo real.",
      tag1: "Dados em tempo real",
      tag2: "Decisões com IA",
      tag3: "Escala global",
      tag4: "Automação primeiro"
    },
    operations: {
      title: "Uma plataforma. Controle operacional total.",
      subtitle: "Gerencie todos os aspectos da sua operação agrícola a partir de um único sistema conectado",
      footer: "Uma plataforma. Uma única fonte de verdade. Controle operacional total.",
      learnMore: "Explorar Operações",
      intro: {
        title: "Uma Plataforma, Controle Operacional Completo",
        text: "Gerenciar uma operação agrícola não deveria exigir dezenas de ferramentas diferentes. Nossa plataforma centraliza tudo o que você precisa em um único sistema integrado, eliminando a complexidade e multiplicando sua eficiência. Do planejamento ao controle de custos, cada módulo trabalha em conjunto para lhe dar visibilidade completa e controle total da sua operação. Chega de dados fragmentados, planilhas desorganizadas ou informações perdidas entre sistemas.",
        bulletsTitle: "Com nossa plataforma unificada você obtém:",
        bullets: {
          visibility: { title: "Visibilidade total", desc: "Todos os seus dados operacionais em um só lugar, atualizados em tempo real" },
          decisions: { title: "Decisões mais inteligentes", desc: "Ferramentas de previsão e análise que preveem tendências e otimizam recursos" },
          savings: { title: "Economia automática", desc: "Controle de custos integrado que identifica oportunidades de otimização sem esforço manual" },
          duplication: { title: "Zero duplicação", desc: "Insira as informações uma vez e elas se sincronizam automaticamente em todos os módulos" },
          scalability: { title: "Escalabilidade garantida", desc: "Cresça de 10 hectares para 1.000 sem mudar de sistema" }
        }
      },
      tabs: {
        mission: {
          title: "Nossa Missão",
          heading: "Nossa Missão",
          description: "Capacitar empresas com ferramentas digitais intuitivas que simplificam operações, aumentam a eficiência e impulsionam o crescimento sustentável."
        },
        vision: {
          title: "Nossa Visão",
          heading: "Nossa Visão",
          description: "Tornar-se a plataforma de referência para operações empresariais, onde cada equipe tem acesso a ferramentas de nível empresarial."
        },
        innovation: {
          title: "Inovação",
          heading: "Inovação",
          description: "Buscamos constantemente novas maneiras de resolver problemas complexos, aproveitando tecnologia de ponta para criar soluções práticas."
        },
        commitment: {
          title: "Compromisso",
          heading: "Compromisso",
          description: "Estamos profundamente comprometidos com o sucesso de nossos clientes. O crescimento deles é o nosso crescimento, e os apoiamos em cada passo."
        },
        collaboration: {
          title: "Colaboração",
          heading: "Colaboração",
          description: "Acreditamos no poder de trabalhar juntos. Fazemos parcerias com equipes, desenvolvedores e especialistas da indústria para construir melhores soluções."
        }
      }
    },
    blogPosts: {
      post1: {
        title: "Como a IA está Revolucionando a Agricultura Moderna",
        subtitle: "Tecnologia inteligente para agricultura sustentável",
        category: "IA e Tecnologia",
        excerpt: "Do monitoramento de culturas em tempo real à análise preditiva, a IA está transformando como cultivamos e gerenciamos operações agrícolas.",
        readTime: "6 min de leitura"
      },
      post2: {
        title: "Tomando Decisões Agrícolas Baseadas em Dados",
        subtitle: "Transforme informação em ação",
        category: "Gestão de Dados",
        excerpt: "Aprenda como sistemas centralizados de dados ajudam produtores a tomar melhores decisões, reduzir custos e melhorar a rentabilidade.",
        readTime: "5 min de leitura"
      },
      post3: {
        title: "Agricultura Sustentável Através da Tecnologia",
        subtitle: "Melhor para o planeta, melhor para os negócios",
        category: "Sustentabilidade",
        excerpt: "Descubra como a tecnologia inteligente permite práticas sustentáveis que beneficiam tanto o meio ambiente quanto seus resultados.",
        readTime: "7 min de leitura"
      },
      post4: {
        title: "Automação em Operações Agrícolas",
        subtitle: "Trabalhe de forma mais inteligente, não mais difícil",
        category: "Automação",
        excerpt: "Explore como a automação reduz trabalho manual, elimina erros e permite que equipes foquem em atividades estratégicas.",
        readTime: "6 min de leitura"
      },
      post5: {
        title: "Previsão de Produção com Confiança",
        subtitle: "Planeje com dados, não com suposições",
        category: "Planejamento e Previsões",
        excerpt: "Aprenda como modelos preditivos ajudam produtores a antecipar produção, gerenciar inventário e cumprir compromissos com clientes.",
        readTime: "7 min de leitura"
      },
      post6: {
        title: "Controle de Custos e Rentabilidade na Agricultura",
        subtitle: "Conheça seus números, melhore suas margens",
        category: "Gestão Financeira",
        excerpt: "Entenda como a alocação e análise correta de custos ajudam a identificar ineficiências e aumentar a rentabilidade.",
        readTime: "8 min de leitura"
      }
    },
    blogPage: {
      notFound: "Artigo não encontrado",
      backToBlog: "Voltar ao blog",
      quickInsight: "Em resumo",
      readNext: "Próximo artigo",
      readArticle: "Ler artigo",
      allRights: "Todos os direitos reservados."
    },
    buttons: {
      startNow: "Começar Agora",
      learnMore: "Explorar Soluções",
      getStarted: "Iniciar",
      bookDemo: "Agendar Demo",
      contactUs: "Contactar",
      viewMore: "Ver Mais"
    },
    wobbleCards: {
      card1: {
        title: "Controle Digital Completo",
        description: "Gerencie toda a sua operação agrícola em uma única plataforma. De pedidos e faturas ao rastreamento de produção e análise de custos.",
        imageAlt: "Dashboard da plataforma Ulture"
      },
      card2: {
        title: "Automação Inteligente",
        description: "Reduza o trabalho manual e elimine erros. Nossos fluxos inteligentes se adaptam à sua operação."
      },
      card3: {
        title: "Conectividade Global",
        description: "Acesse seus dados de qualquer lugar. Sincronização em tempo real em todos os seus dispositivos e equipes.",
        imageAlt: "Dashboard da plataforma Ulture"
      }
    },
    ourStory: {
      mainTitle: "Estávamos cansados de ver ineficiências na agricultura, então um dia decidimos mudar as coisas.",
      challenge: {
        badge: "O Desafio",
        title: "O Desafio",
        description: "Os produtores são especialistas como ninguém em cultivar frutas e vegetais — mas não em contabilidade, logística ou gestão de dados. Percebemos que enquanto seus campos prosperavam, seus sistemas de gestão estavam desatualizados."
      },
      realization: {
        badge: "A Percepção",
        title: "A Percepção",
        description: "A maioria das empresas de software construía ferramentas de escritórios, não dos campos. Nenhuma delas realmente entendia o que os produtores precisavam. Depois de anos trabalhando lado a lado com agricultores, decidimos projetar soluções baseadas em experiência real, não em teoria."
      },
      solution: {
        badge: "A Solução",
        title: "A Solução",
        description: "Construímos uma plataforma que automatiza operações, centraliza informações e transforma a gestão agrícola em um sistema conectado, digital e centrado nas pessoas."
      },
      stats: {
        betterFruits: { title: "Melhores Frutas", subtitle: "Qualidade que você pode medir" },
        betterEfficiency: { title: "Melhor Eficiência", subtitle: "Economia de tempo e custos" },
        betterLives: { title: "Melhores Vidas", subtitle: "Para quem alimenta o mundo" }
      }
    },
    techSection: {
      eyebrow: "PARA AGRICULTURA REAL E OPERAÇÕES DIFÍCEIS DE DIGITALIZAR",
      headlinePart1: "Construímos nossa própria tecnologia para ",
      headlineHighlight: "construir seu próprio software.",
      subheadlineHighlight: "Não somos um SaaS genérico.",
      subheadline: "Projetamos e desenvolvemos soluções de software sob medida adaptadas às necessidades reais de cada operação agrícola — de pequenos produtores a negócios complexos e multicanal.",
      block1: {
        title: "Construímos nossa própria tecnologia",
        description: "Desenvolvemos nossa própria plataforma interna e ferramentas para projetar, implantar e evoluir software de forma eficiente.",
        bullets: [
          "Mover rápido sem sacrificar confiabilidade",
          "Adaptar soluções a casos de uso muito específicos",
          "Construir software que se ajusta a como as pessoas realmente trabalham no campo"
        ]
      },
      block2: {
        title: "Software adaptado a cada operação",
        description: "Cada fazenda, empresa e operação é diferente. Por isso não vendemos software tamanho único.",
        bullets: [
          "O tamanho da operação",
          "O tipo de culturas ou produtos",
          "Fluxos de trabalho e ferramentas existentes",
          "O nível de maturidade digital da equipe"
        ]
      },
      block3: {
        title: "Não limitado a uma indústria",
        description: "Embora nossas raízes estejam na agricultura, nossa tecnologia nos permite construir software personalizado para outras indústrias com operações complexas.",
        bullets: [
          "Complexidade operacional",
          "Processos manuais",
          "Dados desconectados",
          "Necessidade de controle e automação"
        ]
      },
      supportingSentence: "Não pedimos às operações que se adaptem ao software. Construímos software que se adapta às operações.",
      cta: "Fale conosco sobre sua operação"
    },
    magicBento: {
      title: "Funcionalidades",
      titleHighlight: "Chave",
      subtitle: "Tudo o que você precisa para gerenciar seu negócio de forma eficiente.",
      cta: "Saiba mais",
      documents: { 
        title: "Registos e Relatórios", 
        description: "Registe todas as atividades e gere relatórios em segundos. Filtre por campo, cultura ou data para encontrar o que precisa.",
        reportFiles: [
          { name: "Uso de Tratamentos Bloco 1", date: "Maio 2026", type: "pdf" },
          { name: "Relatório Custos de Inventário", date: "Abril 2026", type: "xlsx" },
          { name: "Resumo de Colheita T1", date: "2026", type: "pdf" },
          { name: "Registo Atividades do Campo", date: "Junho 2026", type: "docx" },
          { name: "Relatório Aplicação de Pesticidas", date: "Maio 2026", type: "pdf" }
        ]
      },
      notifications: { 
        title: "Visão clara", 
        description: "Veja o que acontece entre campos e atividades para apoiar melhores decisões.",
        notificationItems: [
          { text: "Tratamento Bloco 1 executado ✓", icon: "✓", color: "#00C9A7" },
          { text: "+50kg comprados: Produto A", icon: "📦", color: "#1E86FF" },
          { text: "Nível de stock: Bom", icon: "📊", color: "#00C9A7" },
          { text: "Relatório mensal automático gerado", icon: "📄", color: "#FFB800" }
        ]
      },
      integrations: { title: "Integrações", description: "Compatível com mais de 100 integrações." },
      calendar: { title: "Calendário", description: "Use o calendário para organizar suas tarefas." }
    },
    aiWorkflowBento: {
      title: "desenvolvida em torno das suas operações",
      titleHighlight: "Tecnologia",
      titlePart1: "Desenvolvida em Torno das",
      titlePart2: "Suas Operações",
      subtitle: "",
      cta: "Saiba mais",
      collaboration: {
        title: "Digitalização Operacional",
        description: "Estruturamos e digitalizamos as operações agrícolas diárias para maior controlo e eficiência.",
        menuItems: {
          planTask: "Planear Tarefa",
          assignTask: "Atribuir Tarefa",
          manageTeam: "Gerir Equipa",
          analytics: "Análises"
        }
      },
      integrations: {
        title: "Operações Conectadas",
        description: "Ligamos ferramentas, dados e equipas para que a informação flua com clareza e nada se perca entre sistemas."
      },
      insights: {
        title: "Clareza e Controlo",
        description: "Tenha uma visão clara e em tempo real das operações, dos recursos e do desempenho, para tomar melhores decisões com confiança.",
        metric: "Kg Colhidos"
      },
      automation: {
        title: "Planeamento Inteligente e Alertas",
        description: "Planeie com antecedência e receba lembretes e alertas automáticos sempre que seja necessária atenção, para que nada passe despercebido.",
        notifications: {
          meeting: "Lembrete de reunião",
          treatment: "Lembrete de tratamento: Bloco 1",
          lowStock: "Alerta: Níveis de stock baixos"
        }
      }
    },
    scrollVelocity: {
      line1: "Ulture Software",
      line2: "Controle",
      line3: "Análise",
      line4: "Automação",
      line5: "Eficiência",
      line6: "Integração",
      line7: "Inovação",
      line8: "Crescimento"
    },
    featuresPreview: {
      titleLine1: "Faça crescer o seu negócio.\nMelhore a visibilidade.\nMantenha o controlo.",
      titleLine2: "Nós facilitamos.",
      subtitle: "À medida que aumentam as equipas, os campos e as atividades, a informação torna-se mais difícil de acompanhar e o trabalho diário mais complexo de gerir.",
      exploreSolutions: "Descubra as nossas soluções",
      viewAll: "Ver Todas as Funcionalidades",
      highlights: {
        monitoring: "Registros de pulverização em PDF. Instantaneamente.",
        alerts: "SMS automático para trabalhadores sobre tarefas e alertas.",
        analytics: "Cada tarefa vinculada a um bloco."
      },
      seeItTitle: "Veja em",
      seeItHighlight: "ação",
      dashboardDescription: "Um painel unificado que dá visibilidade completa das suas operações em estufa.",
      stats: {
        pesticide: "Menos uso de pesticidas",
        reporting: "Relatórios mais rápidos",
        monitoring: "Monitoramento"
      },
      joinText: "Junte-se a centenas de operadores de estufas que transformaram sua gestão de pragas com nossa plataforma. De pequenas fazendas familiares a grandes operações comerciais, nossa plataforma se adapta às suas necessidades.",
      features: {
        control: { title: "Controle Total, Todo Dia", description: "Gerencie suas estufas, rastreie cada praga e registre todos os tratamentos de uma plataforma unificada. Saiba exatamente o que está acontecendo em toda sua operação." },
        costs: { title: "Saiba Para Onde Vai Seu Dinheiro", description: "Rastreie cada despesa, veja seu custo por hectare e entenda para onde vai seu dinheiro. Sem mais adivinhações—apenas números claros que ajudam você a tomar melhores decisões." },
        oversight: { title: "Sempre Um Passo à Frente", description: "Alertas automatizados, monitoramento de limiares e lembretes inteligentes garantem que você nunca perca um tratamento crítico ou prazo. Fique à frente dos problemas antes que cresçam." }
      },
      cards: {
        card1: { title: "Custos e Lucros Difíceis de Acompanhar", description: "Sem dados fiáveis das operações diárias, torna-se difícil compreender os custos reais e a rentabilidade." },
        card2: { title: "Demasiadas Ferramentas, Sem Visão Global", description: "A informação está espalhada por ficheiros Excel, mensagens, cadernos e softwares que não comunicam entre si." },
        card3: { title: "Os Problemas São Detetados Tarde Demais", description: "Falhas, faltas de stock ou erros são muitas vezes identificados apenas depois de afetarem a operação." },
        card4: { title: "Registos e Relatórios Pouco Fiáveis", description: "Os registos e relatórios exigem tempo e são frequentemente incompletos ou imprecisos devido à dispersão da informação." },
        card5: { title: "Sempre Focados no Dia a Dia", description: "As prioridades diárias deixam pouco espaço para planear com antecedência ou melhorar a organização do trabalho." }
      }
    },
    featuresPreviewReversed: {
      title: "Cada empresa tem necessidades diferentes.",
      card1: {
        title: "Descubra as nossas soluções de software personalizadas",
        description: "Software comprovado, desenvolvido para as operações agrícolas do dia a dia.",
        cta: "Saiba mais"
      },
      card2: {
        title: "Crie o seu próprio software connosco",
        description: "Software à medida adaptado aos seus processos, culturas e estrutura.",
        cta: "Começa agora"
      },
      card3: {
        title: "Sempre a melhorar. Sempre a construir.",
        description: "Continuamos a desenvolver e melhorar ferramentas para apoiar a sua operação ao longo do tempo."
      }
    },
    softwareShowcase: {
      ctaButton: "Começa agora",
      previewButton: "Pré-visualização",
      closePreview: "Fechar pré-visualização"
    },
    caseStudies: {
      title: "Casos de sucesso",
      subtitle: "Resultados reais de projetos reais",
      sectionTitle: "Projetos reais",
      sectionSubtitle: "Software personalizado, concebido, desenvolvido e já em funcionamento em operações reais.",
      softwareCard: {
        title: "A ideia",
        description: "A Tomatico precisava de uma forma mais clara de organizar o seu crescimento enquanto marca agrícola e melhorar a gestão interna das suas operações.\n\nO objetivo era desenvolver um software próprio que permitisse centralizar a informação e simplificar a gestão diária do projeto.\n\nO sistema devia permitir:\n\t•\torganizar stock e controlo de produto\n\t•\tgerir eventos e ações promocionais\n\t•\tcriar uma comunidade ativa em torno da Tomatico\n\nA ideia era construir uma ferramenta simples que ligasse todas estas áreas num único sistema."
      },
      impactCard: {
        title: "O resultado",
        description: "Foi desenvolvido o Tomatico Software, uma plataforma interna que centraliza a gestão operacional da marca e permite trabalhar de forma muito mais organizada.\n\nGraças ao sistema:\n\t•\ta equipa sabe exatamente que tarefas realizar e quando\n\t•\ta informação está centralizada e acessível\n\t•\tas operações diárias fluem com maior clareza\n\nO resultado é um sistema que permite à Tomatico gerir o seu crescimento de forma mais estruturada, mantendo o controlo sobre a comunidade, os eventos e o produto."
      },
      results: {
        eyebrow: "O impacto",
        stats: [
          { value: "3", label: "áreas num só sistema" },
          { value: "1", label: "plataforma à medida" },
          { value: "100%", label: "informação centralizada" },
          { value: "24/7", label: "acesso da equipa" },
        ],
      },
      bridgeTitle: "Pronto para construir o seu?",
      bridgeSubtitle: "Cada solução começa por entender os seus desafios únicos.",
      ctaButton: "Começar agora"
    },
    ulturePest: {
      hero: {
        titleLine1: "Menos improviso.",
        titleLine2: "Mais planeamento.",
        subtitle: "Por agricultores, para agricultores.",
        bookCall: "Agendar Chamada",
        startFree: "Começar Grátis",
        stat1Label: "Tratamentos Rastreáveis",
        stat2Label: "Custo Exato por m²",
        stat3Label: "Uso de pesticidas"
      },
      features: {
        titleLine1: "Foque-se na sua quinta.",
        titleLine2: "Nós facilitamos o resto.",
        subtitle: "Um único sistema para planear o trabalho, registar tratamentos, controlar stock e conhecer os custos reais da sua exploração.",
        viewAll: "Ver Todas as Funcionalidades",
        bullets: {
          bullet1: "Registos de tratamento em PDF. Instantaneamente.",
          bullet2: "SMS automáticos aos operadores para tarefas e alertas.",
          bullet3: "Cada tarefa ligada a um bloco."
        },
        assistant: { title: "Assistente inteligente para o dia a dia.", description: "Use áudio, fotos ou vídeo para registar incidências, criar tarefas ou analisar o estado da cultura. O assistente deteta pragas, gera recomendações com base no clima e dados de campo, atribui trabalho à equipa e ajuda a tomar decisões mais rápidas." },
        farm: { title: "A sua exploração, estruturada da forma certa.", description: "Organize localizações, estufas, blocos e setores de acordo com a realidade do terreno. Tratamentos, custos e ciclos partem desta base." },
        prevention: { title: "Prevenção inteligente de pragas.", description: "Antecipe-se aos problemas antes que surjam. Analise condições de cultivo, clima e pressão de pragas para detetar riscos precoces. Receba alertas preventivos e recomendações claras para agir a tempo e proteger a produção." },
        spraylogs: { title: "Registos de tratamentos, sempre disponíveis.", description: "Crie registos rapidamente, aplique filtros detalhados e exporte PDFs prontos para auditorias. Relatórios claros para o dia a dia ou inspeções." },
        treatments: { title: "Acompanhe o que é aplicado, onde faz diferença.", description: "Monitorize tratamentos, pressão de pragas e saúde das culturas por bloco e ciclo. Rastreabilidade total de aplicações biológicas e químicas." },
        stock: { title: "Um inventário sempre atualizado.", description: "Importe o stock em segundos e deixe as quantidades serem ajustadas automaticamente. Defina níveis mínimos e receba alertas antes da rutura." },
        tasks: { title: "Transforme planos em execução.", description: "Atribua tarefas a trabalhadores e áreas, acompanhe o progresso e utilize lembretes por SMS. Cada trabalhador vê apenas o que precisa de fazer." },
        cycles: { title: "Planeie ciclos com clareza.", description: "Crie ciclos personalizados, aplique-os a vários blocos e faça a gestão com facilidade. Compare custos e desempenho por ciclo, época após época." }
      },
      video: {
        title: "Veja em",
        titleHighlight: "ação",
        subtitle: "Veja como os gestores de estufas usam a nossa plataforma diariamente.",
        stat1Label: "Ganho de eficiência",
        stat2Label: "Relatórios mais rápidos",
        stat3Label: "Acesso aos dados"
      }
    }
  },
  
  zh: {
    nav: {
      homepage: "首页",
      aboutUs: "关于我们",
      software: "我们的应用",
      personalize: "定制方案",
      caseStudies: "成功案例",
      blog: "博客",
      contactUs: "联系我们",
      bookingCall: "预约通话"
    },
    personalize: {
      title: "我们为您打造专属的",
      subtitle: "软件",
      swipeHint: "滑动探索→",
      dashboardPreview: "仪表盘预览",
      whatWeBuildTitle: "我们所打造的",
      whatWeBuildSubtitle: "量身定制的软件，经过设计、开发，并已在真实业务中投入使用。",
      bridgeSection: {
        title: "这不是模板，也不是通用平台。",
        subtitle: "围绕您的流程构建，为日常使用而生，而非为演示而生。"
      },
      softwareCard: {
        title: "创意起点",
        description: "Tomatico需要一种更清晰的方式来组织其作为农业品牌的成长，并改善内部运营管理。\n\n目标是开发一款专属软件，能够集中信息并简化项目的日常管理。\n\n该系统需要能够：\n\t•\t组织库存和产品管控\n\t•\t管理活动和促销行动\n\t•\t围绕Tomatico建立活跃的社群\n\n我们的想法是打造一个简单的工具，将所有这些领域连接到一个统一的系统中。"
      },
      impactCard: {
        title: "成果",
        description: "我们开发了Tomatico Software，这是一个集中管理该品牌运营的内部平台，让工作方式更加有条理。\n\n借助该系统：\n\t•\t团队清楚知道要做什么任务、何时完成\n\t•\t信息集中且易于获取\n\t•\t日常运营更加清晰顺畅\n\n最终，Tomatico能够以更加结构化的方式管理其成长，同时掌控好自己的社群、活动和产品。"
      },
      newCard1: {
        title: "您的愿景",
        description: "没有人比您更了解自己的业务。\n您清楚什么有效，什么拖慢了工作，现有工具在哪里出了问题。\n\n您的目标不是增加更多软件，而是让日常工作更清晰、更快捷、更易于管理。\n\n这正是我们的起点。",
        keyPoints: ["您的工作流程", "您的实际限制", "您的运营优先事项"]
      },
      newCard2: {
        title: "我们的解决方案",
        description: "我们将您的愿景转化为契合您业务实际运作方式的软件。\n\n没有模板，也没有通用平台。\n我们开发简单、实用、专为日常使用而设计的工具，并随着系统的演进与您一起持续调整。",
        keyPoints: ["为真实运营而设计", "从第一天起即可轻松上手", "随您的业务成长而不断扩展"]
      },
      buttons: {
        startBuilding: "开始打造您的解决方案",
        viewCaseStudies: "查看我们的成果"
      },
      mobileButtons: {
        startBuilding: "开始",
        viewCaseStudies: "我们的作品"
      },
      workProcess: {
        sectionTitle: "我们如何与您",
        sectionTitleHighlight: "协作",
        sectionSubtitle: "从创意到可投入生产的软件，我们采用清晰透明的流程。我们与您并肩合作，方式简单、结构清晰，专注于真实成果。",
        steps: {
          step1: {
            week: "第1周",
            title: "了解您的",
            highlight: "业务运营",
            description: "我们从了解您的业务实际运作方式开始。这一阶段的重点是倾听、梳理流程，并明确软件应该做什么——以及不应该做什么。",
            features: ["与您的团队进行分析会议", "审查现有工作流程和工具", "明确目标、范围和优先事项"]
          },
          step2: {
            week: "第2-4周",
            title: "软件开发与",
            highlight: "优化",
            description: "借助我们的内部平台，我们快速开始构建，并根据真实反馈进行迭代。您每周都能看到进展，并可随时按需调整方向。",
            features: ["使用自有工具进行敏捷开发", "每周审查和实时演示", "持续测试与调整"]
          },
          step3: {
            week: "第5周",
            title: "上线与",
            highlight: "交付",
            description: "当软件准备就绪后，我们会为实际使用做好一切准备。我们培训您的团队、配置数据，并在我们的支持下正式启用系统。",
            features: ["团队培训与入门指导", "数据配置或迁移", "上线部署并进行密切跟踪"]
          },
          step4: {
            week: "持续进行",
            title: "支持与",
            highlight: "持续优化",
            description: "上线并不是终点。我们将持续与您携手，让软件随您的业务不断发展。",
            features: ["持续改进与调整", "根据需求推出新功能", "需要时提供可靠的技术支持"]
          }
        },
        cta: {
          title: "准备好",
          titleHighlight: "开始了吗",
          subtitle: "预约免费通话，开始转变您的业务。",
          buttonText: "预约免费通话"
        }
      }
    },
    hero: {
      badge: "数字农业平台",
      title: "由农民创造，\n为农民服务。",
      subtitle: "从订单和发票到生产管控，我们帮助您组织并自动化您的农业业务。",
      heroSubtitle: "减少临场应变，加强规划管理。",
      cta: "探索我们的解决方案",
      ctaSecondary: "预约免费演示"
    },
    whatIsUlture: {
      badge: "什么是Ulture？",
      title: "厌倦了无法掌控",
      titleHighlight: "正在发生的一切",
      subtitle: "因为一切都分散在各处，浪费了时间、金钱和精力。这正是我们要解决的问题。",
      painPoints: {
        time: { title: "浪费的时间", description: "在电子表格、纸质文件和互不兼容的应用之间来回切换。" },
        money: { title: "流失的资金", description: "对成本、利润缺乏真实的可见性，也不清楚盈利能力从哪里流失。" },
        work: { title: "重复的工作", description: "由于一切都未打通，同样的数据要反复录入。" }
      },
      cta: "了解我们如何解决"
    },
    metrics: {
      problemsSolved: "已解决的问题",
      savedPerYear: "年度节省",
      efficiency: "运营效率"
    },
    categories: {
      title: "发展业务",
      titleHighlight: "所需的一切",
      subtitle: "六大关键领域，助您节省时间、降低成本、提高效率。",
      administration: { title: "行政与会计", description: "通过智能流程自动化发票、合同和财务跟踪。" },
      workers: { title: "员工与任务", description: "管理团队、分配任务，并实时跟踪生产力。" },
      production: { title: "生产与库存", description: "监控库存水平、生产周期和物流情况。" },
      costs: { title: "成本与收益", description: "获取关于支出、利润率和盈利分析的洞察。" },
      clients: { title: "客户与订单", description: "优化订单管理，与客户保持稳固的关系。" },
      marketing: { title: "营销与品牌形象", description: "打造品牌影响力，有效开拓新市场。" }
    },
    footer: {
      copyright: "© 2026 Ulture",
      tagline: "用心为养育世界的人们打造。",
      product: "产品",
      company: "公司",
      legal: "法律",
      privacy: "隐私政策",
      terms: "服务条款",
      cookiePolicy: "Cookie政策",
      dpa: "数据处理协议"
    },
    whyWeExist: {
      badge: "我们的故事",
      title: "由农民创造",
      titleHighlightBlue: "为农民服务",
      subtitle: "一段迈向农业数字化转型的探索之路",
      steps: {
        challenge: { number: "01", title: "挑战", text: "农民在种植水果和蔬菜方面无人能及，但在会计、数据管理或营销方面却并非专家。我们发现，当他们的田地欣欣向荣时，他们的管理系统却早已过时。" },
        revelation: { number: "02", title: "顿悟", text: "我们发现，70%的行政时间都浪费在各种不兼容工具之间的切换和数据重复录入上。" },
        solution: { number: "03", title: "解决方案", text: "我们打造了一个统一的生态系统，让每个工具完美互通，消除摩擦并实现流程自动化。" },
        result: { number: "04", title: "成果", text: "农民得以夺回每天的时间，减少错误，并根据实时真实数据做出决策。" }
      },
      mission: {
        badge: "我们的使命",
        title: "加速",
        titleHighlight: "农业的数字化转型",
        subtitle: "构建真正理解农业实际需求的技术"
      },
      pillars: {
        automation: { label: "自动化", description: "无摩擦的流程" },
        integration: { label: "集成", description: "一切互联互通" },
        intelligence: { label: "智能", description: "自适应人工智能" },
        efficiency: { label: "效率", description: "真实的成果" }
      }
    },
    macbook: {
      title: "强大的软件，简洁的界面。",
      subtitle: "在一个平台上管理您全部的农业运营"
    },
    about: {
      badge: "关于Ulture / Ulture",
      valueLabel: "价值",
      hero: {
        title: "重新定义农业运营方式",
        subtitle: "我们是一支热衷于技术与农业的团队，致力于在全球范围内实现农业业务的数字化与优化。"
      },
      story: {
        title: "我们的故事",
        paragraph1: "Ulture诞生于对农民每天所面临挑战的深刻理解。我们的创始人兼具农业和技术背景，看到了弥合传统农业与现代数字解决方案之间差距的机会。",
        paragraph2: "自2020年以来，我们开发的工具简化了农业管理的复杂性。从小型家庭农场到大型农业企业，我们的平台都能满足各类业务的独特需求。",
        paragraph3: "如今，我们正帮助欧洲和拉丁美洲的数百位农民更高效地管理运营、降低成本并提高盈利能力。",
        years: "年经验"
      },
      mvv: {
        title: "使命、愿景与价值观",
        subtitle: "指引我们所有工作的核心原则"
      },
      mission: {
        title: "我们的使命",
        description: "为农民和农业企业提供直观的数字工具，简化管理、提高效率并推动可持续发展。"
      },
      vision: {
        title: "我们的愿景",
        description: "成为农业数字化领域全球领先的平台，让每位农民都能获得曾经只属于大型企业的先进技术。"
      },
      values: {
        innovation: { title: "创新", description: "我们不断探索解决老问题的新方法，运用前沿技术打造实用的解决方案。" },
        commitment: { title: "承诺", description: "我们深切致力于客户的成功。他们的成长就是我们的成长，我们与他们携手同行每一步。" },
        collaboration: { title: "协作", description: "我们相信团队合作的力量。我们与农民、农艺师和行业专家携手合作，共同打造更好的解决方案。" }
      },
      team: {
        title: "认识我们的创始团队",
        subtitle: "Ulture背后的团队：根植于田间，热爱技术。",
        headline: "团队",
        description: "我们是一支热衷于技术与效率的团队，致力于帮助全球企业更智能地运营。",
        ceo: "增长与战略",
        cto: "首席技术官兼联合创始人",
        coo: "运营总监",
        head_product: "产品总监",
         roles: {
           ceoCofounder: "增长与战略",
           ctoCofounder: "财务与运营",
           headProduct: "工程与产品",
           headDevelopment: "品牌与增长"
         }
      },
      timeline: {
        title: "源自田间，服务田间",
        subtitle: "我们历史上的关键里程碑",
        welcomeText: "这是",
        flipWords: ["Ulture", "我们的故事"],
        milestone1: { date: "1974年", title: "一家家族企业的起源", description: "我们的故事始于田间。1974年，我们的祖辈在西班牙阿尔梅里亚建起了最早的温室之一：一份靠双手完成的工作，凭借经验、努力和与土地之间深厚的联系支撑至今。" },
        milestone2: { date: "2002年", title: "成长带来新挑战", description: "多年来，这家家族企业不断扩张。现代化温室和新技术推动了产量增长，但运营、行政和成本管理却变得越来越复杂。" },
        milestone3: { date: "2024年", title: "问题浮出水面", description: "在田间一线的工作让一个问题变得清晰：技术在进步，但日常管理却没有跟上。信息分散各处，流程依然过度依赖人工操作。" },
        milestone4: { date: "2025年年中", title: "源于真实使用", description: "我们开发出软件的第一个版本，并直接在家族温室中进行了实际测试：这是一款为解决真实问题而设计的实用工具。" },
        milestone5: { date: "2026年2月", title: "Ulture正式上线", description: "经过数月的实际使用和持续改进，Ulture正式上线，始终坚守一个清晰的原则：简洁与实用。" },
        milestone6: { date: "2026年4月", title: "Ulture Pest", description: "我们推出Ulture Pest，这是我们首个专业化解决方案，专注于病虫害防治、处理方案和施用记录。" },
        milestone7: { date: "2026年……", title: "未来展望", description: "Ulture还在不断发展。新的解决方案正在路上，始终源自田间的真实需求。" },
      },
      cta: {
        title: "准备好改变您的农场了吗？",
        description: "加入数百位已经通过Ulture节省时间和资金的农民行列。",
        contact: "联系我们",
        demo: "申请演示"
      }
    },
    aboutStory: {
      sectionTitle: "我们的故事",
      sectionSubtitle: "我们的故事，源于田间",
      intro: {
        title: "源自真实农业实践",
        subtitle: "我们没有从演示文稿或理论出发：我们从田间起步，直面手工流程、低效问题以及管控不足的现实。"
      },
      challenge: {
        title: "挑战",
        text: "生产者在种植水果和蔬菜方面无人能及——但在会计、物流或数据管理方面并非专家。当他们的田地欣欣向荣时，其管理系统却早已过时。"
      },
      realization: {
        title: "从真实问题到实用软件",
        text: "我们打造的每一款工具都源自真实的运营问题：处理管理、规划、订单、追溯和自动化——一切都为了能在田间实际运作，而非停留于理论。"
      },
      solution: {
        title: "快速构建，田间实测",
        text: "在最初的日子里，我们打造了最早的内部工具。在接下来的几周里，我们快速迭代——直接在真实的农业工作流程中进行测试、调整和改进。"
      },
      transformation: {
        title: "转变",
        text: "我们像从事农业一样开发软件：一步一步来，一季一季推进，取得真实的成果。"
      },
      mission: {
        title: "我们的使命",
        subtitle: "加速农业的数字化转型",
        pillars: {
          automation: { title: "自动化", text: "让从生产到销售的每个流程都更高效" },
          integration: { title: "集成", text: "将您运营的各个环节连接到同一个地方" },
          dataIntelligence: { title: "数据智能", text: "将信息转化为战略决策" }
        }
      }
    },
    blog: {
      badge: "最新动态",
    title: "博客与资源",
    subtitle: "了解关于数字农业和农场管理的最新动态、技巧和案例故事。",
    heroTitle: "最新农业资讯",
    searchPlaceholder: "搜索文章...",
      postTitle: "科技如何改变农业",
      postExcerpt: "探索现代数字工具如何帮助农民提高效率并降低成本。",
      readMore: "阅读更多",
      comingSoonTitle: "更多精彩内容，敬请期待！",
      comingSoonText: "我们正在努力为您带来更多有价值的信息和资源。"
    },
    contact: {
      badge: "联系我们",
      title: "联系我们",
      subtitle: "对我们的平台有疑问？我们很乐意听取您的想法。",
      emailLabel: "邮箱",
      phoneLabel: "电话",
      locationLabel: "地址",
      infoTitle: "让我们聊聊",
      infoSubtitle: "无论您对产品、价格有疑问，还是想了解更多信息——我们随时为您解答。",
      responseTime: "我们通常在24小时内回复",
      successTitle: "消息已发送！",
      successMessage: "我们会尽快回复您。",
      thankYouTitle: "谢谢！",
      thankYouMessage: "您的消息已成功发送。我们会尽快回复您。",
      sendAnother: "发送另一条消息",
      nameLabel: "姓名",
      namePlaceholder: "您的姓名",
      emailFormLabel: "邮箱",
      emailPlaceholder: "tu@email.com",
      companyLabel: "公司（可选）",
      companyPlaceholder: "您的公司",
      messageLabel: "留言",
      messagePlaceholder: "告诉我们您需要什么帮助...",
      sending: "发送中...",
      submitButton: "发送消息"
    },
    software: {
      badge: "我们的平台",
      title: "为现代农业打造的强大软件",
      subtitle: "一个平台，管理您农场运营的方方面面——从田间到财务。",
      cta: "开始使用",
      ctaSecondary: "申请演示",
      screenshotAlt: "Ulture控制面板",
      featuresTitle: "管理农场所需的一切",
      featuresSubtitle: "我们的全套工具涵盖农场管理的方方面面。",
      features: {
        administration: { title: "行政与财务", description: "通过智能流程自动处理发票、合同和财务跟踪。" },
        workers: { title: "员工与任务", description: "管理团队、分配任务并实时跟踪工作效率。" },
        production: { title: "生产与库存", description: "监控库存水平、生产周期和物流情况。" },
        costs: { title: "成本与收益", description: "深入了解支出、利润率和盈利能力分析。" },
        analytics: { title: "分析面板", description: "实时信息和定制报告，助力数据驱动决策。" },
        automation: { title: "智能自动化", description: "由人工智能驱动的工作流程，节省时间并减少人为失误。" }
      },
      benefitsTitle: "为什么选择Ulture？",
      benefitsSubtitle: "",
      benefits: {
        item1: "每周节省超过10小时的行政工作时间",
        item3: "实时掌握所有运营情况",
        item4: "与现有工具无缝集成",
        item5: "我们团队提供持续的支持与协助",
        item6: "定期更新，推出新功能"
      },
      stats: {
        farms: "使用Ulture的农场",
        savings: "平均成本节省",
        time: "每周节省时间",
        satisfaction: "客户满意度"
      },
      connectivity: {
        badge: "全球网络",
        title: "全球互联互通",
        description: "随时随地管理您的农业运营。",
        heroTitle: "无论身处世界何地，都能连接您的农业",
        heroSubtitle: "一个全球化平台，助您跨越国界管理、分析并做出农业决策。",
        stats: {
          countries: "国家",
          farms: "农场",
          availability: "可用性",
          uptime: "运行时间"
        }
      },
      ctaTitle: "准备好转变您的农场了吗？",
      ctaSubtitle: "立即开始免费试用，了解Ulture如何帮助您节省时间和金钱。",
      ctaButton: "开始免费试用",
      ctaButtonSecondary: "联系销售"
    },
    heroInfrastructure: {
      title: "随时随地保持连接",
      subtitle: "一个鲜活的数字基础设施，实时连接田地、设备和数据。",
      tag1: "实时数据",
      tag2: "人工智能驱动决策",
      tag3: "全球化扩展",
      tag4: "自动化优先"
    },
    operations: {
      title: "一个平台，全面运营掌控。",
      subtitle: "通过一个互联系统管理您农业运营的方方面面",
      footer: "一个平台。唯一的真实数据来源。全面的运营掌控。",
      learnMore: "探索运营功能",
      intro: {
        title: "一个平台，完整的运营掌控",
        text: "管理农场运营不应该需要使用几十种不同的工具。我们的平台将您所需的一切集中在一个统一的系统中，消除复杂性，成倍提升效率。从规划到成本控制，每个模块协同运作，为您提供全面的视野和对运营的全面掌控。不再有分散的数据、混乱的电子表格，或散落在各系统之间丢失的信息。",
        bulletsTitle: "使用我们统一的平台，您将获得：",
        bullets: {
          visibility: { title: "全面可视化", desc: "所有运营数据集中一处，实时更新" },
          decisions: { title: "更智能的决策", desc: "预测和分析工具，预判趋势并优化资源配置" },
          savings: { title: "自动节省成本", desc: "内置成本控制，无需人工即可发现优化机会" },
          duplication: { title: "零重复录入", desc: "信息只需录入一次，即可自动同步到所有模块" },
          scalability: { title: "可靠的可扩展性", desc: "从10公顷发展到1,000公顷，无需更换系统" }
        }
      },
      tabs: {
        mission: {
          title: "我们的使命",
          heading: "我们的使命",
          description: "为企业赋能，提供直观的数字工具，简化运营流程，提高效率，推动可持续增长。"
        },
        vision: {
          title: "我们的愿景",
          heading: "我们的愿景",
          description: "成为企业运营领域的标杆平台，让每个团队都能使用企业级工具。"
        },
        innovation: {
          title: "创新",
          heading: "创新",
          description: "我们不断探索解决复杂问题的新方法，运用前沿科技打造实用的解决方案。"
        },
        commitment: {
          title: "承诺",
          heading: "承诺",
          description: "我们深切致力于客户的成功。客户的成长就是我们的成长，我们与您携手同行，共渡每一步。"
        },
        collaboration: {
          title: "协作",
          heading: "协作",
          description: "我们相信团结协作的力量。我们与团队、开发者和行业专家携手合作，共同打造更好的解决方案。"
        }
      }
    },
    blogPosts: {
      post1: {
        title: "人工智能如何革新现代农业",
        subtitle: "面向可持续农业的智能技术",
        category: "人工智能与技术",
        excerpt: "从实时作物监测到预测分析，人工智能正在改变我们种植作物和管理农业运营的方式。",
        readTime: "阅读时间6分钟"
      },
      post2: {
        title: "基于数据做出农业决策",
        subtitle: "将信息转化为行动",
        category: "数据管理",
        excerpt: "了解集中式数据系统如何帮助生产者做出更明智的决策、降低成本并提高盈利能力。",
        readTime: "阅读时间5分钟"
      },
      post3: {
        title: "借助技术实现可持续农业",
        subtitle: "有益于地球，也有益于生意",
        category: "可持续性",
        excerpt: "了解智能技术如何促成既有利于环境又有利于经营效益的可持续实践。",
        readTime: "阅读时间7分钟"
      },
      post4: {
        title: "农业运营中的自动化",
        subtitle: "更聪明地工作，而不是更辛苦地工作",
        category: "自动化",
        excerpt: "探索自动化如何减少人工操作、消除错误，让团队专注于战略性工作。",
        readTime: "阅读时间6分钟"
      },
      post5: {
        title: "自信地预测产量",
        subtitle: "用数据规划，而非靠猜测",
        category: "规划与预测",
        excerpt: "了解预测模型如何帮助生产者预估产量、管理库存并履行客户承诺。",
        readTime: "阅读时间7分钟"
      },
      post6: {
        title: "农业中的成本控制与盈利能力",
        subtitle: "了解你的数字，提升你的利润率",
        category: "财务管理",
        excerpt: "了解合理的成本分配与分析如何帮助发现低效环节并提升盈利能力。",
        readTime: "阅读时间8分钟"
      }
    },
    blogPage: {
      notFound: "未找到文章",
      backToBlog: "返回博客",
      quickInsight: "快速概览",
      readNext: "阅读下一篇",
      readArticle: "阅读文章",
      allRights: "版权所有。"
    },
    buttons: {
      startNow: "立即开始",
      learnMore: "探索解决方案",
      getStarted: "开始使用",
      bookDemo: "预约演示",
      contactUs: "联系我们",
      viewMore: "查看更多"
    },
    wobbleCards: {
      card1: {
        title: "全面数字化管控",
        description: "在同一平台上管理整个农业运营。从订单和发票到生产跟踪与成本分析，一应俱全。",
        imageAlt: "Ulture平台仪表盘"
      },
      card2: {
        title: "智能自动化",
        description: "减少人工操作，消除错误。我们的智能流程可根据你的运营灵活调整。"
      },
      card3: {
        title: "全球互联",
        description: "随时随地访问你的数据。所有设备与团队之间实时同步。",
        imageAlt: "Ulture平台仪表盘"
      }
    },
    ourStory: {
      mainTitle: "我们厌倦了目睹农业中的种种低效，于是有一天，我们决定去改变现状。",
      challenge: {
        badge: "挑战",
        title: "挑战",
        description: "生产者在种植水果和蔬菜方面无人能及，但在会计、物流或数据管理方面却并非如此。我们意识到，尽管他们的田地欣欣向荣，管理系统却已经落后。"
      },
      realization: {
        badge: "认知",
        title: "认知",
        description: "大多数软件公司都是在办公室里而非田间构建工具，没有一家真正理解生产者的需求。在与农户并肩工作多年后，我们决定基于真实经验而非理论来设计解决方案。"
      },
      solution: {
        badge: "解决方案",
        title: "解决方案",
        description: "我们打造了一个平台，实现运营自动化、信息集中化，将农业管理转变为一个互联、数字化且以人为本的系统。"
      },
      stats: {
        betterFruits: { title: "更优质的果实", subtitle: "可衡量的品质" },
        betterEfficiency: { title: "更高的效率", subtitle: "节省时间与成本" },
        betterLives: { title: "更美好的生活", subtitle: "献给养活世界的人们" }
      }
    },
    techSection: {
      eyebrow: "面向真实农业与难以数字化的复杂运营",
      headlinePart1: "我们打造了自己的技术，用以",
      headlineHighlight: "构建你专属的软件。",
      subheadlineHighlight: "我们不是一个通用SaaS。",
      subheadline: "我们为每一种农业运营的真实需求设计并开发定制化软件解决方案——从小型生产者到复杂的多渠道业务，皆可适用。",
      block1: {
        title: "我们打造了自己的技术",
        description: "我们开发了自有的内部平台和工具，可高效地设计、部署和迭代软件。",
        bullets: [
          "在不牺牲最终产品质量的前提下保持敏捷",
          "针对具体使用场景的定制化解决方案"
        ]
      },
      block2: {
        title: "为每种运营量身定制的软件",
        description: "每个农场、企业和运营方式都各不相同。因此我们不销售千篇一律的软件。",
        bullets: [
          "分析规模、作物及现有工作流程",
          "根据团队的数字化成熟度进行适配"
        ]
      },
      block3: {
        title: "不局限于单一行业",
        description: "我们的技术使我们能够为运营复杂的行业构建软件。",
        bullets: [
          "高强度的运营复杂性与人工流程",
          "全面掌控与智能自动化"
        ]
      },
      supportingSentence: "我们不要求运营去迁就软件，而是打造能够适应运营方式的软件。",
      cta: "和我们聊聊你的运营情况"
    },
    magicBento: {
      title: "核心",
      titleHighlight: "功能",
      subtitle: "高效管理业务所需的一切。",
      cta: "了解更多",
      documents: { 
        title: "记录与报告", 
        description: "记录所有活动并在几秒内生成报告。按田块、作物或日期筛选，快速找到所需内容。",
        reportFiles: [
          { name: "1号地块用药记录", date: "Mayo 2026", type: "pdf" },
          { name: "库存成本报告", date: "Abril 2026", type: "xlsx" },
          { name: "第一季度收成摘要", date: "2026", type: "pdf" },
          { name: "田间活动记录", date: "Junio 2026", type: "docx" },
          { name: "农药施用报告", date: "Mayo 2026", type: "pdf" }
        ]
      },
      notifications: { 
        title: "清晰洞察", 
        description: "直观查看田间与活动动态，助你做出更好的决策。",
        notificationItems: [
          { text: "1号地块处理已完成 ✓", icon: "✓", color: "#00C9A7" },
          { text: "已采购+50千克：产品A", icon: "📦", color: "#1E86FF" },
          { text: "库存水平：良好", icon: "📊", color: "#00C9A7" },
          { text: "月度自动报告已生成", icon: "📄", color: "#FFB800" }
        ]
      },
      integrations: { title: "集成", description: "兼容100多种集成方式。" },
      calendar: { title: "日历", description: "使用日历安排你的任务。" }
    },
    aiWorkflowBento: {
      title: "围绕您的运营而设计",
      titleHighlight: "技术",
      titlePart1: "围绕",
      titlePart2: "您的运营而设计",
      subtitle: "",
      cta: "了解更多",
      collaboration: {
        title: "运营数字化",
        description: "我们对日常农业运营进行结构化和数字化处理，从而实现更强的管控和更高的效率。",
        menuItems: {
          planTask: "规划任务",
          assignTask: "分配任务",
          manageTeam: "管理团队",
          analytics: "分析"
        }
      },
      integrations: {
        title: "互联互通的运营",
        description: "我们连接工具、数据和团队，让信息清晰流转，避免在不同系统之间丢失。"
      },
      insights: {
        title: "清晰与掌控",
        description: "实时清晰洞察运营、资源和绩效，助您更自信地做出更好的决策。",
        metric: "已收获千克数"
      },
      automation: {
        title: "智能规划与提醒",
        description: "提前规划，并在需要关注时自动收到提醒和警报，确保不遗漏任何事项。",
        notifications: {
          meeting: "会议提醒",
          treatment: "处理提醒：第1区块",
          lowStock: "警报：库存水平偏低"
        }
      }
    },
    scrollVelocity: {
      line1: "Ulture软件",
      line2: "掌控",
      line3: "分析",
      line4: "自动化",
      line5: "效率",
      line6: "集成",
      line7: "创新",
      line8: "增长"
    },
    featuresPreview: {
      titleLine1: "壮大您的企业。\n提升效率。\n保持掌控。",
      titleLine2: "我们让一切变得简单。",
      subtitle: "随着团队、地块和活动的增加，信息越来越难以追踪，日常工作也变得更加复杂。",
      exploreSolutions: "探索我们的解决方案",
      viewAll: "查看全部功能",
      highlights: {
        monitoring: "喷洒记录一键生成PDF。",
        alerts: "自动短信通知工作人员任务与提醒。",
        analytics: "每项任务都关联到具体地块。"
      },
      seeItTitle: "查看实际",
      seeItHighlight: "应用效果",
      dashboardDescription: "统一的控制面板，让您全面掌握温室运营情况。",
      stats: {
        pesticide: "农药使用量减少",
        reporting: "报告生成更快",
        monitoring: "监测"
      },
      joinText: "加入数百位已通过我们的平台改变病虫害管理方式的温室经营者。从小型家庭农场到大型商业运营，我们的平台都能满足您的需求。",
      features: {
        control: { title: "每天全面掌控", description: "通过统一平台管理温室、追踪每种病虫害并记录所有处理措施。清楚掌握整个运营中发生的一切。" },
        costs: { title: "清楚您的每一分钱去向", description: "追踪每一笔支出，查看每公顷成本，了解资金流向。不再猜测——只有清晰的数据帮您做出更好的决策。" },
        oversight: { title: "始终领先一步", description: "自动化警报、阈值监测和智能提醒确保您绝不会错过关键处理或截止日期。在问题扩大之前提前应对。" }
      },
      cards: {
        card1: { title: "成本与利润难以掌控", description: "没有可靠的日常运营数据，很难了解真实成本和盈利能力。" },
        card2: { title: "工具太多，缺乏全局视野", description: "信息分散在Excel、消息、笔记本和互不相通的程序中。" },
        card3: { title: "问题发现得太晚", description: "被遗忘的工作、库存不足或错误往往在已经影响运营之后才被发现。" },
        card4: { title: "记录和报告不可靠", description: "记录和报告耗费时间，且因信息分散而常常不完整或不准确。" },
        card5: { title: "始终只顾眼前", description: "日常事务留给前瞻规划或改进工作方式的空间很少。" }
      }
    },
    featuresPreviewReversed: {
      title: "每家企业的需求各不相同。",
      card1: {
        title: "探索我们的定制软件解决方案",
        description: "经过验证的软件，为日常农业运营而设计。",
        cta: "了解更多"
      },
      card2: {
        title: "与我们一起打造您自己的软件",
        description: "为您的流程、作物和结构量身定制的软件。",
        cta: "立即开始"
      },
      card3: {
        title: "持续改进，持续构建。",
        description: "我们不断开发和改进工具，长期陪伴您的运营发展。"
      }
    },
    softwareShowcase: {
      ctaButton: "立即开始",
      previewButton: "预览",
      closePreview: "关闭预览"
    },
    caseStudies: {
      title: "成功案例",
      subtitle: "来自真实项目的真实成果",
      sectionTitle: "真实项目",
      sectionSubtitle: "为实际运营量身定制、设计、开发并已投入使用的软件。",
      softwareCard: {
        title: "创意来源",
        description: "Tomatico需要一种更清晰的方式来组织其作为农业品牌的发展，并改善其运营的内部管理。\n\n目标是开发一款自有软件，能够集中信息并方便日常项目管理。\n\n该系统需要能够：\n\t•\t组织库存和产品管控\n\t•\t管理活动和促销行动\n\t•\t围绕Tomatico建立活跃社区\n\n设想是构建一个简单的工具，将所有这些方面整合到一个统一系统中。"
      },
      impactCard: {
        title: "成果",
        description: "开发出了Tomatico Software，一款集中管理品牌运营的内部平台，让工作方式更加有条理。\n\n借助该系统：\n\t•\t团队清楚知道要做什么任务以及何时执行\n\t•\t信息集中且易于访问\n\t•\t日常运营更加清晰顺畅\n\n最终形成的系统让Tomatico能够以更结构化的方式管理增长，同时掌控其社区、活动和产品。"
      },
      results: {
        eyebrow: "影响力",
        stats: [
          { value: "3", label: "个领域整合于一个系统" },
          { value: "1", label: "个定制平台" },
          { value: "100%", label: "信息集中管理" },
          { value: "24/7", label: "团队随时可访问" },
        ],
      },
      bridgeTitle: "准备好打造属于您自己的方案了吗？",
      bridgeSubtitle: "每个解决方案都始于理解您独特的挑战。",
      ctaButton: "立即开始"
    },
    ulturePest: {
      hero: {
        titleLine1: "减少随意应变。",
        titleLine2: "提升科学规划。",
        subtitle: "由农民打造，专为田间而设计。",
        bookCall: "预约通话",
        startFree: "免费开始",
        stat1Label: "可追溯的处理",
        stat2Label: "每平方米精确成本",
        stat3Label: "农药使用量"
      },
      features: {
        titleLine1: "专注于田间工作。",
        titleLine2: "其余交给我们。",
        subtitle: "一个系统即可规划工作、记录处理措施、控制库存并掌握您农业经营的真实成本。",
        viewAll: "查看全部功能",
        bullets: {
          bullet1: "处理记录一键生成PDF。",
          bullet2: "自动短信通知操作人员任务与提醒。",
          bullet3: "每项任务都关联到具体区块。"
        },
        assistant: { title: "陪伴日常工作的智能助手。", description: "使用音频、照片或视频记录事件、创建任务或分析作物状态。助手可检测病虫害，根据气候和田间数据生成建议，为团队分配工作，帮助您更快做出决策。" },
        farm: { title: "让您的农场按真实情况有序组织。", description: "根据您经营的实际运作方式，构建位置、温室、区块和分区结构。处理措施、成本和周期都以此为基础。" },
        prevention: { title: "智能病虫害预防。", description: "在问题出现之前提前预判。分析作物条件、气候和病虫害压力，及早发现风险。接收预防性警报和清晰建议，及时行动，保护产量。" },
        spraylogs: { title: "处理记录，随时齐备。", description: "几秒钟内创建记录，按任意条件筛选，并导出清晰、可用于审计的PDF。为日常使用或官方检查提供可靠报告。" },
        treatments: { title: "按位置掌控每一次施用。", description: "按区块和周期监控处理、病虫害压力和作物状态。全面追溯生物和化学施用情况。" },
        stock: { title: "自动更新的库存系统。", description: "几秒钟内导入库存，完成处理后自动更新。设置最低库存并在缺货前收到提醒。" },
        tasks: { title: "把计划变成行动。", description: "为工作人员和位置分配任务，掌控执行情况，并通过短信提醒保持进度更新。每位工作人员只在恰当的时间看到自己需要的信息。" },
        cycles: { title: "清晰规划周期。", description: "创建自定义周期，应用到多个区块并轻松管理。逐季比较不同周期的成本和结果。" }
      },
      video: {
        title: "查看实际",
        titleHighlight: "应用效果",
        subtitle: "了解温室经营者如何每天使用我们的平台。",
        stat1Label: "效率提升",
        stat2Label: "报告生成更快",
        stat3Label: "数据访问"
      }
    }
  },
  
  ar: {
    nav: {
      homepage: "الرئيسية",
      aboutUs: "معلومات عنا",
      software: "Our Apps",
      personalize: "حلول مخصصة",
      caseStudies: "دراسات الحالة",
      blog: "المدونة",
      contactUs: "اتصل بنا",
      bookingCall: "حجز مكالمة"
    },
    personalize: {
      title: "أنشئ برنامجك",
      subtitle: "الخاص",
      swipeHint: "← اسحب لاستكشاف",
      dashboardPreview: "معاينة لوحة التحكم",
      whatWeBuildTitle: "ما نقوم ببنائه",
      whatWeBuildSubtitle: "برمجيات مخصصة يتم تصميمها وتطويرها وتُستخدم بالفعل في عمليات حقيقية.",
      bridgeSection: {
        title: "ليس قالبًا. وليس منصة عامة.",
        subtitle: "مُصمم حول سير عملك. للاستخدام اليومي، وليس للعرض فقط."
      },
      softwareCard: {
        title: "البرنامج",
        description: "قمنا بتطوير نظام مخصص لإدارة صيانة المعدات، المواقع، الأعطال، وأعمال الصيانة الدورية في منصة واحدة. لكل آلة سجل خاص بها، مع إنشاء المهام تلقائيًا وإظهار الأولويات وحالة العمل بشكل واضح."
      },
      impactCard: {
        title: "النتائج",
        description: "ساعد النظام الشركة على تقليل التوقفات المفاجئة، وتسريع الاستجابة للأعطال، والحصول على رؤية واضحة للتكاليف والمشاكل المتكررة. تحولت الصيانة من عمل عشوائي إلى نظام منظم وسهل التحكم."
      },
      newCard1: {
        title: "رؤيتك",
        description: "أنت تفهم عملك أفضل من أي شخص آخر.\nتعرف ما يحتاج للتحسين، وما يبطئ الأمور، وأين تفشل الأدوات الحالية.\n\nرؤيتك ليست عن إضافة برنامج — بل عن جعل العمل اليومي أوضح وأبسط وأسهل في الإدارة.\n\nمن هنا نبدأ.",
        keyPoints: ["سير عملك", "قيودك", "أولوياتك"]
      },
      newCard2: {
        title: "حلّنا",
        description: "نحوّل تلك الرؤية إلى برنامج مبني حول كيفية عمل شركتك فعلياً.\n\nلا قوالب. لا منصات عامة.\nفقط أدوات عملية مصممة للاستخدام اليومي، تُشكّل من خلال التعاون والتغذية الراجعة الحقيقية.",
        keyPoints: ["مبني على عمليات حقيقية", "مصمم للاستخدام اليومي", "ينمو مع عملك"]
      },
      buttons: {
        startBuilding: "ابدأ ببناء حلّك",
        viewCaseStudies: "تعرّف على ما قمنا ببنائه"
      },
      mobileButtons: {
        startBuilding: "ابدأ",
        viewCaseStudies: "أعمالنا"
      },
      workProcess: {
        sectionTitle: "كيف نعمل",
        sectionTitleHighlight: "معك",
        sectionSubtitle: "من الفكرة إلى برنامج جاهز للاستخدام، عبر عملية واضحة وشفافة. نعمل جنبًا إلى جنب معك بأسلوب بسيط ومنظّم، مع تركيز كامل على نتائج حقيقية.",
        steps: {
          step1: {
            week: "الأسبوع الأول",
            title: "فهم آلية",
            highlight: "عملك",
            description: "نبدأ بفهم كيفية عمل نشاطك على أرض الواقع. تركّز هذه المرحلة على الاستماع، وتحليل العمليات، وتحديد ما يجب أن يفعله النظام — وما لا يجب.",
            features: ["جلسات تحليل مع فريقك", "مراجعة سير العمل والأدوات الحالية", "تحديد الأهداف والنطاق والأولويات"]
          },
          step2: {
            week: "الأسابيع 2-4",
            title: "تطوير النظام",
            highlight: "وتحسينه",
            description: "باستخدام منصتنا الداخلية، نبدأ التطوير بسرعة ونعمل وفق ملاحظات حقيقية. تتابع التقدم أسبوعًا بعد أسبوع مع إمكانية التعديل عند الحاجة.",
            features: ["تطوير سريع باستخدام أدواتنا الخاصة", "اجتماعات أسبوعية وعروض مباشرة", "اختبار وتحسين مستمر"]
          },
          step3: {
            week: "الأسبوع الخامس",
            title: "الإطلاق",
            highlight: "والتسليم",
            description: "بعد جاهزية النظام، نُعد كل شيء للاستخدام الفعلي. يتم تدريب الفريق، إعداد البيانات، وإطلاق النظام بدعم مباشر منا.",
            features: ["تدريب الفريق وتهيئته", "إعداد أو ترحيل البيانات", "إطلاق النظام مع متابعة دقيقة"]
          },
          step4: {
            week: "مستمر",
            title: "الدعم والتطوير",
            highlight: "المستمر",
            description: "الإطلاق ليس النهاية. نبقى معك لضمان تطوّر النظام مع تطوّر عملك.",
            features: ["تحسينات وتعديلات مستمرة", "ميزات جديدة حسب الحاجة", "دعم تقني موثوق عند الحاجة"]
          }
        },
        cta: {
          title: "مستعد",
          titleHighlight: "للبدء",
          subtitle: "احجز مكالمتك المجانية وابدأ بتحويل عملك.",
          buttonText: "احجز مكالمة مجانية"
        }
      }
    },
    hero: {
      badge: "منصة الزراعة الرقمية",
      title: "اهتم بالحقول.\nونحن نتكفّل بالباقي.",
      subtitle: "من الطلبات والفواتير إلى مراقبة الإنتاج، نساعدك على تنظيم وأتمتة عملك الزراعي.",
      heroSubtitle: "برمجيات، حلول مخصصة، وخدمات استشارية لتعزيز الوضوح والتحكم في العمليات الزراعية.",
      cta: "استكشف حلولنا",
      ctaSecondary: "احجز عرضًا توضيحيًا مجانيًا"
    },
    whatIsUlture: {
      badge: "ما هو Ulture؟",
      title: "تعبت من عدم وجود تحكم",
      titleHighlight: "في كل ما يحدث",
      subtitle: "فقدان الوقت والمال والعمل لأن لا شيء في مكان واحد. هذا ما نحله.",
      painPoints: {
        time: { title: "وقت مفقود", description: "الانتقال بين جداول البيانات والأوراق والتطبيقات التي لا تتواصل." },
        money: { title: "مال ضائع", description: "بدون رؤية حقيقية للتكاليف والهوامش وأين تتسرب الربحية." },
        work: { title: "عمل مكرر", description: "إدخال نفس البيانات مراراً وتكراراً لأن لا شيء متصل." }
      },
      cta: "اكتشف كيف نحله"
    },
    metrics: {
      problemsSolved: "مشاكل محلولة",
      savedPerYear: "توفير سنوي",
      efficiency: "كفاءة تشغيلية"
    },
    categories: {
      title: "كل ما تحتاجه لـ",
      titleHighlight: "تنمية أعمالك",
      subtitle: "ست مجالات رئيسية حيث يساعدك Ulture على توفير الوقت وتقليل التكاليف وزيادة الكفاءة.",
      administration: { title: "الإدارة والمحاسبة", description: "أتمتة الفواتير والعقود والمتابعة المالية بتدفقات ذكية." },
      workers: { title: "العمال والمهام", description: "إدارة الفرق وتعيين المهام وتتبع الإنتاجية في الوقت الفعلي." },
      production: { title: "الإنتاج والمخزون", description: "مراقبة مستويات المخزون ودورات الإنتاج والخدمات اللوجستية." },
      costs: { title: "التكاليف والأرباح", description: "الحصول على رؤى حول النفقات والهوامش وتحليلات الربحية." },
      clients: { title: "العملاء والطلبات", description: "تبسيط إدارة الطلبات والحفاظ على علاقات قوية مع العملاء." },
      marketing: { title: "التسويق والصورة", description: "بناء حضور علامتك التجارية والوصول إلى أسواق جديدة بفعالية." }
    },
    footer: {
      copyright: "© 2026 Ulture",
      tagline: "صُنع بعناية من أجل من يُطعمون العالم.",
      product: "المنتج",
      company: "الشركة",
      legal: "قانوني",
      privacy: "سياسة الخصوصية",
      terms: "شروط الخدمة",
      cookiePolicy: "سياسة ملفات تعريف الارتباط",
      dpa: "DPA"
    },
    whyWeExist: {
      badge: "قصتنا",
      title: "من المزارعين",
      titleHighlightBlue: "للمزارعين",
      subtitle: "مسار اكتشاف نحو التحول الرقمي الزراعي",
      steps: {
        challenge: { number: "01", title: "التحدي", text: "المزارعون خبراء لا مثيل لهم في إنتاج الفواكه والخضروات، لكن ليس في المحاسبة أو إدارة البيانات أو التسويق. أدركنا أنه بينما كانت حقولهم مزدهرة، كانت أنظمة إدارتهم قديمة." },
        revelation: { number: "02", title: "الكشف", text: "اكتشفنا أن 70% من الوقت الإداري يُهدر في التنقل بين أدوات غير متوافقة وتكرار البيانات." },
        solution: { number: "03", title: "الحل", text: "أنشأنا نظامًا بيئيًا موحدًا حيث تتواصل كل أداة بشكل مثالي، مما يلغي الاحتكاك ويؤتمت العمليات." },
        result: { number: "04", title: "النتيجة", text: "مزارعون يستعيدون ساعات من يومهم، ويقللون الأخطاء ويتخذون قرارات مبنية على بيانات حقيقية في الوقت الفعلي." }
      },
      mission: {
        badge: "مهمتنا",
        title: "تسريع التحول الرقمي",
        titleHighlight: "للزراعة",
        subtitle: "بناء تقنية تفهم الاحتياجات الحقيقية للحقل"
      },
      pillars: {
        automation: { label: "الأتمتة", description: "عمليات بدون احتكاك" },
        integration: { label: "التكامل", description: "كل شيء متصل" },
        intelligence: { label: "الذكاء", description: "ذكاء اصطناعي تكيفي" },
        efficiency: { label: "الكفاءة", description: "نتائج حقيقية" }
      }
    },
    macbook: {
      title: "برنامج قوي. واجهة بسيطة.",
      subtitle: "إدارة عملياتك الزراعية بالكامل من منصة واحدة"
    },
    about: {
      badge: "حول Ulture / Ulture",
      valueLabel: "قيمة",
      hero: {
        title: "تحويل الزراعة من خلال الابتكار",
        subtitle: "نحن فريق شغوف بالتكنولوجيا والزراعة، نعمل على رقمنة وتحسين الأعمال الزراعية في جميع أنحاء العالم."
      },
      story: {
        title: "قصتنا",
        paragraph1: "ولدت Ulture من فهم عميق للتحديات التي يواجهها المزارعون كل يوم. رأى مؤسسونا، الذين لديهم جذور في كل من الزراعة والتكنولوجيا، فرصة لسد الفجوة بين الزراعة التقليدية والحلول الرقمية الحديثة.",
        paragraph2: "منذ عام 2020، نطور أدوات تبسط تعقيد الإدارة الزراعية. من المزارع العائلية الصغيرة إلى المؤسسات الزراعية الكبيرة، تتكيف منصتنا مع الاحتياجات الفريدة لكل عمل.",
        paragraph3: "اليوم، نساعد مئات المزارعين في أوروبا وأمريكا اللاتينية على إدارة عملياتهم بكفاءة أكبر وتقليل التكاليف وزيادة ربحيتهم.",
        years: "سنوات من الخبرة"
      },
      mvv: {
        title: "المهمة والرؤية والقيم",
        subtitle: "المبادئ التي توجه كل ما نفعله"
      },
      mission: {
        title: "مهمتنا",
        description: "تمكين المزارعين والشركات الزراعية بأدوات رقمية بديهية تبسط الإدارة وتزيد الكفاءة وتعزز النمو المستدام."
      },
      vision: {
        title: "رؤيتنا",
        description: "أن نصبح المنصة المرجعية العالمية للرقمنة الزراعية، حيث يتمتع كل مزارع بالوصول إلى التكنولوجيا التي كانت متاحة فقط للشركات الكبيرة."
      },
      values: {
        innovation: { title: "الابتكار", description: "نبحث باستمرار عن طرق جديدة لحل المشاكل القديمة، مستفيدين من التكنولوجيا المتطورة لإنشاء حلول عملية." },
        commitment: { title: "الالتزام", description: "نحن ملتزمون بعمق بنجاح عملائنا. نموهم هو نمونا، ونقف بجانبهم في كل خطوة." },
        collaboration: { title: "التعاون", description: "نؤمن بقوة العمل الجماعي. نتشارك مع المزارعين والمهندسين الزراعيين وخبراء الصناعة لبناء حلول أفضل." }
      },
      team: {
        title: "تعرّف على فريقنا المؤسس",
        subtitle: "الأشخاص وراء Ulture: جذور في الحقل وشغف بالتقنية.",
        headline: "الفريق",
        description: "نحن فريق شغوف بالتكنولوجيا والكفاءة، نعمل لمساعدة الشركات في جميع أنحاء العالم على العمل بشكل أذكى.",
        ceo: "Growth & Strategy",
        cto: "المدير التقني والمؤسسة المشاركة",
        coo: "مدير العمليات",
        head_product: "رئيسة المنتجات",
        roles: {
          ceoCofounder: "Growth & Strategy",
          ctoCofounder: "المدير التقني والمؤسس المشارك",
          headProduct: "رئيس المنتجات",
          headDevelopment: "رئيس التطوير"
        }
      },
      timeline: {
        title: "من الحقل، لأجل الحقل",
        subtitle: "معالم رئيسية في تاريخنا",
        welcomeText: "هذه هي ",
        flipWords: ["Ulture", "قصتنا"],
        milestone1: { date: "1974", title: "جذور العمل العائلي", description: "تبدأ قصتنا مع الزراعة. في عام 1974، قام أجدادنا ببناء واحدة من أوائل البيوت المحمية في ألميريا، إسبانيا. كان عملاً يدويًا قائمًا على الخبرة والجهد والارتباط بالأرض." },
        milestone2: { date: "2020", title: "النمو يجلب تحديات جديدة", description: "مع توسّع العمل العائلي، ساهمت البيوت المحمية الحديثة في تحسين الإنتاج، لكن إدارة العمليات والأعمال الإدارية والتكاليف أصبحت أكثر تعقيدًا." },
        milestone3: { date: "2024", title: "ظهور المشكلة بوضوح", description: "العمل اليومي داخل المزرعة أظهر فجوة واضحة: التكنولوجيا تطورت، لكن طريقة إدارة العمل اليومي لم تواكب هذا التطور. كانت المعلومات متفرقة والعمليات يدوية إلى حد كبير." },
        milestone4: { date: "منتصف 2025", title: "بُني للاستخدام الحقيقي", description: "تم تطوير النسخة الأولى من البرنامج واستخدامها مباشرة داخل البيوت المحمية العائلية، كأداة عملية لحل مشكلات حقيقية على أرض الواقع." },
        milestone5: { date: "نهاية 2025", title: "إطلاق Ulture", description: "بعد أشهر من الاستخدام الفعلي والتحسين المستمر، تم إطلاق Ulture رسميًا مع التركيز على الوضوح وسهولة الاستخدام." },
        milestone6: { date: "2026", title: "Ulture Pest", description: "تم تقديم Ulture Pest كأول حل مخصص لمراقبة الآفات وتنظيم المعالجات وسجلات الرش." },
        milestone7: { date: "2026", title: "ما القادم", description: "تواصل Ulture التطور. حلول وأدوات جديدة قيد التطوير، مستندة دائمًا إلى احتياجات حقيقية من الميدان." },
      },
      cta: {
        title: "مستعد لتحويل مزرعتك؟",
        description: "انضم إلى مئات المزارعين الذين يوفرون بالفعل الوقت والمال مع Ulture.",
        contact: "تواصل معنا",
        demo: "اطلب عرضاً توضيحياً"
      }
    },
    aboutStory: {
      sectionTitle: "قصتنا",
      sectionSubtitle: "قصتنا، مبنية من الحقل",
      intro: {
        title: "كنا متعبين من رؤية عدم الكفاءة في الزراعة",
        subtitle: "فقررنا يومًا ما أن نغير الأمور."
      },
      challenge: {
        title: "التحدي",
        text: "المزارعون خبراء لا مثيل لهم في إنتاج الفواكه والخضروات — لكن ليس في المحاسبة واللوجستيات وإدارة البيانات. أدركنا أنه بينما كانت حقولهم مزدهرة، كانت أنظمة إدارتهم قديمة."
      },
      realization: {
        title: "الإدراك",
        text: "معظم شركات البرمجيات كانت تبني أدوات من المكاتب، وليس من الحقول. لم تفهم أي منها حقًا ما يحتاجه المزارعون. بعد سنوات من العمل جنبًا إلى جنب مع المزارعين، قررنا تصميم حلول مبنية على الخبرة الحقيقية، وليس النظرية."
      },
      solution: {
        title: "الحل",
        text: "بنينا منصة تعمل على أتمتة العمليات، وتركيز المعلومات، وتحويل الإدارة الزراعية إلى نظام متصل ورقمي ومركز على الإنسان."
      },
      transformation: {
        title: "التحول",
        text: "عندما يركز المزارعون على الزراعة ونتولى نحن الباقي — التكنولوجيا والبيانات والتنسيق — تكون النتائج استثنائية. ثمار أفضل، كفاءة أفضل، حياة أفضل لمن يغذون العالم."
      },
      mission: {
        title: "مهمتنا",
        subtitle: "تسريع التحول الرقمي للزراعة",
        pillars: {
          automation: { title: "الأتمتة", text: "جعل كل عملية أكثر كفاءة — من الإنتاج إلى المبيعات" },
          integration: { title: "التكامل", text: "ربط جميع أجزاء عملياتك في مكان واحد" },
          dataIntelligence: { title: "ذكاء البيانات", text: "تحويل المعلومات إلى قرارات استراتيجية" }
        }
      }
    },
    blog: {
      badge: "آخر الأخبار",
      title: "المدونة والموارد",
      subtitle: "اكتشف آخر الأخبار والنصائح والقصص حول الزراعة الرقمية وإدارة المزارع.",
      heroTitle: "آخر أخبار الزراعة",
      searchPlaceholder: "البحث في المقالات...",
      postTitle: "كيف تحول التكنولوجيا الزراعة",
      postExcerpt: "اكتشف كيف تساعد الأدوات الرقمية الحديثة المزارعين على زيادة الكفاءة وتقليل التكاليف.",
      readMore: "اقرأ المزيد",
      comingSoonTitle: "المزيد من المحتوى قريباً!",
      comingSoonText: "نعمل على تقديم المزيد من المعلومات والموارد القيمة لك."
    },
    contact: {
      badge: "تواصل معنا",
      title: "اتصل بنا",
      subtitle: "هل لديك أسئلة حول منصتنا؟ نحب أن نسمع منك.",
      emailLabel: "البريد الإلكتروني",
      phoneLabel: "الهاتف",
      locationLabel: "الموقع",
      infoTitle: "دعنا نتحدث",
      infoSubtitle: "سواء كان لديك سؤال حول الميزات أو الأسعار أو أي شيء آخر، فريقنا جاهز للإجابة.",
      responseTime: "نرد عادة خلال 24 ساعة",
      successTitle: "تم إرسال الرسالة!",
      successMessage: "سنتواصل معك قريباً.",
      thankYouTitle: "شكراً لك!",
      thankYouMessage: "تم إرسال رسالتك بنجاح. سنتواصل معك قريباً.",
      sendAnother: "إرسال رسالة أخرى",
      nameLabel: "الاسم",
      namePlaceholder: "اسمك",
      emailFormLabel: "البريد الإلكتروني",
      emailPlaceholder: "بريدك@email.com",
      companyLabel: "الشركة (اختياري)",
      companyPlaceholder: "شركتك",
      messageLabel: "الرسالة",
      messagePlaceholder: "أخبرنا كيف يمكننا مساعدتك...",
      sending: "جارٍ الإرسال...",
      submitButton: "إرسال الرسالة"
    },
    software: {
      badge: "منصتنا",
      title: "برنامج قوي للزراعة الحديثة",
      subtitle: "منصة واحدة لإدارة عملياتك الزراعية بالكامل — من الحقل إلى المالية.",
      cta: "ابدأ الآن",
      ctaSecondary: "اطلب عرضاً توضيحياً",
      screenshotAlt: "لوحة تحكم Ulture",
      featuresTitle: "كل ما تحتاجه لإدارة مزرعتك",
      featuresSubtitle: "مجموعتنا الشاملة من الأدوات تغطي جميع جوانب الإدارة الزراعية.",
      features: {
        administration: { title: "الإدارة والمحاسبة", description: "أتمتة الفواتير والعقود والمتابعة المالية بتدفقات ذكية." },
        workers: { title: "العمال والمهام", description: "إدارة الفرق وتعيين المهام وتتبع الإنتاجية في الوقت الفعلي." },
        production: { title: "الإنتاج والمخزون", description: "مراقبة مستويات المخزون ودورات الإنتاج والخدمات اللوجستية." },
        costs: { title: "التكاليف والأرباح", description: "الحصول على رؤى حول النفقات والهوامش وتحليلات الربحية." },
        analytics: { title: "لوحة التحليلات", description: "معلومات في الوقت الفعلي وتقارير مخصصة لاتخاذ قرارات مستندة إلى البيانات." },
        automation: { title: "الأتمتة الذكية", description: "تدفقات عمل بالذكاء الاصطناعي توفر الوقت وتقلل الأخطاء اليدوية." }
      },
      benefitsTitle: "لماذا تختار Ulture؟",
      benefitsSubtitle: "",
      benefits: {
        item1: "وفر أكثر من 10 ساعات أسبوعياً في المهام الإدارية",
        item3: "رؤية في الوقت الفعلي لجميع العمليات",
        item4: "تكامل سلس مع الأدوات الموجودة",
        item5: "دعم ومساعدة مستمرة من فريقنا",
        item6: "تحديثات منتظمة بميزات جديدة"
      },
      stats: {
        farms: "مزارع تستخدم Ulture",
        savings: "متوسط توفير التكاليف",
        time: "الوقت الأسبوعي الموفر",
        satisfaction: "رضا العملاء"
      },
      connectivity: {
        badge: "شبكة عالمية",
        title: "اتصال عالمي",
        description: "أدر عملياتك الزراعية من أي مكان في العالم.",
        heroTitle: "اتصل بزراعتك من أي مكان في العالم",
        heroSubtitle: "منصة عالمية لإدارة وتحليل واتخاذ القرارات الزراعية بدون حدود.",
        stats: {
          countries: "الدول",
          farms: "المزارع",
          availability: "التوفر",
          uptime: "وقت التشغيل"
        }
      },
      ctaTitle: "مستعد لتحويل مزرعتك؟",
      ctaSubtitle: "ابدأ تجربتك المجانية اليوم واكتشف كيف يمكن لـ Ulture مساعدتك في توفير الوقت والمال.",
      ctaButton: "ابدأ التجربة المجانية",
      ctaButtonSecondary: "تحدث إلى المبيعات"
    },
    heroInfrastructure: {
      title: "اتصل من أي مكان",
      subtitle: "بنية تحتية رقمية حية تربط الحقول والفرق والبيانات في الوقت الفعلي.",
      tag1: "بيانات في الوقت الفعلي",
      tag2: "قرارات مدعومة بالذكاء الاصطناعي",
      tag3: "نطاق عالمي",
      tag4: "الأتمتة أولاً"
    },
    operations: {
      title: "منصة واحدة. تحكم تشغيلي كامل.",
      subtitle: "أدر كل جانب من جوانب عمليتك الزراعية من نظام واحد متصل",
      footer: "منصة واحدة. مصدر واحد للحقيقة. تحكم تشغيلي كامل.",
      learnMore: "استكشف العمليات",
      intro: {
        title: "منصة واحدة، تحكم تشغيلي كامل",
        text: "إدارة عملية زراعية لا ينبغي أن تتطلب عشرات الأدوات المختلفة. منصتنا تجمع كل ما تحتاجه في نظام واحد متكامل، مما يزيل التعقيد ويضاعف كفاءتك. من التخطيط إلى التحكم في التكاليف، كل وحدة تعمل معاً لتمنحك رؤية كاملة وتحكماً تاماً في عمليتك. لا مزيد من البيانات المجزأة أو جداول البيانات الفوضوية أو المعلومات المفقودة بين الأنظمة.",
        bulletsTitle: "مع منصتنا الموحدة تحصل على:",
        bullets: {
          visibility: { title: "رؤية كاملة", desc: "جميع بياناتك التشغيلية في مكان واحد، محدثة في الوقت الفعلي" },
          decisions: { title: "قرارات أذكى", desc: "أدوات التنبؤ والتحليل التي تتوقع الاتجاهات وتحسن الموارد" },
          savings: { title: "توفير تلقائي", desc: "التحكم في التكاليف المتكامل الذي يحدد فرص التحسين دون جهد يدوي" },
          duplication: { title: "صفر تكرار", desc: "أدخل المعلومات مرة واحدة وستتم مزامنتها تلقائياً عبر جميع الوحدات" },
          scalability: { title: "قابلية توسع مضمونة", desc: "انمُ من 10 هكتارات إلى 1000 دون تغيير الأنظمة" }
        }
      },
      tabs: {
        mission: {
          title: "مهمتنا",
          heading: "مهمتنا",
          description: "تمكين الشركات بأدوات رقمية سهلة الاستخدام تبسط العمليات وتزيد الكفاءة وتدفع النمو المستدام."
        },
        vision: {
          title: "رؤيتنا",
          heading: "رؤيتنا",
          description: "أن نصبح المنصة المفضلة لعمليات الأعمال، حيث يحصل كل فريق على أدوات على مستوى المؤسسات."
        },
        innovation: {
          title: "الابتكار",
          heading: "الابتكار",
          description: "نسعى باستمرار لإيجاد طرق جديدة لحل المشاكل المعقدة، مستفيدين من التكنولوجيا المتطورة لإنشاء حلول عملية."
        },
        commitment: {
          title: "الالتزام",
          heading: "الالتزام",
          description: "نحن ملتزمون بشدة بنجاح عملائنا. نموهم هو نمونا، ونقف معهم في كل خطوة."
        },
        collaboration: {
          title: "التعاون",
          heading: "التعاون",
          description: "نؤمن بقوة العمل معاً. نتشارك مع الفرق والمطورين وخبراء الصناعة لبناء حلول أفضل."
        }
      }
    },
    blogPosts: {
      post1: {
        title: "كيف يُحدث الذكاء الاصطناعي ثورة في الزراعة الحديثة",
        subtitle: "تقنية ذكية تلتقي بالزراعة المستدامة",
        category: "الذكاء الاصطناعي والتكنولوجيا",
        excerpt: "من مراقبة المحاصيل في الوقت الفعلي إلى التحليلات التنبؤية، يُحوّل الذكاء الاصطناعي طريقة زراعتنا وإدارة العمليات الزراعية.",
        readTime: "6 دقائق للقراءة"
      },
      post2: {
        title: "اتخاذ قرارات زراعية مبنية على البيانات",
        subtitle: "حوّل المعلومات إلى عمل",
        category: "إدارة البيانات",
        excerpt: "تعلم كيف تساعد أنظمة البيانات المركزية المزارعين على اتخاذ قرارات أفضل، وتقليل التكاليف، وتحسين الربحية.",
        readTime: "5 دقائق للقراءة"
      },
      post3: {
        title: "الزراعة المستدامة من خلال التكنولوجيا",
        subtitle: "أفضل للكوكب، أفضل للأعمال",
        category: "الاستدامة",
        excerpt: "اكتشف كيف تُمكّن التكنولوجيا الذكية الممارسات المستدامة التي تفيد البيئة وأرباحك.",
        readTime: "7 دقائق للقراءة"
      },
      post4: {
        title: "الأتمتة في العمليات الزراعية",
        subtitle: "اعمل بذكاء، ليس بجهد أكبر",
        category: "الأتمتة",
        excerpt: "استكشف كيف تقلل الأتمتة العمل اليدوي، وتزيل الأخطاء، وتسمح للفرق بالتركيز على الأنشطة الاستراتيجية.",
        readTime: "6 دقائق للقراءة"
      },
      post5: {
        title: "التنبؤ بالإنتاج بثقة",
        subtitle: "خطط بالبيانات، ليس بالتخمين",
        category: "التخطيط والتوقعات",
        excerpt: "تعلم كيف تساعد النماذج التنبؤية المزارعين على توقع الإنتاج، وإدارة المخزون، والوفاء بالتزامات العملاء.",
        readTime: "7 دقائق للقراءة"
      },
      post6: {
        title: "التحكم في التكاليف والربحية في الزراعة",
        subtitle: "اعرف أرقامك، حسّن هوامشك",
        category: "الإدارة المالية",
        excerpt: "افهم كيف يساعد التخصيص والتحليل الصحيح للتكاليف في تحديد أوجه القصور وزيادة الربحية.",
        readTime: "8 دقائق للقراءة"
      }
    },
    blogPage: {
      notFound: "المقال غير موجود",
      backToBlog: "العودة إلى المدونة",
      quickInsight: "نظرة سريعة",
      readNext: "اقرأ التالي",
      readArticle: "قراءة المقال",
      allRights: "جميع الحقوق محفوظة."
    },
    buttons: {
      startNow: "ابدأ الآن",
      learnMore: "استكشف الحلول",
      getStarted: "البدء",
      bookDemo: "احجز العرض التوضيحي",
      contactUs: "اتصل بنا",
      viewMore: "عرض المزيد"
    },
    wobbleCards: {
      card1: {
        title: "تحكم رقمي كامل",
        description: "إدارة عمليتك الزراعية بالكامل من منصة واحدة. من الطلبات والفواتير إلى تتبع الإنتاج وتحليل التكاليف.",
        imageAlt: "لوحة تحكم منصة Ulture"
      },
      card2: {
        title: "أتمتة ذكية",
        description: "قلل العمل اليدوي وأزل الأخطاء. تتكيف سير العمل الذكية لدينا مع عمليتك."
      },
      card3: {
        title: "اتصال عالمي",
        description: "الوصول إلى بياناتك من أي مكان. مزامنة في الوقت الفعلي عبر جميع أجهزتك وفرقك.",
        imageAlt: "لوحة تحكم منصة Ulture"
      }
    },
    ourStory: {
      mainTitle: "كنا متعبين من رؤية عدم الكفاءة في الزراعة، لذا قررنا يومًا ما تغيير الأمور.",
      challenge: {
        badge: "التحدي",
        title: "التحدي",
        description: "المزارعون خبراء لا مثيل لهم في إنتاج الفواكه والخضروات — لكنهم ليسوا كذلك في المحاسبة أو اللوجستيات أو إدارة البيانات. أدركنا أنه بينما كانت حقولهم مزدهرة، كانت أنظمة إدارتهم قديمة."
      },
      realization: {
        badge: "الإدراك",
        title: "الإدراك",
        description: "كانت معظم شركات البرمجيات تبني أدوات من المكاتب، وليس من الحقول. لم تفهم أي منها حقًا ما يحتاجه المزارعون. بعد سنوات من العمل جنبًا إلى جنب مع المزارعين، قررنا تصميم حلول مبنية على تجربة حقيقية، وليس نظرية."
      },
      solution: {
        badge: "الحل",
        title: "الحل",
        description: "بنينا منصة تؤتمت العمليات، وتركز المعلومات، وتحول الإدارة الزراعية إلى نظام متصل ورقمي ومتمحور حول الإنسان."
      },
      stats: {
        betterFruits: { title: "فواكه أفضل", subtitle: "جودة يمكنك قياسها" },
        betterEfficiency: { title: "كفاءة أفضل", subtitle: "توفير الوقت والتكاليف" },
        betterLives: { title: "حياة أفضل", subtitle: "لمن يطعمون العالم" }
      }
    },
    techSection: {
      eyebrow: "للزراعة الحقيقية والعمليات صعبة الرقمنة",
      headlinePart1: "نبني تقنيتنا الخاصة ",
      headlineHighlight: "لبناء برنامجك الخاص.",
      subheadlineHighlight: "لسنا SaaS عام.",
      subheadline: "نصمم ونطور حلول برمجية مخصصة تتكيف مع الاحتياجات الحقيقية لكل عملية زراعية — من المنتجين الصغار إلى الأعمال المعقدة متعددة القنوات.",
      block1: {
        title: "نبني تقنيتنا الخاصة",
        description: "طورنا منصتنا الداخلية وأدواتنا لتصميم ونشر وتطوير البرمجيات بكفاءة.",
        bullets: [
          "التحرك بسرعة دون التضحية بالموثوقية",
          "تكييف الحلول لحالات استخدام محددة جداً",
          "بناء برمجيات تناسب طريقة عمل الناس فعلياً في الميدان"
        ]
      },
      block2: {
        title: "برمجيات مكيفة لكل عملية",
        description: "كل مزرعة وشركة وعملية مختلفة. لهذا لا نبيع برمجيات بمقاس واحد يناسب الجميع.",
        bullets: [
          "حجم العملية",
          "نوع المحاصيل أو المنتجات",
          "سير العمل والأدوات الموجودة",
          "مستوى النضج الرقمي للفريق"
        ]
      },
      block3: {
        title: "غير محدود بصناعة واحدة",
        description: "على الرغم من أن جذورنا في الزراعة، تقنيتنا تسمح لنا ببناء برمجيات مخصصة لصناعات أخرى ذات عمليات معقدة.",
        bullets: [
          "التعقيد التشغيلي",
          "العمليات اليدوية",
          "البيانات المنفصلة",
          "الحاجة للتحكم والأتمتة"
        ]
      },
      supportingSentence: "لا نطلب من العمليات أن تتكيف مع البرمجيات. نبني برمجيات تتكيف مع العمليات.",
      cta: "تحدث معنا عن عمليتك"
    },
    magicBento: {
      title: "الميزات",
      titleHighlight: "الرئيسية",
      subtitle: "كل ما تحتاجه لإدارة عملك بكفاءة.",
      cta: "اعرف المزيد",
      documents: { 
        title: "السجلات والتقارير", 
        description: "سجّل جميع الأنشطة وأنشئ التقارير خلال ثوانٍ. صفِّ حسب الحقل أو المحصول أو التاريخ للعثور على المعلومات.",
        reportFiles: [
          { name: "استخدام العلاج الكتلة 1", date: "مايو 2026", type: "pdf" },
          { name: "تقرير تكاليف المخزون", date: "أبريل 2026", type: "xlsx" },
          { name: "ملخص الحصاد ر1", date: "2026", type: "pdf" },
          { name: "سجل أنشطة الحقل", date: "يونيو 2026", type: "docx" },
          { name: "تقرير تطبيق المبيدات", date: "مايو 2026", type: "pdf" }
        ]
      },
      notifications: { 
        title: "رؤية واضحة", 
        description: "اطّلع على ما يحدث عبر الحقول والأنشطة لدعم قرارات أفضل.",
        notificationItems: [
          { text: "تنفيذ العلاج الكتلة 1 ✓", icon: "✓", color: "#00C9A7" },
          { text: "+50 كجم تم شراؤها: المنتج أ", icon: "📦", color: "#1E86FF" },
          { text: "مستوى المخزون: جيد", icon: "📊", color: "#00C9A7" },
          { text: "تم إنشاء التقرير الشهري التلقائي", icon: "📄", color: "#FFB800" }
        ]
      },
      integrations: { title: "التكاملات", description: "متوافق مع أكثر من 100 تكامل." },
      calendar: { title: "التقويم", description: "استخدم التقويم لتنظيم مهامك." }
    },
    aiWorkflowBento: {
      title: "مصممة حول عملياتك",
      titleHighlight: "تقنية",
      titlePart1: "مصممة حول",
      titlePart2: "عملياتك",
      subtitle: "",
      cta: "اعرف المزيد",
      collaboration: {
        title: "الرقمنة التشغيلية",
        description: "نُنظِّم ونُرقمن العمليات الزراعية اليومية لتحقيق قدر أكبر من التحكم والكفاءة.",
        menuItems: {
          planTask: "تخطيط المهمة",
          assignTask: "تعيين المهمة",
          manageTeam: "إدارة الفريق",
          analytics: "التحليلات"
        }
      },
      integrations: {
        title: "عمليات مترابطة",
        description: "نربط الأدوات والبيانات وفرق العمل لضمان تدفق المعلومات بوضوح دون أن يضيع شيء بين الأنظمة."
      },
      insights: {
        title: "الوضوح والتحكم",
        description: "احصل على رؤية واضحة وفورية للعمليات والموارد والأداء، لاتخاذ قرارات أفضل بثقة.",
        metric: "كجم محصود"
      },
      automation: {
        title: "التخطيط الذكي والتنبيهات",
        description: "خطط مسبقًا وتلقَّ تذكيرات وتنبيهات تلقائية كلما تطلب الأمر الانتباه، حتى لا يفوتك أي شيء.",
        notifications: {
          meeting: "تذكير بالاجتماع",
          treatment: "تذكير بالعلاج: القطعة 1",
          lowStock: "تنبيه: مستويات المخزون منخفضة"
        }
      }
    },
    scrollVelocity: {
      line1: "Ulture Software",
      line2: "تحكم",
      line3: "تحليل",
      line4: "أتمتة",
      line5: "كفاءة",
      line6: "تكامل",
      line7: "ابتكار",
      line8: "نمو"
    },
    featuresPreview: {
      titleLine1: "نمِّ عملك.\nحسّن الرؤية.\nحافظ على التحكم.",
      titleLine2: "نحن نسهّل الأمر.",
      subtitle: "مع زيادة الفرق والحقول والأنشطة، تصبح المعلومات أصعب في المتابعة ويزداد تعقيد إدارة العمل اليومي.",
      exploreSolutions: "استكشف حلولنا",
      viewAll: "عرض جميع الميزات",
      highlights: {
        monitoring: "سجلات الرش بصيغة PDF. فوراً.",
        alerts: "رسائل SMS تلقائية للعمال للمهام والتنبيهات.",
        analytics: "كل مهمة مرتبطة بقطعة."
      },
      seeItTitle: "شاهدها",
      seeItHighlight: "عملياً",
      dashboardDescription: "لوحة تحكم موحدة تمنحك رؤية كاملة لعمليات الصوبة الزراعية.",
      stats: {
        pesticide: "استخدام أقل للمبيدات",
        reporting: "تقارير أسرع",
        monitoring: "مراقبة"
      },
      joinText: "انضم إلى مئات مشغلي الصوبات الذين غيّروا إدارة الآفات بمنصتنا. من المزارع العائلية الصغيرة إلى العمليات التجارية الكبيرة، منصتنا تتكيف مع احتياجاتك.",
      features: {
        control: { title: "تحكم كامل، كل يوم", description: "أدِر صوباتك، تتبع كل آفة، وسجّل جميع العلاجات من منصة موحدة. اعرف بالضبط ما يحدث في عمليتك بالكامل." },
        costs: { title: "اعرف أين يذهب مالك", description: "تتبع كل نفقة، شاهد تكلفتك لكل هكتار وافهم أين يذهب مالك. لا مزيد من التخمين—فقط أرقام واضحة تساعدك على اتخاذ قرارات أفضل." },
        oversight: { title: "دائماً خطوة للأمام", description: "تنبيهات آلية، مراقبة العتبات، وتذكيرات ذكية تضمن ألا تفوت أبداً علاجاً حرجاً أو موعداً نهائياً. ابقَ متقدماً على المشاكل قبل أن تكبر." }
      },
      cards: {
        card1: { title: "صعوبة تتبّع التكاليف والأرباح", description: "من دون بيانات موثوقة من العمليات اليومية، يصبح من الصعب فهم التكاليف الحقيقية والربحية الفعلية." },
        card2: { title: "تعدد الأدوات دون رؤية شاملة", description: "المعلومات موزعة بين ملفات Excel والرسائل والملاحظات وأنظمة لا تتواصل مع بعضها." },
        card3: { title: "اكتشاف المشكلات بعد فوات الأوان", description: "غالبًا ما يتم اكتشاف الأخطاء أو النواقص أو المهام الفائتة بعد أن تكون قد أثرت بالفعل على العمليات." },
        card4: { title: "سجلات وتقارير غير موثوقة", description: "إعداد السجلات والتقارير يستغرق وقتًا وغالبًا ما يكون غير مكتمل أو غير دقيق بسبب تشتت المعلومات." },
        card5: { title: "التركيز الدائم على اليوم فقط", description: "الأولويات اليومية لا تترك مجالًا للتفكير المسبق أو لتحسين تنظيم العمل." }
      }
    },
    featuresPreviewReversed: {
      title: "لكل شركة احتياجات مختلفة.",
      card1: {
        title: "استكشف حلولنا البرمجية المخصصة",
        description: "برمجيات مجرّبة ومصممة لدعم العمليات الزراعية اليومية.",
        cta: "اكتشف المزيد"
      },
      card2: {
        title: "ابنِ برنامجك الخاص معنا",
        description: "برمجيات مخصصة تتكيف مع أساليب العمل والمحاصيل وهيكل العملية لديك.",
        cta: "ابدأ الآن"
      },
      card3: {
        title: "نطوّر باستمرار. ونبني دائمًا.",
        description: "نواصل تطوير وتحسين الأدوات لدعم عمليتك على المدى الطويل."
      }
    },
    softwareShowcase: {
      ctaButton: "ابدأ الآن",
      previewButton: "معاينة",
      closePreview: "إغلاق المعاينة"
    },
    caseStudies: {
      title: "دراسات الحالة",
      subtitle: "نتائج حقيقية من مشاريع حقيقية",
      sectionTitle: "مشاريع حقيقية",
      sectionSubtitle: "برمجيات مخصصة يتم تصميمها وتطويرها وتُستخدم بالفعل في عمليات حقيقية.",
      softwareCard: {
        title: "الفكرة",
        description: "احتاجت Tomatico إلى طريقة أوضح لتنظيم نموها كعلامة تجارية زراعية وتحسين الإدارة الداخلية لعملياتها.\n\nكان الهدف تطوير برنامج خاص يسمح بتجميع المعلومات وتسهيل الإدارة اليومية للمشروع.\n\nكان يجب أن يتيح النظام:\n\t•\tتنظيم المخزون ومراقبة المنتجات\n\t•\tإدارة الفعاليات والأنشطة الترويجية\n\t•\tإنشاء مجتمع نشط حول Tomatico\n\nكانت الفكرة بناء أداة بسيطة تربط كل هذه المجالات في نظام واحد."
      },
      impactCard: {
        title: "النتيجة",
        description: "تم تطوير Tomatico Software — منصة داخلية تجمع الإدارة التشغيلية للعلامة التجارية وتتيح العمل بطريقة أكثر تنظيمًا.\n\nبفضل النظام:\n\t•\tيعرف الفريق بالضبط المهام المطلوبة ومتى يجب تنفيذها\n\t•\tالمعلومات مركزية ويمكن الوصول إليها بسهولة\n\t•\tالعمليات اليومية تسير بوضوح أكبر\n\nالنتيجة هي نظام يمكّن Tomatico من إدارة نموها بشكل أكثر هيكلة، مع الحفاظ على السيطرة على مجتمعها وفعالياتها ومنتجاتها."
      },
      results: {
        eyebrow: "الأثر",
        stats: [
          { value: "3", label: "مجالات في نظام واحد" },
          { value: "1", label: "منصة مخصّصة" },
          { value: "100%", label: "معلومات مركزية" },
          { value: "24/7", label: "وصول الفريق" },
        ],
      },
      bridgeTitle: "مستعد لبناء نظامك الخاص؟",
      bridgeSubtitle: "كل حل يبدأ بفهم تحدياتك الفريدة.",
      ctaButton: "ابدأ الآن"
    },
    ulturePest: {
      hero: {
        titleLine1: "ارتجال أقل.",
        titleLine2: "تخطيط أفضل.",
        subtitle: "من المزارعين، للمزارعين.",
        bookCall: "احجز مكالمة",
        startFree: "ابدأ مجاناً",
        stat1Label: "علاجات قابلة للتتبع",
        stat2Label: "التكلفة الدقيقة لكل م²",
        stat3Label: "استخدام المبيدات"
      },
      features: {
        titleLine1: "ركز على مزرعتك.",
        titleLine2: "نحن نسهّل الباقي.",
        subtitle: "نظام واحد لتخطيط العمل، تتبع العلاجات، مراقبة المخزون ومعرفة التكاليف الحقيقية لمزرعتك.",
        viewAll: "عرض جميع الميزات",
        bullets: {
          bullet1: "سجلات الرش بصيغة PDF. فوراً.",
          bullet2: "رسائل SMS تلقائية للعمال للمهام والتنبيهات.",
          bullet3: "كل مهمة مرتبطة بقطاع."
        },
        assistant: { title: "مساعد ذكي لعملك اليومي.", description: "استخدم الصوت أو الصور أو الفيديو لتسجيل المشكلات وإنشاء المهام أو تحليل حالة المحاصيل. يكتشف المساعد الآفات ويولّد توصيات مبنية على المناخ وبيانات الحقل ويوزّع العمل على فريقك ويساعدك على اتخاذ قرارات أسرع." },
        farm: { title: "مزرعتك، منظمة بالطريقة الصحيحة.", description: "قم بتنظيم المواقع والبيوت الزراعية والقطاعات بما يعكس الواقع الفعلي للعمل. جميع المعالجات والتكاليف والدورات تعتمد على هذه البنية." },
        prevention: { title: "وقاية ذكية من الآفات.", description: "استبق المشاكل قبل ظهورها. حلّل ظروف الزراعة والمناخ وضغط الآفات لاكتشاف المخاطر مبكرًا. تلقَّ تنبيهات وقائية وتوصيات واضحة للتصرف في الوقت المناسب وحماية الإنتاج." },
        spraylogs: { title: "سجلات الرش، جاهزة عند الحاجة.", description: "أنشئ السجلات خلال ثوانٍ، طبّق أي فلتر، وصدّر ملفات PDF واضحة وجاهزة للتدقيق. تقارير موثوقة للاستخدام اليومي أو التفتيشات." },
        treatments: { title: "تابع ما يتم تطبيقه، في المكان الصحيح.", description: "راقب المعالجات وضغط الآفات وصحة المحاصيل حسب القطاع والدورة. تتبع كامل للمعالجات البيولوجية والكيميائية عبر الزمن." },
        stock: { title: "مخزون يتحدث مع الواقع.", description: "استورد المخزون خلال ثوانٍ ويتم تحديث الكميات تلقائيًا بعد تنفيذ المعالجات. حدّد مستويات إعادة الطلب وتلقَّ التنبيهات قبل النفاد." },
        tasks: { title: "حوّل التخطيط إلى تنفيذ.", description: "عيّن المهام للعمال والمناطق، وتابع التنفيذ مع تذكيرات عبر الرسائل النصية. كل عامل يرى فقط ما يخصه، في الوقت المناسب." },
        cycles: { title: "خطّط الدورات بوضوح.", description: "أنشئ دورات زراعية مخصصة وطبّقها على عدة قطاعات بسهولة. قارن التكاليف والأداء لكل دورة لاتخاذ قرارات أفضل موسمًا بعد موسم." }
      },
      video: {
        title: "شاهده",
        titleHighlight: "بالفعل",
        subtitle: "شاهد كيف يستخدم مديرو البيوت المحمية منصتنا يومياً.",
        stat1Label: "زيادة الكفاءة",
        stat2Label: "تقارير أسرع",
        stat3Label: "الوصول للبيانات"
      }
    }
  }
};

// ─── Neerlandés (nl) ───
// Solo se traducen las secciones que siguen vivas en la web: blog, página de
// artículo, casos de éxito y el aviso de deslizar que usa esa página. El resto
// (secciones retiradas) hereda el inglés. Además, LanguageContext rellena con el
// inglés cualquier clave que falte, así que nunca se pinta `undefined`.
const nl: (typeof baseTranslations)["en"] = {
  ...baseTranslations.en,
  personalize: {
    ...baseTranslations.en.personalize,
    swipeHint: "Swipe om te ontdekken →",
  },
  blog: {
    badge: "Laatste nieuws",
    title: "Blog en kennisbank",
    subtitle: "Ontdek het laatste nieuws, praktische tips en verhalen over digitale landbouw en agrarische bedrijfsvoering.",
    heroTitle: "Het laatste agrarische nieuws",
    searchPlaceholder: "Artikelen zoeken...",
    postTitle: "Hoe technologie de landbouw verandert",
    postExcerpt: "Ontdek hoe moderne digitale tools telers helpen efficiënter te werken en kosten te verlagen.",
    readMore: "Lees meer",
    comingSoonTitle: "Binnenkort meer artikelen!",
    comingSoonText: "We werken eraan om je nog meer waardevolle inzichten en informatie te bieden."
  },
  blogPage: {
    notFound: "Artikel niet gevonden",
    backToBlog: "Terug naar de blog",
    quickInsight: "In het kort",
    readNext: "Volgend artikel",
    readArticle: "Lees artikel",
    allRights: "Alle rechten voorbehouden."
  },
  caseStudies: {
    title: "Klantcases",
    subtitle: "Echte resultaten uit echte projecten",
    sectionTitle: "Echte projecten",
    sectionSubtitle: "Software op maat: ontworpen, gebouwd en al in gebruik in echte bedrijven.",
    softwareCard: {
      title: "Het idee",
      description: "Tomatico had een duidelijkere manier nodig om zijn groei als agrarisch merk in goede banen te leiden en het interne beheer van zijn activiteiten te verbeteren.\n\nHet doel was om eigen software te ontwikkelen die alle informatie centraliseert en het dagelijkse beheer van het project vereenvoudigt.\n\nHet systeem moest:\n\t•\tde voorraad en productcontrole organiseren\n\t•\tevenementen en promotieacties beheren\n\t•\teen actieve community rond Tomatico opbouwen\n\nHet idee was om een eenvoudige tool te bouwen die al deze onderdelen in één systeem samenbrengt."
    },
    impactCard: {
      title: "Het resultaat",
      description: "Zo ontstond Tomatico Software: een intern platform dat het operationele beheer van het merk centraliseert en een veel overzichtelijkere manier van werken mogelijk maakt.\n\nDankzij het systeem:\n\t•\tweet het team precies welke taken wanneer moeten gebeuren\n\t•\tis alle informatie centraal en toegankelijk\n\t•\tverloopt het dagelijkse werk een stuk soepeler\n\nHet resultaat is een systeem waarmee Tomatico zijn groei gestructureerder beheert en grip houdt op zijn community, evenementen en product."
    },
    results: {
      eyebrow: "De impact",
      stats: [
        { value: "3", label: "onderdelen in één systeem" },
        { value: "1", label: "platform op maat" },
        { value: "100%", label: "gecentraliseerde informatie" },
        { value: "24/7", label: "toegang voor het team" },
      ],
    },
    bridgeTitle: "Klaar om jouw eigen software te bouwen?",
    bridgeSubtitle: "Elke oplossing begint bij een goed begrip van jouw uitdagingen.",
    ctaButton: "Start nu"
  },
};

export const translations: Record<Language, (typeof baseTranslations)["en"]> = {
  ...baseTranslations,
  nl,
};
